// /appv3/product-shot — PROTOTYPE surface for a redesigned Product Shot
// workflow. NOT a replacement for /appv2 (which stays the production
// wizard). This is a one-screen layout exploration: LEFT 440px column
// holds the design fork + creative direction OR template picker +
// aspect/quality chips at the bottom; RIGHT column holds product
// picker (horizontal strip) + preview + Generate CTA.
//
// Scope (deliberately minimal):
//   - No Add Text & Logo step (just a "+ Add text" TODO link)
//   - No Platform picker (aspect chip replaces it)
//   - No Category step
//   - No human-model gate / analyze-product safety call
//   - Single frame only (product-shot route)
//
// Reuses existing components:
//   - AppLayoutV2 shell (sidebar + main area)
//   - CreativeDirectionFilters from /appv2/components/AiGeneratePanel
//   - AiResultViewer + AiLoadingSkeleton from /appv2/components/AiResultViewer
//
// Inlined (component reuse would have added 100+ lines of adapter):
//   - Template picker — uses demoScenes from /mocks/demo.ts directly in a
//     2-col grid. StepBackground has too many wizard-specific props
//     (tabs, recommendations, pagination) for this single-purpose
//     prototype.
//   - Product picker — horizontal scrolling strip with My Products
//     thumbs + an inline "+ Upload" file picker. StepUpload's tabsMode
//     is a side-by-side / tabbed layout that doesn't match the spec.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import ThumbImage from '@/components/feature/ThumbImage';
import { CreativeDirectionFilters } from '@/pages/appv2/components/AiGeneratePanel';
import AiResultViewer from '@/pages/appv2/components/AiResultViewer';
// Jun 30 — the SAME editor used at /creatives/:id. For text-overlay tiles the
// wizard finishing surface renders THIS in place of StudioFinishEditor so a
// generated tile is edited in-wizard (one editor, no save→navigate→edit hop).
import OverlayEditor from '@/components/feature/OverlayEditor';
// Jul 17 — the ONE editor (extracted from /editor-v4). Product Shot flat renders
// edit in-wizard through THIS, unifying with /creatives/:id + the v4 route.
import CreativeEditor from '@/components/feature/CreativeEditor';
import StepUpload from '@/pages/app/components/StepUpload';
import {
  REFERENCE_CANVAS_WIDTH,
  aspectClass,
  aspectToRefHeight,
  clampAnchor,
  flattenComposedFrame,
  getTextLayerStyle,
  resolveStackedLayers,
  snapForPosition,
  suggestTextColorsFromImage,
  type LogoOverlay,
  type LogoTint,
  type ShapeOverlay,
  type SuggestedFrame,
  type TextOverlay,
} from '@/pages/app/lib/composeRender';
import { recreateTemplates, type RecreateTemplate } from '@/mocks/recreate-templates';
import { fashionModels, fashionAssetUrl } from '@/mocks/app-data';
// V3 Carousel — type pre-step shares constants + card components with
// the standalone v1 fallback at /appv3/carousel/page.tsx.
import {
  CAROUSEL_TYPES,
  AI_CHOOSE_TYPE_ID,
  getCarouselType,
  CarouselTypeCard,
  type CarouselTypeId,
} from './carouselTypes';
import {
  SOCIAL_POST_INTENTS,
  IntentCard,
  getSocialPostIntent,
  type SocialPostIntentId,
} from './socialPostIntents';
import {
  aiTemplatesForRoute,
  aiTemplatesForStudioRoute,
  aiTemplateSamplePath,
  domainForProductMeta,
  AI_TEMPLATE_DOMAIN_LABELS,
  AI_FREESTYLE_TEMPLATE_ID,
  PRODUCT_SHOT_AI_TEMPLATES,
  type AiTemplate,
  type AiTemplateDomain,
} from '@/mocks/ai-templates';
import { showLegacyForRoute } from '@/config/featureFlags';
import { useCredits } from '@/hooks/useCredits';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';
import { getPost, replacePostImage, savePost, type Post } from '@/lib/gallery';
import ScheduleModal from '@/pages/schedule/components/ScheduleModal';
import {
  analyzeProduct,
  generateImage,
  getBrand,
  listProducts,
  suggestCopy,
  wizardAiGenerate,
  wizardEditImage,
  // Jun 18 — Recipes-as-Templates Phase 3 (Product Shot Templates tab)
  listTemplateRecipes,
  wizardRenderRecipe,
  synthesizeRecipe,
  SYNTHESIZE_RECIPE_ID,
  type ProductionRecipe,
  InsufficientCreditsError,
  type CreativeDirection,
  type CreativeFraming,
  type CreativePose,
  type CreativePattern,
  type Quality,
  type UserProduct,
  type WizardAiGenerateResult,
  type AspectRatio,
} from '@/lib/api';

type DesignMode = 'ai' | 'template';
type ProtoAspect = '1:1' | '4:5' | '9:16' | '16:9';
type AppV3Category = 'product-shot' | 'product-swap' | 'fashion' | 'carousel' | 'social-post';
type Format = 'single' | 'carousel';

// IG carousel spec accepts 1:1 / 4:5 / 9:16 (no 16:9). Per-route allowed
// aspects already exclude 16:9 for held + worn; intersect with this set
// when carousel is on so any future 16:9-allowing route stays compliant.
const CAROUSEL_ALLOWED_ASPECTS: readonly ProtoAspect[] = ['1:1', '4:5', '9:16'];

// Jul 17 — Product Shot flat renders finish in the shared CreativeEditor (v4).
// Flip to false to instantly revert Product Shot to the old StudioFinishEditor
// (which is untouched + still the default for every other route). One switch.
const EMBED_CREATIVE_EDITOR_FOR_PRODUCT_SHOT = true;

// Quality multipliers mirror credits.calculate_cost on the backend.
// Single frame → cost == multiplier.
const QUALITY_MULTIPLIER: Record<Quality, number> = { low: 1, medium: 2, high: 4 };

const ASPECT_LABEL: Record<ProtoAspect, string> = {
  '1:1': 'Square 1:1',
  '4:5': 'Portrait 4:5',
  '9:16': 'Vertical 9:16',
  '16:9': 'Widescreen 16:9',
};

// CSS aspect-ratio tokens — static literals so Tailwind JIT can see them.
const ASPECT_TW: Record<ProtoAspect, string> = {
  '1:1': 'aspect-square',
  '4:5': 'aspect-[4/5]',
  '9:16': 'aspect-[9/16]',
  '16:9': 'aspect-[16/9]',
};

// platformId on a Recreate template is like 'shot-1x1' / 'shot-4x5'.
// Convert to the wizard's ProtoAspect for the AspectRow display + the
// /api/generate aspectRatio param.
function platformIdToAspect(platformId: string): ProtoAspect {
  if (platformId === 'shot-4x5' || platformId === 'social-4x5') return '4:5';
  if (platformId === 'shot-9x16' || platformId === 'social-9x16') return '9:16';
  if (platformId === 'shot-16x9') return '16:9';
  return '1:1';
}

// ============================================================================
// RouteConfig — per-category configuration powering the parameterized
// /appv3/:category page. Each category gets:
//   - title + subtitle for the page header
//   - apiRoute: the value passed to wizardAiGenerate({route}) — drives
//     the FEATURE_PROVIDERS routing + the route_scaffold in
//     _build_wizard_concept (model holds vs model wears vs no human)
//   - framings: the Framing CD row's value list. Each route has its
//     own vocabulary (Product Shot = composition; Held = pose with
//     model; Worn = garment showcase angle)
//   - defaultFraming: the initial value on the CD when this route
//     mounts (must be a member of `framings`)
//   - recreateCategory: which Recreate templates to surface in the
//     Choose Template tab (product-swap currently has zero authored;
//     the grid shows an empty state)
//   - allowedAspects: which AspectRow chips to show. Held + Worn don't
//     yet support 16:9 (no authored templates) so we hide those chips
//   - defaultAspect: initial aspectRatio state on mount
//   - showTextDensity: whether to render the Text density CD row.
//     Today only Product Shot surfaces it (the backend override is
//     plumbed only for _render_ai_magic single-frame, not
//     model-holding renders)
// ============================================================================

interface RouteConfig {
  category: AppV3Category;
  title: string;
  subtitle: string;
  apiRoute: 'product-shot' | 'product-swap' | 'fashion';
  framings: readonly CreativeFraming[];
  defaultFraming: CreativeFraming;
  recreateCategory: 'product-shot' | 'product-swap' | 'fashion';
  allowedAspects: readonly ProtoAspect[];
  defaultAspect: ProtoAspect;
  showTextDensity: boolean;
  /** Auto-picked 4 framings used when format='carousel'. Ordered tight
   *  → wide so frame 1 reads as the hero shot. The user's selected
   *  Framing CD filter is IGNORED in carousel mode — these win. Pick is
   *  variety-optimized (one extreme close, two mediums, one wide)
   *  rather than 4 similar variants. */
  carouselFramings: readonly CreativeFraming[];
  /** Held Product route ONLY (Jun 11). Hand-position values surfaced as
   *  the hero POSE picker (replaces the empty placeholder previously
   *  shown in the MODEL slot for Held). Optional — Fashion + Product
   *  Shot leave it unset. */
  poses?: readonly CreativePose[];
  /** Held Product route ONLY. Initial pose pre-selected when the route
   *  loads. Required when `poses` is set. */
  defaultPose?: CreativePose;
  /** Product Shot route ONLY (Jun 11). Arrangement values surfaced as
   *  the hero PATTERN picker. Optional. */
  patterns?: readonly CreativePattern[];
  /** Product Shot route ONLY. Initial pattern pre-selected when the
   *  route loads. Required when `patterns` is set. */
  defaultPattern?: CreativePattern;
}

// Frontend pattern name → backend pattern_id. Backend constants live in
// main.py / prompts.py (ARRANGEMENT_PROMPTS + PATTERN_BACKDROPS) and use
// hyphenated slugs. The 4 existing patterns (circular-array,
// top-down-single, top-down-multi, falling) already have reference
// images on disk; the 2 NEW ones (triangle, lined-up) were rendered
// Jun 11 via gen_pattern_references.py.
export const PATTERN_TO_BACKEND_ID: Record<CreativePattern, string> = {
  'Single': 'top-down-single',
  'Circular': 'circular-array',
  'Triangle': 'triangle',
  'Mosaic': 'top-down-multi',
  'Lined up': 'lined-up',
  'Falling': 'falling',
};

// Per-route header icon (shown in the gradient tile in the sticky page header).
const CATEGORY_ICON: Record<AppV3Category, string> = {
  'product-shot': 'ri-camera-lens-line',
  'product-swap': 'ri-hand-heart-line',
  'fashion': 'ri-user-line',
  'carousel': 'ri-slideshow-3-line',
  'social-post': 'ri-megaphone-line',
};

const ROUTE_CONFIG: Record<AppV3Category, RouteConfig> = {
  'product-shot': {
    category: 'product-shot',
    title: 'Product Shot',
    subtitle: 'Single-screen workflow. Pick a design path, drop a product, generate.',
    apiRoute: 'product-shot',
    framings: [
      'Hero close-up',
      'Centered product',
      'Lifestyle wide',
      'Flat lay top-down',
    ],
    defaultFraming: 'Centered product',
    recreateCategory: 'product-shot',
    allowedAspects: ['1:1', '4:5', '9:16', '16:9'],
    defaultAspect: '1:1',
    showTextDensity: true,
    carouselFramings: [
      'Hero close-up',       // frame 1 — tightest, hero
      'Centered product',    // frame 2 — medium
      'Flat lay top-down',   // frame 3 — alt angle
      'Lifestyle wide',      // frame 4 — widest, scene context
    ],
    // Jun 11 — Pattern hero picker. Arrangement style. Single is the
    // safe default (no multi-instance issues). Other patterns vary
    // count from 1 (Single) to 4 (Mosaic 2×2). Triangle + Lined up are
    // new with refs rendered Jun 11; rest reuse existing refs.
    patterns: [
      'Single',
      'Circular',
      'Triangle',
      'Mosaic',
      'Lined up',
      'Falling',
    ],
    defaultPattern: 'Single',
    // Jun 12 v6 — In Hand merged into Product Shot. Poses surface as a
    // SECOND hero section (under Patterns) on the Create tab. Picking a
    // pose switches the shot to "held" mode (product-swap backend). No
    // defaultPose → the route defaults to pattern (product-only) mode.
    poses: [
      'Close-up grip',
      'Two-hand present',
      'Palm display',
      'Mid-application',
      'Hands only',
      'Over-the-shoulder',
    ],
  },
  'product-swap': {
    category: 'product-swap',
    // 'In Hand' is the locked user-facing name (Jun 11 rename — was
    // "Held Product" here and "Product Swap" on /create). Route id +
    // apiRoute stay 'product-swap' for URL/backend continuity.
    title: 'In Hand',
    subtitle: 'Show a real human model holding your product. AI invents the model + scene; your product stays the hero.',
    apiRoute: 'product-swap',
    // Jun 11 — Held filter values reshuffled. `framing` now means
    // CAMERA ANGLE (6 cohesive values describing camera setup). The
    // hand position (pose) lives in the separate `poses` list below
    // and gets promoted to the hero POSE picker in the LEFT panel.
    framings: [
      'Close-up',
      'Eye-level',
      'Side-on',
      'Three-quarter',
      'Low-hero',
      'Top-down',
    ],
    defaultFraming: 'Eye-level',
    // POSE values — what the model is DOING with the product. These
    // used to be the framings (pre-Jun 11); now they're a separate
    // CD field surfaced as the hero picker for Held.
    poses: [
      'Close-up grip',
      'Two-hand present',
      'Palm display',
      'Mid-application',
      'Hands only',
      'Over-the-shoulder',
    ],
    defaultPose: 'Two-hand present',
    recreateCategory: 'product-swap',
    allowedAspects: ['1:1', '4:5', '9:16'],
    defaultAspect: '1:1',
    // Text density gating — backend's _render_ai_magic accepts the
    // override for every route (not just product-shot), so surfacing
    // it here is safe. Users want to control "model holds product, no
    // text overlay" vs "model holds product with headline + CTA baked
    // in" — same control as Product Shot.
    showTextDensity: true,
    // Carousel variety for Held = pose variation (more visually
    // interesting per frame than 3 angle variants). These are still
    // typed as CreativeFraming (legacy values are kept in the union
    // for exactly this purpose). Backend treats them as pose values
    // during carousel rendering — backward-compat with pre-Jun-11
    // behavior.
    carouselFramings: [
      'Close-up grip',       // frame 1 — extreme close
      'Hands only',          // frame 2 — tight crop
      'Two-hand present',    // frame 3 — medium presentation
      'Over-the-shoulder',   // frame 4 — wide context
    ],
  },
  'fashion': {
    category: 'fashion',
    // Jul 24 — renamed "Fashion" → "Model Shot" (user): the counterpart to
    // "Product Shot". Product Shot = the item by itself; Model Shot = the item
    // on a real model (worn apparel or held accessory). Route id + apiRoute stay
    // 'fashion' for URL/backend continuity.
    title: 'Model Shot',
    subtitle: 'Show your product on a real human model — worn like apparel or held like an accessory. AI invents the model + scene; your product stays the hero.',
    apiRoute: 'fashion',
    framings: [
      'Full-body editorial',
      'Mid-shot lifestyle',
      'Detail close-crop',
      'Walking in-motion',
      'Side profile',
      'Styled layered',
    ],
    defaultFraming: 'Mid-shot lifestyle',
    recreateCategory: 'fashion',
    allowedAspects: ['1:1', '4:5', '9:16'],
    defaultAspect: '1:1',
    // Text density gating — same rationale as product-swap. Backend
    // _render_ai_magic accepts the override regardless of route, so
    // surfacing the Text zone lets users control whether the Worn Item
    // ad bakes in headline + CTA or stays pure photography.
    showTextDensity: true,
    // Same variety pattern as held: one extreme close + two mediums +
    // one wide. The 4-pick from a 6-list gives a recognizable model in
    // the same garment from 4 distinct angles.
    carouselFramings: [
      'Detail close-crop',     // frame 1 — fabric/stitching close
      'Mid-shot lifestyle',    // frame 2 — waist-up
      'Side profile',          // frame 3 — alt angle
      'Full-body editorial',   // frame 4 — head-to-feet wide
    ],
  },
  // V3 Carousel (Jun 14) — adds a type-pre-step (5 big cards) above the
  // existing Product Shot Studio UI. Once the type is picked + Continue
  // pressed, this category renders the EXACT same FashionStudioLayout
  // as product-shot, with format LOCKED to 'carousel' (no Single
  // toggle) + matrix mode DISABLED (single template only). The
  // carouselType state flows into wizardAiGenerate as `carouselType`
  // → backend's CAROUSEL_TYPE_SCAFFOLDS in helpers/ad_brief.py picks
  // the structural prompt scaffold.
  //
  // apiRoute='product-shot' — backend's route_scaffold is product-shot's
  //   (no human; product-only). Per-frame structure comes from
  //   carousel_type, not from the per-route framings list.
  // recreateCategory='product-shot' — same template library as Product
  //   Shot's Templates tab. Carousel templates aren't a separate library
  //   (an AI template is just a scene-style anchor that rides into
  //   every frame as scene_ref_bytes; carousel_type drives structure).
  // 1:1-only for v1 per IG/FB/LinkedIn carousel spec.
  // No patterns or poses — those are product-arrangement concepts that
  //   don't apply to carousels (each frame's structure is type-driven,
  //   not arrangement-driven).
  'carousel': {
    category: 'carousel',
    title: 'Carousel',
    subtitle: 'Pick a type, then design 4 cohesive frames.',
    apiRoute: 'product-shot',
    framings: [
      'Hero close-up',
      'Centered product',
      'Lifestyle wide',
      'Flat lay top-down',
    ],
    defaultFraming: 'Centered product',
    recreateCategory: 'product-shot',
    allowedAspects: ['1:1'],
    defaultAspect: '1:1',
    showTextDensity: true,
    // Not used for carousel — carousel_type scaffold drives per-frame
    // structure instead of framings. Kept populated for type safety.
    carouselFramings: [
      'Hero close-up',
      'Centered product',
      'Flat lay top-down',
      'Lifestyle wide',
    ],
  },
  // V3 Social Post (Jun 15) — intent-driven. The user picks 1 of 6 intents
  // on the pre-step (SocialPostIntentPreStep), then lands in the Studio
  // with intent reflected as a header pill. Backend gets `intent` form
  // field → routes through INTENT_SCAFFOLDS in helpers/ad_brief.py to
  // shape the LLM concept writer's composition + headline tone + CTA
  // presence. 1:1-only in v1 (matches Product Shot V3 + Carousel V3).
  // No Templates tab — Create is the whole flow.
  'social-post': {
    category: 'social-post',
    title: 'Social Post',
    subtitle: 'Pick an intent, drop a product, generate. AI shapes the ad to match.',
    apiRoute: 'social-post',
    // Social Post V3 doesn't use framings (intent scaffold drives
    // composition). Kept populated for type safety with the Product Shot
    // defaults — they're never surfaced in the UI for this route.
    framings: [
      'Hero close-up',
      'Centered product',
      'Lifestyle wide',
      'Flat lay top-down',
    ],
    defaultFraming: 'Centered product',
    recreateCategory: 'product-shot',
    allowedAspects: ['1:1'],
    defaultAspect: '1:1',
    showTextDensity: true,
    carouselFramings: [
      'Hero close-up',
      'Centered product',
      'Flat lay top-down',
      'Lifestyle wide',
    ],
  },
};

// Default fallback when the URL param doesn't match a known category.
const DEFAULT_ROUTE: RouteConfig = ROUTE_CONFIG['product-shot'];

// Resolve the backend route for an AI template (Jun 12 v6 — merged library).
// Held templates carry route='product-swap' and MUST generate through that
// backend path (safety scaffold + "model holding the product"); product-only
// templates use 'product-shot'. Freestyle / unknown ids fall back to the
// page route.
function routeForAiTemplate(
  id: string,
  fallback: RouteConfig['apiRoute'],
): RouteConfig['apiRoute'] {
  const t = PRODUCT_SHOT_AI_TEMPLATES.find((x) => x.id === id);
  return t?.route ?? fallback;
}

export default function ProductShotV3Page() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { balance, planTier, refresh: refreshCredits } = useCredits();

  // Read :category from URL — falls back to product-shot for unknown
  // / missing values so /appv3 still loads cleanly. The page also
  // re-mounts when the param changes (Router triggers it) so per-route
  // state defaults are honoured.
  const { category: rawCategory } = useParams<{ category?: string }>();
  const routeConfig = useMemo<RouteConfig>(() => {
    const c = rawCategory as AppV3Category;
    return ROUTE_CONFIG[c] ?? DEFAULT_ROUTE;
  }, [rawCategory]);

  // Recreate templates for THIS route's category — used by the Choose
  // Template tab. product-swap currently has zero authored templates;
  // the grid handles the empty state gracefully.
  const routeTemplates: RecreateTemplate[] = useMemo(
    () => recreateTemplates.filter((t) => t.category === routeConfig.recreateCategory),
    [routeConfig.recreateCategory],
  );

  // ---------- State ----------
  // Templates is the default tab on ALL routes (Jun 11 — user lock):
  // the template library is the selling point, Create is the power-user
  // fine-tune path.
  const [designMode, setDesignMode] = useState<DesignMode>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [creativeDirection, setCreativeDirection] = useState<CreativeDirection>({
    mood: 'Premium',
    light: 'Soft daylight',
    background: 'Lifestyle',
    framing: routeConfig.defaultFraming,
    // Brand colors default On (Jul 14 — binary On/Off, was Balanced); logo
    // defaults OFF (Jun 12 user lock) — compositing the brand logo is opt-in.
    brand_presence: 'On',
    logo: 'Off',
    // Jun 22 / Jul 24 — Product Shot + Model Shot default to NO text (a clean
    // product/model shot is the point; user toggles copy on via the binary Text
    // chip). Other routes default to 'Text + CTA' (social-ready output).
    text_density: routeConfig.category === 'product-shot' || routeConfig.category === 'fashion'
      ? 'None'
      : 'Text + CTA',
    // Held route only — populated when routeConfig has a defaultPose.
    // Stays undefined on Fashion + Product Shot so the backend allowlist
    // silently no-ops there.
    ...(routeConfig.defaultPose ? { pose: routeConfig.defaultPose } : {}),
  });
  const [aspectRatio, setAspectRatio] = useState<ProtoAspect>(routeConfig.defaultAspect);
  const [quality, setQuality] = useState<Quality>('low');

  // Output format — single static frame (1 credit at Low) OR 4-frame
  // swipeable carousel (4× the single cost). When carousel is active,
  // the aspect chips intersect with CAROUSEL_ALLOWED_ASPECTS (drops 16:9
  // since IG carousels don't support it). User's selected Framing CD
  // filter is IGNORED — routeConfig.carouselFramings drives the 4 frames.
  const [format, setFormat] = useState<Format>('single');

  // Fashion-only: optional model preference. When set, the picked model's
  // {name, vibe} is sent as model_hint to the backend's wizard endpoint,
  // which appends it to the fashion route_scaffold so the AI-invented
  // model leans toward that look. Not a hard lock — gpt-image-1 still
  // invents the model. Reset when route changes (held + product-shot
  // don't surface the picker).
  const [selectedFashionModelId, setSelectedFashionModelId] = useState<string | null>(null);

  // Product Shot route ONLY (Jun 11). Pattern arrangement selected from
  // the hero PATTERN picker in the Studio layout. Initial value pulled
  // from routeConfig.defaultPattern when the route loads. Sent to the
  // backend wizard endpoint as `pattern_id` (after PATTERN_TO_BACKEND_ID
  // slug mapping) — when present, backend bypasses AI Magic flow and
  // dispatches to the pattern pipeline.
  const [selectedPattern, setSelectedPattern] = useState<CreativePattern | null>(
    routeConfig.defaultPattern ?? null,
  );

  // V3 Carousel (Jun 14) — Step 1 type pre-step state. When category=
  // 'carousel' and this is null, render the type picker page (5 big
  // cards + Continue). When set, render the existing Studio UI with
  // the type summary bar at top. Other categories ignore this — type
  // is carousel-only.
  const [selectedCarouselType, setSelectedCarouselType] = useState<CarouselTypeId | null>(null);

  // V3 Social Post (Jun 15) — Step 1 intent pre-step state. Same shape
  // as selectedCarouselType but for the 6 Social Post intents. When
  // category='social-post' and this is null, render the intent picker
  // (4×2 grid of intent cards). When set, render the Studio UI with
  // the intent surfaced as a header pill; backend gets `intent` form
  // field to scaffold the LLM concept writer.
  const [selectedIntent, setSelectedIntent] = useState<SocialPostIntentId | null>(null);

  // V3 Social Post (Jun 15 v2) — MASTER FILTERS TOGGLE. When OFF, no
  // creative_direction vibe/light/bg/framing/text_density values are
  // sent to the backend → AI gets full discretion over visual style.
  // Brand chips (right footer: brand_presence + logo) ride independently
  // — they're NOT gated by this. Default OFF per user direction "right
  // now all the renders look almost the same — I want to avoid it by
  // giving the AI complete control when the user wants it". Only applies
  // to social-post route; other routes ignore this state.
  const [filtersEnabled, setFiltersEnabled] = useState<boolean>(false);

  // V3 Social Post (Jun 15 v3) — SHOT TYPE picker (hoisted from filter
  // row to hero picker in v3). Three states:
  //   'ai-chooses' (default) → wizardAiGenerate({route: 'social-post'}),
  //     no shot directive. LLM decides whether to include a model based
  //     on the intent's scaffold + visual_direction freedom.
  //   'product-only' → wizardAiGenerate({route: 'social-post',
  //     shotMode: 'product-only'}). Adds a "NO humans in the frame"
  //     directive to the prompt so the LLM is constrained.
  //   'in-use' → wizardAiGenerate({route: 'product-swap'}). Reuses the
  //     existing Held safety scaffold + render pipeline.
  // The three options give users a clear escape hatch (AI Chooses) AND
  // explicit overrides (Product Shot / In Use) — matches the master
  // Filters toggle pattern of "intent-driven default + optional control".
  const [socialPostShot, setSocialPostShot] = useState<'ai-chooses' | 'product-only' | 'in-use'>('ai-chooses');

  // V3 Carousel — clear the type when route changes away from carousel
  // (avoid stale state if the user comes back to the carousel route).
  useEffect(() => {
    if (routeConfig.category !== 'carousel') {
      setSelectedCarouselType(null);
    }
  }, [routeConfig.category]);

  // V3 Social Post — same pattern as Carousel above. Clear the intent
  // when route changes away from social-post so a returning user lands
  // back on the intent picker rather than a stale Studio.
  useEffect(() => {
    if (routeConfig.category !== 'social-post') {
      setSelectedIntent(null);
    }
  }, [routeConfig.category]);

  // Product Shot route ONLY (Jun 11). AI template selection from the
  // Templates tab — each tile is a pre-rendered sample showing what
  // that template's prompt produces. When set, the wizard endpoint
  // looks up the template's prompt and uses it as visual_direction
  // (concept LLM bypassed). Null when in AI Magic mode OR no template
  // picked yet.
  // Multi-select (Jun 12 v3 — matrix): ordered list of picked AI templates.
  // The matrix = (products) × (these templates). `selectedAiTemplateId`
  // (the first) is derived below so the ~24 existing single-template reads
  // + the single-render path keep working unchanged. The AI Magic freestyle
  // tile is exclusive (single-select) — see handleAiTemplateToggle.
  const [selectedAiTemplateIds, setSelectedAiTemplateIds] = useState<string[]>([]);
  const selectedAiTemplateId = selectedAiTemplateIds[0] ?? null;
  // Deep-link lock (from /templates ?aiTemplate=): when set, the left
  // Templates panel shows ONLY this template (Recreate-style) instead of the
  // full grid. "Browse all templates" clears it back to the grid.
  const [lockedTemplateId, setLockedTemplateId] = useState<string | null>(null);
  // Recipe equivalent (Jun 18) — deep-linked from /templates via ?recipe=.
  // When set, the Templates tab shows ONLY this recipe (single locked view,
  // same shape as lockedTemplateId) + product picker on the right.
  const [lockedRecipeId, setLockedRecipeId] = useState<string | null>(null);

  // ─── Recipes-as-Templates (Jun 18 — Phase 3) ────────────────────────
  // Product Shot's Templates tab surfaces approved moodboard recipes
  // ABOVE the existing scene-template grid. Single-select (no matrix
  // on recipes in v1). Picking a recipe clears any AI template selection
  // and vice versa — mutually exclusive paths.
  const [recipeTemplates, setRecipeTemplates] = useState<ProductionRecipe[] | null>(null);
  // Distinct from recipeTemplates===null: true ONLY while a fetch is in
  // flight. Lets the Templates tab show a loader instead of flashing the
  // "no recipes" empty-state before the list lands (recipe fetch is a
  // network call + GCS walk; legacy templates were bundled = instant).
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [recipeVibeFilter, setRecipeVibeFilter] = useState<string | null>(null);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);

  // Reset CD framing + aspect when category changes via in-page nav
  // (without remount). Keeps state legal — framing values across
  // routes don't overlap so leaving stale values would render nothing
  // selected. Same for aspectRatio if the new route doesn't allow it.
  useEffect(() => {
    setCreativeDirection((prev) => {
      const next: CreativeDirection = {
        ...prev,
        framing: routeConfig.defaultFraming,
      };
      // Held → set pose to route default. Other routes → drop pose
      // entirely (left over from a previous Held visit would otherwise
      // travel into the next API call as a stale Held-only directive).
      if (routeConfig.defaultPose) {
        next.pose = routeConfig.defaultPose;
      } else {
        delete next.pose;
      }
      return next;
    });
    setAspectRatio((prev) =>
      routeConfig.allowedAspects.includes(prev) ? prev : routeConfig.defaultAspect,
    );
    setSelectedTemplateId(null);
    setSelectedFashionModelId(null);
    // Product Shot → snap pattern to route default. Other routes → null
    // so a stale Product Shot pattern doesn't travel into a Held/Fashion
    // generation as a leftover `pattern_id` form field.
    setSelectedPattern(routeConfig.defaultPattern ?? null);
    setSelectedAiTemplateIds([]);
    setLockedTemplateId(null);
    setLockedRecipeId(null);
    // V3 Carousel — carousel route locks format to 'carousel'; other
    // routes reset to 'single' (the previous unconditional behavior).
    // This effect runs AFTER my carousel-mount useEffect above, so
    // unconditionally setting 'single' here would clobber the lock
    // (cost label drops to "1 CR", footer reads "SINGLE", Generate
    // ships format='single' → backend renders one frame instead of 4).
    setFormat(routeConfig.category === 'carousel' ? 'carousel' : 'single');
    // Fashion (Jun 18) — apparel recipes now BACK the Templates tab.
    // The force-lock to 'ai' was removed; default to 'template' so the
    // user lands on the recipe library (same as Product Shot). Create
    // tab (model + pose) is still there via the pill.
    if (routeConfig.category === 'fashion') {
      setDesignMode('template');
    }
    // V3 Social Post — default to AI Magic (intent-driven) on route entry.
    // Jun 18: the Templates pill is now un-hidden, so the user can switch
    // to recipe templates (recipe = visual × intent = copy). AI Magic
    // stays the landing default since intent IS the creative direction.
    if (routeConfig.category === 'social-post') {
      setDesignMode('ai');
      // V3 Social Post (Jun 15 v3) — reset master Filters toggle to OFF
      // + Shot type to ai-chooses on route entry. User-locked defaults:
      // filters off + ai-chooses = full AI discretion guided only by intent.
      setFiltersEnabled(false);
      setSocialPostShot('ai-chooses');
    }
  }, [routeConfig]);

  // Deep-link from /templates → pre-select an AI template (?aiTemplate=<id>).
  // Apply on mount + whenever ?aiTemplate changes. NO ref guard and NO param
  // strip on mount — idempotent re-application is what makes it survive React
  // StrictMode's double-invoke (otherwise the route-reset effect's second run
  // clears the lock and a guarded one-shot can't re-set it). It's defined
  // AFTER the route-reset effect so it runs last in each pass. The param is
  // stripped only when the user explicitly unlocks (onClearLockedTemplate),
  // so a refresh doesn't silently re-lock.
  useEffect(() => {
    const aiTemplate = searchParams.get('aiTemplate');
    if (!aiTemplate) return;
    setDesignMode('template');
    setSelectedAiTemplateIds([aiTemplate]);
    setLockedTemplateId(aiTemplate);
  }, [searchParams]);

  // Recipe deep-link (Jun 18) — /templates page tiles navigate here with
  // ?recipe=<vibe_slug>/<id>. Pre-select the recipe + flip to template
  // mode so the Templates tab opens with it active. User still uploads a
  // product + hits Generate (same as the aiTemplate deep-link).
  useEffect(() => {
    const recipe = searchParams.get('recipe');
    if (!recipe) return;
    setDesignMode('template');
    setSelectedRecipeId(recipe);
    setSelectedAiTemplateIds([]);
    setLockedRecipeId(recipe);
  }, [searchParams]);

  // Design-tab switch. Leaving Templates for Create ('ai') drops the
  // template/recipe selection so a stale pick can't drive the render
  // (a template pick survived the switch and re-rendered the recipe,
  // ignoring the Create model + filters) and the preview doesn't linger
  // on a stale recipe. Belt-and-suspenders with handleGenerate's
  // designMode gate. useState setters are stable → empty deps.
  const handleDesignModeChange = useCallback((m: DesignMode) => {
    setDesignMode(m);
    if (m === 'ai') {
      setSelectedRecipeId(null);
      setSelectedAiTemplateIds([]);
      setSelectedTemplateId(null);
      setLockedRecipeId(null);
      setLockedTemplateId(null);
    }
  }, []);

  // Product state — mirrors what /appv2 tracks for a single-product route.
  const [productFile, setProductFile] = useState<File | null>(null);
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  // Cached classification metadata (the 13-category vocab + vibe tags)
  // riding along with the picked product. Set from the saved-product
  // pick (library rows carry both) or URL-scrape insights; cleared on
  // raw file uploads (no metadata). Drives the Templates tab's
  // "Recommended for <domain>" grouping via domainForProductMeta.
  const [productCategory, setProductCategory] = useState<string | null>(null);
  const [productTags, setProductTags] = useState<string[]>([]);
  const [savedProducts, setSavedProducts] = useState<UserProduct[] | null>(null);
  const [selectedSavedProductId, setSelectedSavedProductId] = useState<string | null>(null);
  const [productModalOpen, setProductModalOpen] = useState(false);

  // Result state.
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<WizardAiGenerateResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Result-action state (Jun 12 — Studio footer's post-result row).
  // savedPostIdRef makes Save idempotent: Save → Schedule/Share reuses
  // the same My Creatives row instead of inserting a duplicate. Reset
  // whenever the result is cleared or a new generation fires.
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const savedPostIdRef = useRef<string | null>(null);
  // Jul 18 — the embedded CreativeEditor FORKS a new post version when the user
  // saves an edit. Track the forked id (state, so flatEditorPost recomputes AND
  // the "was it forked?" flag is reactive) so on Back-to-design → return we load
  // the EDITED version from the DB instead of re-seeding the stale frame.
  // Non-null ⇒ the user saved an in-editor edit.
  const [editorActiveId, setEditorActiveId] = useState<string | null>(null);
  // Text-overlay output of the latest render (Jun 29). The synth path sets
  // generatedImage (not aiResult), so the editor doc + text-free scene ride
  // here for the save to persist. Cleared on every new generate.
  const overlayOutRef = useRef<{ base_image?: string; text_overlay?: Record<string, unknown> } | null>(null);
  const [downloadingResult, setDownloadingResult] = useState(false);
  // Matrix result (Jun 12 v3) — when a multi-template batch was generated,
  // each frame is an INDEPENDENT image (not a carousel). resultTemplateIds
  // captures the template per frame AT GENERATE TIME (templates count as
  // edits-behind so the live selection can drift). savedMatrixIdsRef holds
  // the per-frame My Creatives row ids (one row per image — no zip).
  const [resultIsMatrix, setResultIsMatrix] = useState(false);
  const [resultTemplateIds, setResultTemplateIds] = useState<string[]>([]);
  const savedMatrixIdsRef = useRef<(string | null)[]>([]);
  // Schedule modal (Jun 12) — the SAME ScheduleModal My Creatives +
  // /creatives/:id use, opened inline with the just-saved post
  // pre-selected. null = closed.
  const [schedulePost, setSchedulePost] = useState<Post | null>(null);
  const { status: fbStatus } = useFacebookConnection();
  // Bottom-center toast (mirrors the gallery/detail treatment) — used for
  // "Scheduled" confirmation after the modal saves.
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ---------- Finishing state (Jun 12 v2 — render panel = edit screen) ----------
  // displayFrames is the CANONICAL editable base: data-URL frames set on
  // generation success, mutated in place by prompt-edits. Text/logo
  // overlays ride ON TOP client-side (StepText) and only get baked in at
  // flatten time — so prompt-edits transform the base underneath while
  // the user's text survives untouched (user-locked Jun 12).
  const [displayFrames, setDisplayFrames] = useState<string[]>([]);
  // Aspect the current result was rendered at — captured at generate time
  // so later aspect-chip changes don't corrupt StepText's canvas math.
  const [renderedAspect, setRenderedAspect] = useState<ProtoAspect>('1:1');
  const [textOverlays, setTextOverlays] = useState<TextOverlay[]>([]);
  // Shapes aren't authorable in the freestyle editor (no presets) but the
  // flatten pipeline takes the array — kept as always-empty state so the
  // signature stays identical to the wizard's.
  const [shapeOverlays] = useState<ShapeOverlay[]>([]);
  const [logoOverlays, setLogoOverlays] = useState<LogoOverlay[]>([]);
  const [suggestedFrames, setSuggestedFrames] = useState<SuggestedFrame[]>([]);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);
  const [brandColors, setBrandColors] = useState<string[]>([]);
  // True when the saved creative no longer matches the on-screen
  // composite (overlay edits / prompt-edits since the last save/sync).
  // syncSavedCreative() flattens + REPLACES the saved asset (one row per
  // creative — no duplicates in My Creatives, user-locked Jun 12).
  const [creativeDirty, setCreativeDirty] = useState(false);
  const [activeFrameIdx, setActiveFrameIdx] = useState(0);
  const [editPrompt, setEditPrompt] = useState('');
  const [promptEditing, setPromptEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Brand logo for StepText's Add Logo affordance (same fetch /appv2 does).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const brand = await getBrand();
        if (!cancelled) {
          setBrandLogoUrl(brand?.logoUrl ?? null);
          setBrandColors(brand?.colors ?? []);
        }
      } catch {
        /* Add Logo affordance just shows the "Set up in My Brand" prompt */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // "Preview is N edit behind" badge tracking — counts how many user
  // edits have happened since the last successful generation. Resets to
  // 0 when generation completes, increments on every CD/aspect/quality/
  // model/framing/format change. Display rule: show badge when count > 0
  // AND we have a result to compare against.
  const [editsSinceGenerate, setEditsSinceGenerate] = useState(0);
  // displayFrames covers the matrix path (which sets neither aiResult nor
  // generatedImage — the finishing editor is driven by displayFrames).
  const hasResult = !!(aiResult || generatedImage) || displayFrames.length > 0;

  // ---------- Derived ----------
  const isFree = planTier === 'free';
  // Jul 24 tiering — Enhanced/Studio quality unlocks on Pro & Business only, so
  // Free AND Starter are Regular-locked. This flag drives ONLY the quality-tier
  // locks (passed as the `isFree` prop to the quality controls); `isFree` itself
  // stays free-only for any other free-trial gating (e.g. download).
  const qualityRestricted = planTier === 'free' || planTier === 'starter';
  // AI-template routes (product-shot / In Hand) use the multi-select AI
  // template grid + support carousel (templates round-robin into 4 frames).
  // Fashion's Choose Template uses single-frame Recreate templates → can't
  // carousel, so format is forced to single there only.
  // V3 Carousel (Jun 14) — carousel reads from the same AI Template
  // library as product-shot (no Recreate templates). Without this
  // include, recreateTemplateMode → TRUE for carousel, effectiveFormat
  // gets forced to 'single' (footer reads "SINGLE · 1 CR" instead of
  // "CAROUSEL · 4 CR"), AND Generate dispatches through the Recreate
  // template path which tries to load `templates/null/frame1_empty.png`
  // because selectedTemplateId is null on the AI-template path.
  const aiTemplateRoute = routeConfig.category === 'product-shot'
    || routeConfig.category === 'product-swap'
    || routeConfig.category === 'carousel';
  const recreateTemplateMode = designMode === 'template' && !aiTemplateRoute;
  // Carousel = 4× single. Allowed in Create mode (any route) AND the AI
  // Templates tab (round-robin); blocked only for Recreate single-frame
  // templates AND social-post (Jun 15 v2 — locked decision "drop the
  // Carousel" for social-post; users wanting carousels use /appv3/carousel).
  const effectiveFormat: Format = (
    recreateTemplateMode
    || routeConfig.category === 'social-post'
  ) ? 'single' : format;
  const frameCount = effectiveFormat === 'carousel' ? 4 : 1;
  // ---- Matrix (Jun 12 v3) ----
  // SINGLE format + 2+ AI templates → one image PER template (a matrix of N
  // independent images). CAROUSEL format + AI templates → ONE carousel whose
  // 4 frames round-robin the picked templates (not a matrix). Freestyle is
  // exclusive (counts as 1).
  const matrixActive = designMode === 'template' && aiTemplateRoute
    && effectiveFormat === 'single' && selectedAiTemplateIds.length > 1;
  const matrixImageCount = matrixActive ? selectedAiTemplateIds.length : 1;
  // Cost = per-image cost (quality × frames) × number of images in the matrix.
  const creditCost = QUALITY_MULTIPLIER[quality] * frameCount * matrixImageCount;
  // A human is in the shot when EITHER a held template is selected (Templates
  // tab) OR a pose is active (Create tab) — in both cases 16:9 is dropped
  // (reads awkward for human-subject framing), matching the old In Hand
  // aspect matrix (Jun 12 v6 merge).
  const hasHeldSelection = useMemo(
    () =>
      selectedAiTemplateIds.some(
        (id) => routeForAiTemplate(id, routeConfig.apiRoute) === 'product-swap',
      )
      || (designMode === 'ai' && !!creativeDirection.pose),
    [selectedAiTemplateIds, routeConfig.apiRoute, designMode, creativeDirection.pose],
  );
  // Aspect chips: intersect route's allowed list with carousel constraints
  // when carousel is on, AND drop 16:9 when a held template is selected. The
  // picker re-renders with whichever subset; if the current aspect drops out,
  // the useEffect below snaps to defaultAspect.
  const allowedAspectsNow: readonly ProtoAspect[] = useMemo(() => {
    let list = effectiveFormat === 'carousel'
      ? routeConfig.allowedAspects.filter((a) => CAROUSEL_ALLOWED_ASPECTS.includes(a))
      : [...routeConfig.allowedAspects];
    if (hasHeldSelection) list = list.filter((a) => a !== '16:9');
    return list;
  }, [effectiveFormat, routeConfig.allowedAspects, hasHeldSelection]);
  const insufficient = balance !== null && balance < creditCost;
  // Template mode is satisfied by EITHER a Recreate template
  // (selectedTemplateId — fashion/held routes) OR an AI template
  // (selectedAiTemplateId — Product Shot's Templates tab, Jun 11).
  // Product domain (skincare / consumable / wearable) from the picked
  // product's cached classification — null for raw file uploads or
  // products that don't fit a domain. Drives the Templates tab's
  // "Recommended for <domain>" grouping.
  const productDomain: AiTemplateDomain | null = useMemo(
    () => domainForProductMeta(productCategory, productTags),
    [productCategory, productTags],
  );
  // Jun 22 — Fashion route safety: when the user has uploaded a
  // CLASSIFIED product (saved-product pick or URL scrape carrying
  // productCategory/tags) and it is NOT wearable, BLOCK Generate. The
  // recipe library is apparel/footwear/accessory only — a skincare
  // bottle on Fashion produces garbled output. Raw file uploads land
  // with productDomain=null (no classification signal) → we allow them
  // through, same as the legacy /app behavior pre-vision-fallback.
  const fashionProductMismatch = useMemo(
    () => routeConfig.category === 'fashion'
      && !!productFile
      && productDomain !== null
      && productDomain !== 'wearable',
    [routeConfig.category, productFile, productDomain],
  );
  const canGenerate = !!productFile
    && !generating
    && !insufficient
    && !fashionProductMismatch
    && (designMode === 'ai' || !!selectedTemplateId || !!selectedAiTemplateId || !!selectedRecipeId);

  // Recipes-as-Templates DISPLAY hint (Jul 13). The actual product-type
  // filtering now happens SERVER-side in the recipe fetch (the backend owns
  // category→type + family subsumption — single source of truth, no stale
  // client vocab). This value is used ONLY by the RecipesSection for the
  // "matched to your <category>" label + the empty gate: [category] once a
  // product is picked, null while browsing (section shows unfiltered).
  const recipeProductTypes: string[] | null = useMemo(
    () => (productImageUrl && productCategory ? [productCategory] : null),
    [productImageUrl, productCategory],
  );

  // Jul 24 — Fashion is a fashion-only surface, so the library picker shows
  // ONLY wearable products (a skincare item can't produce a fashion render).
  // Non-library paths (URL scrape / raw upload) are still possible → the
  // Continue gate below blocks a classified non-fashion product. null is
  // preserved so the picker keeps its loading state.
  const studioSavedProducts = useMemo(() => {
    if (routeConfig.category !== 'fashion' || savedProducts === null) return savedProducts;
    return savedProducts.filter(
      (p) => domainForProductMeta(p.productCategory, p.tags) === 'wearable',
    );
  }, [routeConfig.category, savedProducts]);

  // Aspect coercion when carousel toggles ON and current aspect isn't
  // allowed (e.g. user had 16:9 on Product Shot). Snap to defaultAspect
  // if defaultAspect itself is allowed; else first in the allowed list.
  useEffect(() => {
    if (!allowedAspectsNow.includes(aspectRatio)) {
      const fallback = allowedAspectsNow.includes(routeConfig.defaultAspect)
        ? routeConfig.defaultAspect
        : allowedAspectsNow[0];
      if (fallback) setAspectRatio(fallback);
    }
  }, [allowedAspectsNow, aspectRatio, routeConfig.defaultAspect]);

  // ---------- Load My Products ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const products = await listProducts();
        if (!cancelled) setSavedProducts(products);
      } catch {
        if (!cancelled) setSavedProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------- Load production recipes (Recipes-as-Templates Phase 3) ----------
  // Fetch on mount + whenever the category changes. v1 surfaces only on
  // Product Shot route — the production picker for held/fashion/carousel
  // can come later. Empty list (no baked previews yet) is the expected
  // soft-empty state; tile section just hides when zero recipes match.
  useEffect(() => {
    // Recipes back the Templates tab on Product Shot (Jun 18) AND Fashion
    // (Jun 18 — apparel-family recipes). Other routes don't fetch.
    if (
      routeConfig.category !== 'product-shot'
      && routeConfig.category !== 'fashion'
      && routeConfig.category !== 'carousel'
      && routeConfig.category !== 'product-swap'
      && routeConfig.category !== 'social-post'
    ) {
      setRecipeTemplates(null);
      setRecipesLoading(false);
      return;
    }
    let cancelled = false;
    setRecipesLoading(true);
    // Sep 4 — Model Shot ('fashion') is the "item ON A MODEL" surface for
    // EVERY model recipe: worn garments AND model-held products (a model
    // holding your bottle / bag). So filter the route's recipe pool to
    // requires_model rather than the wearable domain — that way both the
    // browse grid AND the /templates deep-link (its locked single-recipe view
    // resolves against THIS list) surface all model recipes, not just apparel.
    // When a product is picked the fetch is already server-filtered by the
    // product's category, so this only keeps it model-consistent. Product Shot
    // and every other route → unfiltered here (Product Shot hides models from
    // browse via modelFilter='without' inside RecipesSection).
    // (Supersedes the Jul-24 wearable-domain-only filter.)
    const modelOnly = routeConfig.category === 'fashion';
    const filterForRoute = (list: ProductionRecipe[]): ProductionRecipe[] =>
      modelOnly ? list.filter((r) => r.requires_model) : list;
    (async () => {
      try {
        // Jul 13 — product-type filtering is now SERVER-side (single source of
        // truth: the backend owns category→type + family subsumption). EVERY
        // route (incl. Fashion) passes the picked product's raw category once
        // classified, so a pants product sees pants recipes, a tote sees bags
        // recipes, etc. Fashion additionally narrows to model-worn recipes via
        // the RecipesSection modelFilter='with'. (Fashion used to pin the fetch
        // to the 'wearable' family, but granular re-tagging emptied that tag →
        // 0 recipes for every garment; using the product's own category is both
        // correct AND non-empty.)
        // Browsing (no product yet) → unfiltered list off the instant seed cache.
        let recipes: ProductionRecipe[];
        if (productImageUrl && productCategory) {
          ({ recipes } = await listTemplateRecipes({ productCategory }));
        } else if (productImageUrl) {
          // Jul 13 — product picked but UNCLASSIFIED (no category). We don't
          // know what it is, so we show NO templates (a mismatched recipe would
          // be worse than none) — the user goes through AI Magic / GPT direct.
          recipes = [];
        } else {
          // Browsing, no product yet → unfiltered list off the instant seed.
          ({ recipes } = await listTemplateRecipes({
            onReconcile: (rs) => { if (!cancelled) setRecipeTemplates(filterForRoute(rs)); },
          }));
        }
        if (!cancelled) {
          const shown = filterForRoute(recipes);
          setRecipeTemplates(shown);
          // Jul 13 — empty pool on a showSynthesize route (Product Shot /
          // Fashion) with a product picked → pre-select the AI Magic tile so
          // Generate is ready and routes to gpt-image-2 freestyle (the synth
          // endpoint falls back to a from-scratch render when no recipe fits).
          if (
            shown.length === 0
            && productImageUrl
            && (routeConfig.category === 'product-shot' || routeConfig.category === 'fashion')
            // Sep 4 — but NEVER when the user deep-linked an explicit recipe
            // (?recipe=). Their chosen template must survive even if it doesn't
            // match the picked product's type, so "generate anyway" renders THAT
            // recipe (render-recipe looks it up by id) instead of silently
            // swapping to AI Magic.
            && !lockedRecipeId
          ) {
            setSelectedRecipeId(SYNTHESIZE_RECIPE_ID);
          }
        }
      } catch {
        if (!cancelled) setRecipeTemplates([]);
      } finally {
        if (!cancelled) setRecipesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeConfig.category, productImageUrl, productCategory, lockedRecipeId]);

  // ---------- Fetch AI-suggested CD defaults (Studio layout only) ----------
  // Studio layout = held + fashion. Product Shot keeps the legacy layout
  // which doesn't have this concept. Triggers whenever the picked product's
  // name changes (covers saved-product picks + manual file uploads where
  // the user typed a name). File uploads with no name leave suggested
  // defaults at null → user gets route-generic defaults.
  //
  // The picked saved product carries productCategory + productTags from
  // analyze-product/scrape-product — we forward both so the LLM has rich
  // context. File uploads without metadata get name-only context (LLM
  // still does a reasonable job).
  // Track edits since last generation — drives "Preview is N edit
  // behind" badge. Increment on every CD / aspect / quality / format /
  // framing / model change. Reset on successful generate.
  useEffect(() => {
    if (!hasResult) return;
    setEditsSinceGenerate((n) => n + 1);
    // Deliberately stale deps — we want to react to user-driven changes,
    // not to hasResult flipping false→true (that's handled by reset on
    // generate). This is a one-way counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creativeDirection, aspectRatio, quality, format,
    selectedFashionModelId,
    // Jun 12 — template / pattern picks no longer clear the result (the
    // render + its actions stay up until "Back to design" or "Try again");
    // they count as edits-behind instead, same as the CD filters.
    selectedTemplateId, selectedAiTemplateIds, selectedPattern,
  ]);

  // ---------- Handlers ----------
  const clearResult = useCallback(() => {
    setGeneratedImage(null);
    setAiResult(null);
    setError(null);
    setSaveState('idle');
    savedPostIdRef.current = null;
    setEditorActiveId(null);
    // Finishing state (Jun 12 v2) — a cleared result takes its edit
    // session with it. Only fires on product changes (template picks
    // keep the result per the edits-behind mechanic).
    setDisplayFrames([]);
    setTextOverlays([]);
    setLogoOverlays([]);
    setSuggestedFrames([]);
    setCreativeDirty(false);
    setActiveFrameIdx(0);
    setEditPrompt('');
    setEditError(null);
    // Matrix tracking.
    setResultIsMatrix(false);
    setResultTemplateIds([]);
    savedMatrixIdsRef.current = [];
  }, []);

  const handleSavedProductPick = useCallback(async (product: UserProduct) => {
    if (!product.imageUrl) return;
    try {
      const resp = await fetch(product.imageUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const filename = `saved-${product.id.slice(0, 8)}-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
      setProductFile(file);
      setProductImageUrl(dataUrl);
      setProductName(product.name ?? '');
      setProductDescription(product.description ?? '');
      setProductCategory(product.productCategory ?? null);
      setProductTags(product.tags ?? []);
      setSelectedSavedProductId(product.id);
      clearResult();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that product image.');
    }
  }, [clearResult]);

  const handleFileUpload = useCallback((file: File) => {
    // Clear product metadata SYNCHRONOUSLY — NOT inside the async
    // reader.onload. The URL-scrape path calls onUpload (→ here) and then
    // onScrapeSuccess in the same tick; if the category/tags clear lived in
    // reader.onload it would fire LATER and clobber the classification that
    // onScrapeSuccess just set, leaving productDomain null so the template
    // grid never shortlists (it showed all templates). Only the image-set
    // needs the reader result, so it stays in onload.
    setProductFile(file);
    setProductName('');
    setProductDescription('');
    setProductCategory(null);
    setProductTags([]);
    setSelectedSavedProductId(null);
    clearResult();
    const reader = new FileReader();
    reader.onload = () => setProductImageUrl(String(reader.result));
    reader.readAsDataURL(file);

    // Vision analysis (Jun 15 v3 fix) — without this, productName +
    // productDescription stay empty → backend's _build_wizard_concept
    // gets "the product" as fallback context → LLM has zero clue what
    // the product actually is → defaults to skincare patterns regardless
    // of upload. Fire-and-forget; if it fails the user can still type
    // name/description manually (or live with weaker outputs).
    //
    // Race protection: if the user uploads a NEW file before this
    // resolves, the older response would clobber the newer file's
    // metadata. Guard via a closure-captured file reference + setState
    // check that confirms we're still on the SAME file when applying.
    //
    // URL-scrape path race: onScrapeSuccess sets name/desc/category
    // SYNCHRONOUSLY in the same tick as handleFileUpload (for products
    // picked from the library or scraped from a URL). If those are
    // already populated by the time analyze resolves, we skip — the
    // scrape path's metadata is more reliable (came from the product
    // page directly, not vision inference).
    const analyzeForFile = file;
    void analyzeProduct(file)
      .then((res) => {
        // Skip if user uploaded a different file mid-flight OR if scrape/
        // library pick already populated metadata for this file.
        setProductFile((current) => {
          if (current !== analyzeForFile) return current;
          setProductName((prev) => prev || res.name || '');
          setProductDescription((prev) => prev || res.description || '');
          setProductCategory((prev) => prev || res.productCategory || null);
          setProductTags((prev) => prev.length > 0 ? prev : (res.tags || []));
          return current;
        });
      })
      .catch(() => {
        // Silent fail — user can type metadata manually. Logging would
        // spam the console for users on flaky connections.
      });
  }, [clearResult]);

  // Adapter for StepUpload's onUpload signature
  // `(images: string[], files: File[]) → void` → our single-file handler.
  // StepUpload bundles its internal file-pick + image-resize + data-URL
  // pipeline before calling onUpload, so by the time we get here `files`
  // has length 1 with the resized File ready to use.
  const handleStepUploadCallback = useCallback((_images: string[], files: File[]) => {
    if (files.length > 0) {
      handleFileUpload(files[0]);
      return;
    }
    // Empty arrays = StepUpload's "×" remove button. Clear the product so
    // the upload pane returns to its empty state (mirrors handleFileUpload's
    // reset, to empty values).
    setProductFile(null);
    setProductImageUrl(null);
    setProductName('');
    setProductDescription('');
    setProductCategory(null);
    setProductTags([]);
    setSelectedSavedProductId(null);
    clearResult();
  }, [handleFileUpload, clearResult]);

  // URL-scrape success → populate name + description (and category/tags
  // for downstream defaults). Same shape /appv2 uses.
  const handleScrapeSuccess = useCallback((
    name: string,
    description: string,
    insights?: { productCategory?: string; tags?: string[] },
  ) => {
    setProductName(name);
    setProductDescription(description);
    setProductCategory(insights?.productCategory ?? null);
    setProductTags(insights?.tags ?? []);
    setSelectedSavedProductId(null);
  }, []);

  // Jun 11 — Inline product picker on /appv3 Studio routes (Fashion / Held /
  // Product Shot). Default: open when no product has been picked yet. User
  // confirms selection via "Continue" → picker collapses to preview mode +
  // a small thumb + Edit button at the top of the right panel (clicking
  // Edit re-opens the picker).
  const [productPickerOpen, setProductPickerOpen] = useState(true);
  // Sync once on mount based on whether we already have a product (e.g.
  // user is returning to /appv3 with state from a prior session).
  useEffect(() => {
    if (productImageUrl) setProductPickerOpen(false);
    // Intentionally empty deps — only fires once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleProductPickerContinue = useCallback(() => {
    // Jul 24 — Fashion: don't let a classified non-fashion product past the
    // picker. The button is also disabled below, but guard the handler too.
    if (fashionProductMismatch) return;
    setProductPickerOpen(false);
    // Jul 14 (user lock) — Product Shot: on Continue, default-select the
    // AI Magic (fusion / synthesize) tile so the user doesn't have to pick a
    // template. Only when they haven't already chosen a recipe/template
    // (incl. a ?recipe/?aiTemplate deep-link) and we're on the Templates tab.
    if (
      routeConfig.category === 'product-shot'
      && designMode === 'template'
      && !selectedRecipeId
      && selectedAiTemplateIds.length === 0
    ) {
      setSelectedRecipeId(SYNTHESIZE_RECIPE_ID);
    }
  }, [routeConfig.category, designMode, selectedRecipeId, selectedAiTemplateIds, fashionProductMismatch]);
  const handleProductEdit = useCallback(() => {
    setProductPickerOpen(true);
  }, []);

  // Toggle an AI template in/out of the multi-select array (Jun 12 v3).
  // Real templates accumulate (numbered badges → matrix). The AI Magic
  // freestyle tile is EXCLUSIVE: picking it clears any real templates and
  // vice-versa (decision — the matrix is "real templates × products";
  // freestyle has no sample to pair, so it stays a single-select path).
  const handleAiTemplateToggle = useCallback((id: string) => {
    // V3 Carousel (Jun 14) — SINGLE-select only. Carousel is 1 template
    // × N frames; multi-template matrix mode doesn't apply (the frames
    // come from carousel_type, not from a matrix of templates). Picking
    // a 2nd template REPLACES the 1st.
    const isCarousel = routeConfig.category === 'carousel';
    setSelectedAiTemplateIds((prev) => {
      if (id === AI_FREESTYLE_TEMPLATE_ID) {
        return prev.length === 1 && prev[0] === AI_FREESTYLE_TEMPLATE_ID
          ? []
          : [AI_FREESTYLE_TEMPLATE_ID];
      }
      if (isCarousel) {
        // Single-select: clicking the same tile toggles off; clicking
        // a different tile replaces.
        if (prev.length === 1 && prev[0] === id) return [];
        return [id];
      }
      const base = prev.filter((x) => x !== AI_FREESTYLE_TEMPLATE_ID);
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
    // Picking ANY AI template clears recipe selection (mutually exclusive).
    setSelectedRecipeId(null);
  }, [routeConfig.category]);

  // Recipe template select — single-select, toggles off when clicking
  // the same id again. Clears AI templates (mutually exclusive paths).
  const handleRecipeSelect = useCallback((id: string) => {
    setSelectedRecipeId((prev) => (prev === id ? null : id));
    setSelectedAiTemplateIds([]);
  }, []);

  // ---- Create-tab hero: Pattern XOR Pose (Jun 12 v6 — In Hand merge) ----
  // The Product Shot Create tab now has TWO stacked hero groups: Patterns
  // (product-only arrangements → product-shot backend) and Poses (held →
  // product-swap backend). Exactly one is the active composition; picking
  // in one group clears the other. Pose mode also seeds a sensible camera
  // angle (product-swap's default framing) so the held render isn't framed
  // with a product-only framing value.
  const handlePatternPick = useCallback((p: CreativePattern | null) => {
    setSelectedPattern(p);
    setCreativeDirection((cd) => {
      if (cd.pose === undefined) return cd;
      const next = { ...cd };
      delete next.pose;
      return next;
    });
  }, []);
  const handlePosePick = useCallback((pose: CreativePose) => {
    setSelectedPattern(null);
    setCreativeDirection((cd) => ({
      ...cd,
      pose,
      framing: ROUTE_CONFIG['product-swap'].defaultFraming,
    }));
  }, []);

  // AI copy for the finish editor's text layers (headline + tagline via
  // the FREE /api/suggest-copy endpoint). Fired in the background after a
  // render lands whenever the generation path didn't produce concept copy
  // (template + AI-template renders bypass the concept LLM). Best-effort:
  // failure just leaves the generic placeholder defaults.
  const fetchAiCopy = useCallback(async (file: File, frameCt: number) => {
    try {
      const res = await suggestCopy({
        productFile: file,
        productName,
        productDescription,
        frameCount: frameCt,
      });
      if (res.frames?.length) setSuggestedFrames(res.frames);
    } catch (err) {
      console.warn('[appv3] suggest-copy failed (placeholders stay)', err);
    }
  }, [productName, productDescription]);

  // Fresh AI copy for the editor's "AI text" button. suggest-copy converges to
  // near-identical copy on repeat single-frame calls, so instead we ask for a
  // BATCH of 6 frames (the prompt makes each frame's copy DISTINCT — a narrative
  // arc) and flatten to ~12 deduped headline/tagline lines. The editor drains
  // them one per click, so every click gives new text before re-fetching.
  const handleRequestAiText = useCallback(async (): Promise<string[] | null> => {
    if (!productFile) return null;
    try {
      const res = await suggestCopy({ productFile, productName, productDescription, frameCount: 6 });
      const seen = new Set<string>();
      const lines: string[] = [];
      for (const f of res.frames ?? []) {
        for (const s of [f.headline, f.tagline]) {
          const t = s?.trim();
          if (!t) continue;
          const k = t.toLowerCase();
          if (seen.has(k)) continue;
          seen.add(k);
          lines.push(t);
        }
      }
      return lines.length ? lines : null;
    } catch (err) {
      console.warn('[appv3] fresh AI text failed', err);
      return null;
    }
  }, [productFile, productName, productDescription]);

  // Optional qualityOverride powers the result row's "Try again" popover
  // (re-run the same setup at a picked quality). RUNTIME-GUARDED rather
  // than trusting the type: several call sites pass this straight into
  // onClick, so the first arg can be a MouseEvent — anything that isn't
  // a literal quality value is ignored.
  const handleGenerate = useCallback(async (qualityOverride?: Quality) => {
    const effQuality: Quality =
      qualityOverride === 'low' || qualityOverride === 'medium' || qualityOverride === 'high'
        ? qualityOverride
        : quality;
    if (effQuality !== quality) setQuality(effQuality);
    if (!productFile) return;
    if (designMode === 'template' && !selectedTemplateId && !selectedAiTemplateId && !selectedRecipeId) return;
    setError(null);
    setGenerating(true);
    setGeneratedImage(null);
    setAiResult(null);
    overlayOutRef.current = null;
    setSaveState('idle');
    savedPostIdRef.current = null;
    setEditorActiveId(null);
    // Reset matrix tracking — set true only in the matrix branch below.
    setResultIsMatrix(false);
    savedMatrixIdsRef.current = [];
    // Fresh render targets frame 1; any stale prompt-edit error clears.
    setActiveFrameIdx(0);
    setEditError(null);

    // ─── Recipe template short-circuit (Jun 18 — Phase 3) ────────────
    // When user picked a recipe-backed template, dispatch through the
    // dedicated render endpoint that bypasses concept LLM / scene anchor
    // / carousel branching. Single image, full credit lifecycle handled
    // server-side. Result lands in displayFrames[0] same as the
    // non-matrix AI template path.
    // AI Magic / Synthesize — recombine a vibe's recipe attributes into a
    // brand-new spec server-side, then render. Single image, same result
    // plumbing as a single recipe render.
    // Recipe branches are TEMPLATE-mode only. Without the designMode gate
    // they fire whenever selectedRecipeId is set — which survives a switch
    // to the Create tab — so picking a template then generating from Create
    // re-rendered the old recipe and ignored the model + filters. (The tab
    // switch also clears selectedRecipeId now; this gate is the guarantee.)
    if (designMode === 'template' && selectedRecipeId === SYNTHESIZE_RECIPE_ID) {
      try {
        const result = await synthesizeRecipe({
          product: productFile,
          aspectRatio,
          quality: effQuality,
          productName,
          productDescription,
          creativeDirection,
          // Jul 26 — send the product's REAL leaf category for BOTH routes
          // (was hardcoded 'apparel' for fashion). The classifier emits leaves
          // now (shirts / sneakers / heels / belts / …), not the dead 'apparel'
          // umbrella, so 'apparel' missed every leaf-tagged recipe + couldn't
          // represent shoes/belts. Backend resolves worn/held from the leaf and
          // forces kind=model_holding for the fashion route.
          productType: productCategory ?? undefined,
          // Jul 13 — route drives the backend's empty-pool freestyle
          // fallback scaffold (Fashion → "model wearing the garment").
          route: routeConfig.apiRoute,
          // Jul 13 — Carousel renders the picked recipe at 4 framings.
          format: effectiveFormat,
        });
        setResultIsMatrix(false);
        if (result.frames && result.frames.length) {
          // Carousel — 4 frames → ONE carousel post (not a matrix). Same
          // shape the template-carousel + ai-generate carousel paths make.
          overlayOutRef.current = null;
          setDisplayFrames(result.frames.map((f) => `data:image/png;base64,${f}`));
          setRenderedAspect(aspectRatio);
          setActiveFrameIdx(0);
        } else {
          const dataUrl = `data:image/png;base64,${result.image_b64}`;
          // Text-overlay tile → stash base scene + editor doc for the save.
          overlayOutRef.current =
            result.base_image && result.text_overlay
              ? { base_image: result.base_image, text_overlay: result.text_overlay }
              : null;
          setDisplayFrames([dataUrl]);
          setRenderedAspect(aspectRatio);
          setGeneratedImage(dataUrl);
          setActiveFrameIdx(0);
        }
        // Recipe renders bake the copy into the pixels; fetch fresh AI
        // headline/tagline so the editor's "AI text" button + new text layers
        // have copy to insert.
        void fetchAiCopy(productFile, result.frames?.length || 1);
        await refreshCredits();
      } catch (e: unknown) {
        if (e instanceof InsufficientCreditsError) {
          setError('Not enough credits to generate.');
        } else if (routeConfig.category === 'fashion') {
          // Jul 26 — a fashion (Model Shot) render that fails is almost always
          // an AI content-policy rejection (lingerie / swimwear / bikinis).
          // The backend already refunds the credit on any render failure, so
          // surface a clear "won't work, no charge, don't retry" message rather
          // than a generic error. (Content-policy detail is swallowed backend-
          // side, so this is route-scoped rather than error-matched.)
          setError(
            "This item can't be generated — lingerie, swimwear, and bikinis are "
            + "blocked by the AI's content policy, so retrying won't help. Your "
            + 'credit has been refunded (no charge). Try a different garment.',
          );
        } else {
          const msg = e instanceof Error ? e.message : String(e);
          setError(`AI Magic failed: ${msg}`);
        }
      } finally {
        setGenerating(false);
      }
      return;
    }

    if (designMode === 'template' && selectedRecipeId) {
      // Recipe → carousel = 1 recipe rendered at 4 camera distances
      // (hero/close/wide/macro) → one carousel post.
      // Jun 22 — use effectiveFormat (respects the footer Single/
      // Carousel toggle) instead of the old route-based hardcode that
      // only enabled carousel on /appv3/carousel. The toggle was visible
      // on Product Shot + Fashion footers but did nothing because the
      // dispatch ignored it and always sent format='single'. The route-
      // based check also caused a bug on /appv3/carousel where the
      // toggle was forced to carousel — effectiveFormat already reflects
      // that via the route-reset effect at line 662 (sets format to
      // 'carousel' when category === 'carousel'), so this collapses
      // both branches to one source of truth. Social Post passes the
      // picked intent (shapes copy; recipe owns the visual).
      const recipeFormat: 'single' | 'carousel' = effectiveFormat;
      const recipeIntent =
        routeConfig.category === 'social-post' ? (selectedIntent ?? undefined) : undefined;
      try {
        const result = await wizardRenderRecipe({
          product: productFile,
          recipeId: selectedRecipeId,
          aspectRatio,
          quality: effQuality,
          productName,
          productDescription,
          creativeDirection,
          format: recipeFormat,
          intent: recipeIntent,
        });
        if (recipeFormat === 'carousel' && result.frames?.length) {
          // 4 frames → ONE carousel post (not a matrix). Same shape the
          // template-carousel + ai-generate carousel paths produce, so the
          // finishing editor + zip-save path handle it unchanged.
          const frames = result.frames.map((f) => `data:image/png;base64,${f}`);
          setResultIsMatrix(false);
          setDisplayFrames(frames);
          setRenderedAspect(aspectRatio);
          setActiveFrameIdx(0);
        } else {
          const dataUrl = `data:image/png;base64,${result.image_b64}`;
          setDisplayFrames([dataUrl]);
          setRenderedAspect(aspectRatio);
          setGeneratedImage(dataUrl);
          setActiveFrameIdx(0);
          // Text-overlay tile (Templates path) — carry base + doc to save so it
          // opens in the same /creatives overlay editor as AI Magic.
          overlayOutRef.current =
            result.base_image && result.text_overlay
              ? { base_image: result.base_image, text_overlay: result.text_overlay }
              : null;
        }
        // Recipe renders bake the copy into the pixels; fetch fresh AI
        // headline/tagline so the editor's "AI text" button + new text layers
        // have copy to insert.
        void fetchAiCopy(productFile, result.frames?.length || 1);
        await refreshCredits();
      } catch (e: unknown) {
        if (e instanceof InsufficientCreditsError) {
          setError('Not enough credits to render this recipe.');
        } else {
          const msg = e instanceof Error ? e.message : String(e);
          setError(`Render failed: ${msg}`);
        }
      } finally {
        setGenerating(false);
      }
      return;
    }

    // MATRIX path (Jun 12 v3) — N AI templates × 1 product → N independent
    // images. Only product-shot / In Hand Templates tab, single format,
    // 2+ real templates. Carousel + multi-product round-robin is next.
    const realTemplateIds = selectedAiTemplateIds.filter((id) => id !== AI_FREESTYLE_TEMPLATE_ID);
    const isMatrix = designMode === 'template' && aiTemplateRoute
      && effectiveFormat === 'single' && realTemplateIds.length > 1;
    // Carousel + AI templates (Product Shot ONLY) → ONE carousel; its 4
    // frames round-robin the picked templates (frame i = template[i % N]).
    // V3 Carousel (Jun 14) is EXCLUDED — it's single-template + type-driven
    // (one backend call with format='carousel' + carouselType + aiTemplateId),
    // NOT round-robin. Letting carousel category hit this branch would fire
    // 4 INDEPENDENT single-frame AI-template renders (each backend call has
    // no format='carousel'), so the carousel_type scaffold + scene-anchor
    // wiring would never run → frames look identical, type ignored.
    const isTemplateCarousel = designMode === 'template' && aiTemplateRoute
      && effectiveFormat === 'carousel' && realTemplateIds.length >= 1
      && routeConfig.category !== 'carousel';

    try {
      if (isTemplateCarousel) {
        // 4 frames, round-robin the templates. Fire 4 single AI-template
        // renders (concurrency 2) and assemble ONE carousel post (saved as a
        // frame1..4.png zip — NOT a matrix of separate rows).
        const ids = Array.from({ length: 4 }, (_, i) => realTemplateIds[i % realTemplateIds.length]);
        const frames: string[] = new Array(4).fill('');
        const copies: SuggestedFrame[] = ids.map(() => ({
          headline: productName || 'Your headline',
          tagline: '',
        }));
        let next = 0;
        const worker = async (): Promise<void> => {
          for (;;) {
            const i = next++;
            if (i >= 4) return;
            const r = await wizardAiGenerate({
              product: productFile,
              route: routeForAiTemplate(ids[i], routeConfig.apiRoute),
              aspectRatio,
              quality: effQuality,
              productName,
              productDescription,
              creativeDirection,
              aiTemplateId: ids[i],
              // V3 Carousel — non-carousel routes have selectedCarouselType
              // === null → this passes undefined → backend ignores it.
              // Matrix mode is also gated off for carousel category.
              carouselType: selectedCarouselType && selectedCarouselType !== AI_CHOOSE_TYPE_ID ? selectedCarouselType : undefined,
              // V3 Social Post intent — non-social-post routes have
              // selectedIntent === null → undefined → backend no-ops.
              intent: selectedIntent ?? undefined,
            });
            frames[i] = `data:image/png;base64,${r.frames[0]}`;
            if (r.concept?.headline) {
              copies[i] = { headline: r.concept.headline, tagline: r.concept.subhead || '' };
            }
          }
        };
        await Promise.all([worker(), worker()]);
        setResultIsMatrix(false); // ONE carousel post, not a matrix
        setDisplayFrames(frames);
        setRenderedAspect(aspectRatio);
        setSuggestedFrames(copies);
        if (!copies.some((c) => c.headline && c.headline !== productName)) {
          void fetchAiCopy(productFile, 4);
        }
        setCreativeDirty(false);
        setEditsSinceGenerate(0);
        refreshCredits();
      } else if (isMatrix) {
        // Fire with concurrency 2 (OpenAI input-image rate cap, gotcha #69).
        // Results collected in template order; the finishing filmstrip shows
        // them once the whole batch lands (the generating takeover covers the
        // wait). Per-render credit reserve/refund is server-side, so a failed
        // render refunds itself; a mid-batch failure surfaces as an error.
        const ids = [...realTemplateIds];
        const frames: string[] = new Array(ids.length).fill('');
        const copies: SuggestedFrame[] = ids.map(() => ({
          headline: productName || 'Your headline',
          tagline: '',
        }));
        let next = 0;
        const worker = async (): Promise<void> => {
          for (;;) {
            const i = next++;
            if (i >= ids.length) return;
            const r = await wizardAiGenerate({
              product: productFile,
              route: routeForAiTemplate(ids[i], routeConfig.apiRoute),
              aspectRatio,
              quality: effQuality,
              productName,
              productDescription,
              creativeDirection,
              aiTemplateId: ids[i],
              // V3 Carousel — non-carousel routes have selectedCarouselType
              // === null → this passes undefined → backend ignores it.
              // Matrix mode is also gated off for carousel category.
              carouselType: selectedCarouselType && selectedCarouselType !== AI_CHOOSE_TYPE_ID ? selectedCarouselType : undefined,
              // V3 Social Post intent — non-social-post routes have
              // selectedIntent === null → undefined → backend no-ops.
              intent: selectedIntent ?? undefined,
            });
            frames[i] = `data:image/png;base64,${r.frames[0]}`;
            if (r.concept?.headline) {
              copies[i] = { headline: r.concept.headline, tagline: r.concept.subhead || '' };
            }
          }
        };
        await Promise.all([worker(), worker()]);
        setResultIsMatrix(true);
        setResultTemplateIds(ids);
        savedMatrixIdsRef.current = new Array(ids.length).fill(null);
        setDisplayFrames(frames);
        setRenderedAspect(aspectRatio);
        setSuggestedFrames(copies);
        // AI-template renders bypass the concept LLM → usually no copy.
        // Fetch real headline/tagline in the background for the text layers.
        if (!copies.some((c) => c.headline && c.headline !== productName)) {
          void fetchAiCopy(productFile, 1);
        }
        setCreativeDirty(false);
        setEditsSinceGenerate(0);
        refreshCredits();
      } else if (designMode === 'ai') {
        // Resolve optional fashion model preference. Only the fashion
        // route surfaces the picker; other routes send undefined for
        // both. modelHint is text-only (LLM concept writer); modelId
        // triggers backend to load the model's base.png and pass it as
        // a reference image to gpt-image (the actual visual lock).
        let modelHint: string | undefined;
        let modelId: string | undefined;
        if (routeConfig.category === 'fashion' && selectedFashionModelId) {
          const picked = fashionModels.find((m) => m.id === selectedFashionModelId);
          if (picked) {
            modelHint = `${picked.name} · ${picked.vibe}`;
            modelId = picked.id;
          }
        }
        // Pose XOR pattern (Jun 12 v6 merge). When a POSE is active, this is
        // a HELD shot → route through the product-swap backend (safety
        // scaffold + "model holding"), send cd.pose, no pattern_id. Otherwise
        // it's a product-only PATTERN → product-shot backend + pattern_id.
        const poseMode = !!creativeDirection.pose;
        // V3 Social Post (Jun 15 v3) — Shot type picker overrides routing:
        //   'ai-chooses' (default) → social-post route, no extra directive
        //   'product-only' → social-post route + shotMode='product-only' form
        //                    field (backend injects "no humans" directive)
        //   'in-use' → product-swap route (existing Held safety scaffold)
        const socialInUse = routeConfig.category === 'social-post' && socialPostShot === 'in-use';
        const aiRoute = socialInUse
          ? 'product-swap'
          : poseMode
            ? 'product-swap'
            : routeConfig.apiRoute;
        // shotMode rides ALONGSIDE the route — only meaningful when route
        // stays 'social-post' (i.e. NOT in-use, NOT pose-mode); sending it
        // on other routes is harmless because backend's handler ignores
        // unknown shot_mode values + routes with their own scaffolds
        // (product-swap, fashion) take precedence.
        const shotMode = routeConfig.category === 'social-post' && socialPostShot === 'product-only'
          ? 'product-only'
          : undefined;
        let patternId: string | undefined;
        if (!poseMode
          && (routeConfig.category === 'product-shot' || routeConfig.category === 'product-swap')
          && selectedPattern) {
          patternId = PATTERN_TO_BACKEND_ID[selectedPattern];
        }
        // V3 Social Post (Jun 15 v2) — when master Filters toggle is OFF
        // on social-post, strip visual CD fields (vibe/light/bg/framing/
        // text_density) so backend's allowlist drops their prompt
        // fragments. Brand chips (brand_presence, logo, show_logo) ride
        // independently (they're driven by the right-footer chips, not
        // the gated left filter bar). Backend's `_format_creative_direction_block`
        // skips empty values via .get(value, "") — so we just blank them.
        const cdForGenerate: CreativeDirection = (
          routeConfig.category === 'social-post' && !filtersEnabled
        )
          ? {
              ...creativeDirection,
              // Visual fields cleared → backend emits no fragments for these
              // (lookup via .get(value) returns None, which the if-check
              // filters out). Cast empty strings through the value union;
              // backend treats unknown values as "no fragment".
              mood: '' as unknown as CreativeDirection['mood'],
              light: '' as unknown as CreativeDirection['light'],
              background: '' as unknown as CreativeDirection['background'],
              framing: '' as unknown as CreativeDirection['framing'],
              // text_density = 'Text + CTA' is the backend's no-op default
              // (maps to empty override string in _CD_TEXT_DENSITY_RENDER).
              // The INTENT scaffold drives text shape, NOT this knob —
              // setting 'None' here would actively suppress text and fight
              // the intent's text-heavy directives.
              text_density: 'Text + CTA',
            }
          : creativeDirection;
        const result = await wizardAiGenerate({
          product: productFile,
          route: aiRoute,
          aspectRatio,
          quality: effQuality,
          productName,
          productDescription,
          creativeDirection: cdForGenerate,
          modelHint,
          modelId,
          format: effectiveFormat,
          // Only ship the framings list in carousel mode — single
          // generations don't read it; backend tolerates either but
          // sending only when needed keeps the wire format clean.
          // V3 Carousel: when carouselType is set, the backend's
          // type-scaffold OVERRIDES framings-mode anyway, so passing
          // both is harmless — backend picks type-mode first.
          frameFramings: effectiveFormat === 'carousel'
            ? [...routeConfig.carouselFramings]
            : undefined,
          patternId,
          // V3 Carousel — carouselType drives per-frame structural
          // scaffold in helpers/ad_brief.py. Null on non-carousel routes.
          carouselType: selectedCarouselType && selectedCarouselType !== AI_CHOOSE_TYPE_ID ? selectedCarouselType : undefined,
          // V3 Social Post intent — null on non-social-post routes.
          intent: selectedIntent ?? undefined,
          // V3 Social Post (Jun 15 v3) — Shot type 'product-only' rides
          // through as a directive injection. Undefined on AI Chooses
          // (no constraint) + In Use (route='product-swap' carries its
          // own scaffold).
          shotMode,
        });
        setAiResult(result);
        setDisplayFrames(result.frames.map((f) => `data:image/png;base64,${f}`));
        setRenderedAspect(aspectRatio);
        setSuggestedFrames(result.frames.map(() => ({
          headline: result.concept?.headline || productName || 'Your headline',
          tagline: result.concept?.subhead || '',
        })));
        // Concept LLM usually wrote the copy on this path; fall back to
        // suggest-copy when it didn't (pattern dispatch bypasses it).
        if (!result.concept?.headline) {
          void fetchAiCopy(productFile, result.frames.length);
        }
        setCreativeDirty(false);
        setEditsSinceGenerate(0);
        refreshCredits();
      } else if (
        (routeConfig.category === 'product-shot'
          || routeConfig.category === 'product-swap'
          // V3 Carousel (Jun 14) — single-template + type-driven path.
          // Carousel category lands here so ONE backend call ships with
          // format='carousel' + carouselType + aiTemplateId. Backend's
          // _build_wizard_concept(carousel_type=...) writes 4 type-
          // aware visual_directions + scene_ref_bytes (template sample)
          // rides into every frame. Without this branch, carousel +
          // template would have no path that runs the type scaffold.
          || routeConfig.category === 'carousel')
        && selectedAiTemplateId
      ) {
        // AI template mode (Product Shot + In Hand, Jun 11) — route
        // through the wizard endpoint with `aiTemplateId` set. Backend
        // bypasses the concept LLM and uses the template's hardcoded
        // prompt as visual_direction (single-frame mode). For V3
        // Carousel category, the backend instead takes the type-mode
        // branch (carousel_type set + is_carousel) and calls
        // _build_wizard_concept which writes 4 type-aware frames.
        // Returns a single frame for product-shot, 4 frames for carousel.
        // creativeDirection rides along for the LOGO gate (cd.logo='Off'
        // → backend skips the composite) + brand presence. The visual
        // filters (vibe/light/background) are ignored in this branch —
        // the template's prompt IS the visual direction.
        //
        // AI MAGIC FREESTYLE (Jun 12): the sentinel tile sends NO
        // aiTemplateId — backend falls through to the concept-LLM path
        // and designs the scene from the product metadata + brand
        // context alone. No pattern dispatch either (that's a Create-tab
        // concept) — pure freestyle.
        const result = await wizardAiGenerate({
          product: productFile,
          // Per-template route (Jun 12 v6) — a held template generates through
          // the product-swap backend (safety scaffold). Freestyle / unknown →
          // page route.
          route: selectedAiTemplateId
            ? routeForAiTemplate(selectedAiTemplateId, routeConfig.apiRoute)
            : routeConfig.apiRoute,
          aspectRatio,
          quality: effQuality,
          productName,
          productDescription,
          creativeDirection,
          aiTemplateId: selectedAiTemplateId === AI_FREESTYLE_TEMPLATE_ID
            ? undefined
            : selectedAiTemplateId,
          // V3 Carousel — when category='carousel', format is locked to
          // 'carousel' (useEffect on mount) and carouselType is set,
          // so backend's CAROUSEL_TYPE_SCAFFOLDS picks the per-frame
          // scaffold. Also forces format='carousel' here for the AI-
          // template path which doesn't go through `format:` above.
          carouselType: selectedCarouselType && selectedCarouselType !== AI_CHOOSE_TYPE_ID ? selectedCarouselType : undefined,
          // V3 Social Post intent — null on non-social-post routes.
          intent: selectedIntent ?? undefined,
          format: routeConfig.category === 'carousel' ? 'carousel' : undefined,
        });
        setAiResult(result);
        setDisplayFrames(result.frames.map((f) => `data:image/png;base64,${f}`));
        setRenderedAspect(aspectRatio);
        setSuggestedFrames(result.frames.map(() => ({
          headline: result.concept?.headline || productName || 'Your headline',
          tagline: result.concept?.subhead || '',
        })));
        // AI-template renders bypass the concept LLM (the template's
        // prompt IS the visual direction) → no concept copy. Fetch real
        // AI headline/tagline in the background for the text layers.
        if (!result.concept?.headline) {
          void fetchAiCopy(productFile, result.frames.length);
        }
        setCreativeDirty(false);
        setEditsSinceGenerate(0);
        refreshCredits();
      } else {
        // Template mode — look up the picked Recreate template + use its
        // locked `templateId` (the backend scene id) + provider. Aspect
        // + quality fall through from the UI state so the user can
        // override per-render. Falls back gracefully if for some reason
        // the id doesn't resolve.
        const tmpl = routeTemplates.find((t) => t.id === selectedTemplateId);
        const result = await generateImage({
          productFile,
          templateId: tmpl?.templateId ?? selectedTemplateId!,
          frameNumber: 1,
          aspectRatio: aspectRatio as AspectRatio,
          productName,
          productDescription,
          quality: effQuality,
          provider: tmpl?.provider ?? 'openai',
        });
        setGeneratedImage(result.imageUrl);
        setDisplayFrames([result.imageUrl]);
        setRenderedAspect(aspectRatio);
        setSuggestedFrames([{
          headline: productName || 'Your headline',
          tagline: productDescription.slice(0, 90),
        }]);
        // Recreate-template renders have no concept LLM at all — always
        // fetch real AI copy for the text layers.
        void fetchAiCopy(productFile, 1);
        setCreativeDirty(false);
        setEditsSinceGenerate(0);
        refreshCredits();
      }
    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        setError(err.message || 'Not enough credits. Upgrade or top up to continue.');
      } else {
        setError(err instanceof Error ? err.message : 'Generation failed.');
      }
    } finally {
      setGenerating(false);
    }
  }, [
    productFile, designMode, selectedTemplateId, selectedAiTemplateId,
    selectedRecipeId,
    selectedAiTemplateIds, aiTemplateRoute,
    aspectRatio, quality, productName, productDescription, creativeDirection,
    refreshCredits, routeConfig, routeTemplates, selectedFashionModelId,
    selectedPattern, effectiveFormat, fetchAiCopy,
    selectedCarouselType,
    // V3 Social Post (Jun 15 v2) — drive the generate handler: intent
    // scaffold rides into the prompt; filtersEnabled gates whether CD
    // visual fields are sent; socialPostShot toggles the apiRoute.
    selectedIntent, filtersEnabled, socialPostShot,
  ]);

  // ---------- Result actions (Jun 12 — Studio footer post-result row) ----------

  // Resolve the current creative into its FINAL blob: base frames with
  // text/shape/logo overlays flattened via the SAME canvas pipeline
  // StepText's download uses (flattenComposedFrame). With no overlays
  // this degenerates to the raw render — so auto-save, download, and the
  // replace-sync all share ONE path. Singles → PNG; carousels →
  // frame1..4.png zip (gallery's CarouselThumbnail convention).
  const buildFinalBlob = useCallback(async (): Promise<{
    blob: Blob; contentType: string; fileExt: string;
  } | null> => {
    if (displayFrames.length === 0) return null;
    if (displayFrames.length > 1) {
      const { zip } = await import('fflate');
      const blobs = await Promise.all(
        displayFrames.map((src, i) => flattenComposedFrame({
          imageSrc: src,
          frameIndex: i,
          overlays: textOverlays,
          shapeOverlays,
          logoOverlays,
        })),
      );
      const entries: Record<string, Uint8Array> = {};
      for (let i = 0; i < blobs.length; i++) {
        const b = blobs[i];
        if (!b) throw new Error(`Could not render frame ${i + 1}`);
        entries[`frame${i + 1}.png`] = new Uint8Array(await b.arrayBuffer());
      }
      const zipped: Uint8Array = await new Promise((resolve, reject) => {
        zip(entries, (err, data) => (err ? reject(err) : resolve(data)));
      });
      return {
        blob: new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' }),
        contentType: 'application/zip',
        fileExt: 'zip',
      };
    }
    const blob = await flattenComposedFrame({
      imageSrc: displayFrames[0],
      frameIndex: 0,
      overlays: textOverlays,
      shapeOverlays,
      logoOverlays,
    });
    if (!blob) return null;
    return { blob, contentType: 'image/png', fileExt: 'png' };
  }, [displayFrames, textOverlays, shapeOverlays, logoOverlays]);

  // Overlay setters wrapped with dirty-marking — any composition change
  // means the saved creative is stale until syncSavedCreative runs.
  const handleTextOverlaysChange = useCallback((o: TextOverlay[]) => {
    setTextOverlays(o);
    setCreativeDirty(true);
  }, []);
  const handleLogoOverlaysChange = useCallback((l: LogoOverlay[]) => {
    setLogoOverlays(l);
    setCreativeDirty(true);
  }, []);

  // ---- Matrix save (Jun 12 v3) — N independent rows, NOT a carousel zip ----
  // Each frame flattens with its own overlays (filtered by imageIndex inside
  // flattenComposedFrame) and saves its OWN My Creatives row. Idempotent via
  // savedMatrixIdsRef. Per-frame template id from resultTemplateIds (captured
  // at generate time so a drifting live selection doesn't mislabel rows).
  const saveMatrixRows = useCallback(async (): Promise<void> => {
    if (savedMatrixIdsRef.current.some(Boolean)) return;
    if (saveState === 'saving') return;
    try {
      setSaveState('saving');
      const ids: (string | null)[] = [];
      for (let i = 0; i < displayFrames.length; i++) {
        const blob = await flattenComposedFrame({
          imageSrc: displayFrames[i],
          frameIndex: i,
          overlays: textOverlays,
          shapeOverlays,
          logoOverlays,
        });
        if (!blob) { ids.push(null); continue; }
        const id = await savePost({
          blob,
          templateId: resultTemplateIds[i] ?? null,
          aspectRatio: renderedAspect,
          headline: suggestedFrames[i]?.headline ?? null,
          tagline: suggestedFrames[i]?.tagline ?? null,
          title: productName.trim() || 'AI creative',
          contentType: 'image/png',
          fileExt: 'png',
          productId: selectedSavedProductId,
        });
        ids.push(id);
      }
      savedMatrixIdsRef.current = ids;
      setSaveState('saved');
      setCreativeDirty(false);
    } catch (err) {
      console.error('[appv3] matrix save failed', err);
      setSaveState('error');
    }
  }, [
    saveState, displayFrames, textOverlays, shapeOverlays, logoOverlays,
    resultTemplateIds, renderedAspect, suggestedFrames, productName,
    selectedSavedProductId,
  ]);

  // Re-flatten + replace every saved matrix row after edits (overlays /
  // prompt-edits). Simple + correct: re-saves all rows. No-op while clean.
  const syncMatrixRows = useCallback(async (force = false): Promise<void> => {
    if (!force && !creativeDirty) return;
    const ids = savedMatrixIdsRef.current;
    if (!ids.some(Boolean)) return;
    try {
      for (let i = 0; i < displayFrames.length; i++) {
        const postId = ids[i];
        if (!postId) continue;
        const blob = await flattenComposedFrame({
          imageSrc: displayFrames[i],
          frameIndex: i,
          overlays: textOverlays,
          shapeOverlays,
          logoOverlays,
        });
        if (!blob) continue;
        await replacePostImage({ postId, blob, contentType: 'image/png', fileExt: 'png' });
      }
      setCreativeDirty(false);
    } catch (err) {
      console.error('[appv3] matrix sync failed (will retry next action)', err);
    }
  }, [creativeDirty, displayFrames, textOverlays, shapeOverlays, logoOverlays]);

  // Flatten + REPLACE the saved creative's asset (one row per creative —
  // user-locked Jun 12). No-op while clean or before the auto-save has
  // produced a post id. Failures keep dirty=true so the next trigger
  // (download / schedule / back-to-edit) retries. Matrix results delegate
  // to syncMatrixRows (N rows instead of one).
  const syncSavedCreative = useCallback(async (force = false) => {
    if (resultIsMatrix) { await syncMatrixRows(force); return; }
    if (!force && !creativeDirty) return;
    const postId = savedPostIdRef.current;
    if (!postId) return;
    try {
      const resolved = await buildFinalBlob();
      if (!resolved) return;
      await replacePostImage({
        postId,
        blob: resolved.blob,
        contentType: resolved.contentType,
        fileExt: resolved.fileExt,
      });
      setCreativeDirty(false);
    } catch (err) {
      console.error('[appv3] creative sync failed (will retry on next action)', err);
    }
  }, [creativeDirty, buildFinalBlob, resultIsMatrix, syncMatrixRows]);

  // Prompt-edit the ACTIVE frame's base (1 credit). Text overlays are
  // not touched — they ride on top of whatever base comes back.
  const handlePromptEdit = useCallback(async () => {
    const text = editPrompt.trim();
    if (!text || promptEditing) return;
    const src = displayFrames[activeFrameIdx];
    if (!src) return;
    setPromptEditing(true);
    setEditError(null);
    try {
      const blob = await (await fetch(src)).blob();
      const res = await wizardEditImage({
        image: blob,
        prompt: text,
        aspectRatio: renderedAspect,
        compositeLogo: creativeDirection.logo === 'On',
      });
      setDisplayFrames((prev) => prev.map((f, i) => (
        i === activeFrameIdx ? `data:image/png;base64,${res.image_b64}` : f
      )));
      setCreativeDirty(true);
      setEditPrompt('');
      refreshCredits();
    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        setEditError(err.message || 'Not enough credits for this edit.');
      } else {
        setEditError(err instanceof Error ? err.message : 'Edit failed.');
      }
    } finally {
      setPromptEditing(false);
    }
  }, [
    editPrompt, promptEditing, displayFrames, activeFrameIdx,
    renderedAspect, creativeDirection.logo, refreshCredits,
  ]);

  // Save to My Creatives — idempotent within one result (savedPostIdRef).
  // Returns the post id so Schedule/Share can chain off the same save.
  const handleSaveToCreatives = useCallback(async (): Promise<string | null> => {
    if (savedPostIdRef.current) return savedPostIdRef.current;
    if (saveState === 'saving') return null;
    try {
      setSaveState('saving');
      const resolved = await buildFinalBlob();
      if (!resolved) { setSaveState('idle'); return null; }
      const postId = await savePost({
        blob: resolved.blob,
        templateId: selectedAiTemplateId ?? selectedTemplateId ?? null,
        aspectRatio: renderedAspect,
        headline: aiResult?.concept?.headline ?? null,
        tagline: aiResult?.concept?.subhead ?? null,
        title: productName.trim() || 'AI creative',
        contentType: resolved.contentType,
        fileExt: resolved.fileExt,
        productId: selectedSavedProductId,
        // Text-overlay tile (Product Shot, text ON): persist the text-free
        // scene + editor doc so the tile opens in the /creatives editor.
        // overlayOutRef = synth path; aiResult = ai-generate path.
        baseImageB64: overlayOutRef.current?.base_image ?? aiResult?.base_image ?? null,
        textOverlay: overlayOutRef.current?.text_overlay ?? aiResult?.text_overlay ?? null,
      });
      savedPostIdRef.current = postId;
      setSaveState('saved');
      // The save just captured the current composite — clean by definition.
      setCreativeDirty(false);
      return postId;
    } catch (err) {
      console.error('[appv3] save to My Creatives failed', err);
      setSaveState('error');
      return null;
    }
  }, [
    saveState, buildFinalBlob, selectedAiTemplateId, selectedTemplateId,
    renderedAspect, aiResult, productName, selectedSavedProductId,
  ]);

  // (The old Edit → /creatives/:id button is gone — the render panel IS
  // the edit screen now, per user direction Jun 12. /creatives/:id stays
  // the edit surface for creatives opened from My Creatives.)

  // Schedule — save first (idempotent), flatten-sync any pending edits so
  // the scheduled asset is the FINAL composite, then open the SAME
  // ScheduleModal My Creatives + /creatives/:id use, with the saved post
  // pre-selected (modal auto-selects when posts.length === 1). Falls back
  // to the Ad Calendar page only if the post fetch fails for some reason.
  const handleScheduleShare = useCallback(async () => {
    // Matrix — ensure all N rows are saved + synced, then schedule the
    // ACTIVE frame's row (each image schedules independently; the others
    // stay in My Creatives to schedule from there).
    if (resultIsMatrix) {
      await saveMatrixRows();
      await syncMatrixRows();
      const postId = savedMatrixIdsRef.current[activeFrameIdx];
      if (!postId) { navigate('/schedule'); return; }
      try {
        const post = await getPost(postId);
        setSchedulePost(post);
      } catch (err) {
        console.error('[appv3] could not load matrix post for scheduling', err);
        navigate('/schedule');
      }
      return;
    }
    const postId = await handleSaveToCreatives();
    if (!postId) return;
    await syncSavedCreative();
    try {
      const post = await getPost(postId);
      setSchedulePost(post);
    } catch (err) {
      console.error('[appv3] could not load saved post for scheduling', err);
      navigate('/schedule');
    }
  }, [
    resultIsMatrix, saveMatrixRows, syncMatrixRows, activeFrameIdx,
    handleSaveToCreatives, syncSavedCreative, navigate,
  ]);

  // MANUAL SAVE (Jun 12 v5) — explicit "Save" button on the image. Auto-save
  // already creates the row(s), but the user wanted a visible affordance to
  // flush overlay/prompt edits on demand ("just in case"). Force-flushes the
  // current composite + flashes the save status.
  const handleManualSave = useCallback(async () => {
    if (saveState === 'saving') return;
    if (resultIsMatrix) {
      if (!savedMatrixIdsRef.current.some(Boolean)) {
        await saveMatrixRows();
      } else {
        setSaveState('saving');
        await syncMatrixRows(true);
        setSaveState('saved');
      }
    } else if (!savedPostIdRef.current) {
      await handleSaveToCreatives();
    } else {
      setSaveState('saving');
      await syncSavedCreative(true);
      setSaveState('saved');
    }
  }, [
    saveState, resultIsMatrix, saveMatrixRows, syncMatrixRows,
    handleSaveToCreatives, syncSavedCreative,
  ]);

  // AUTO-SAVE (Jun 12) — every successful render lands in My Creatives
  // without the user clicking Save. Fires once per result: saveState is
  // reset to 'idle' on each new generation, flips to 'saving'/'saved'
  // here, and savedPostIdRef keeps Edit/Schedule/manual-Save idempotent
  // on the same row. A failed save flips to 'error' (no retry loop) —
  // the action row's "Retry save" button covers that case.
  useEffect(() => {
    if (!hasResult || generating) return;
    if (saveState !== 'idle') return;
    // Matrix → N separate rows; single/carousel → one row.
    if (resultIsMatrix) void saveMatrixRows();
    else void handleSaveToCreatives();
  }, [hasResult, generating, saveState, resultIsMatrix, saveMatrixRows, handleSaveToCreatives]);

  // ── In-wizard overlay editor (Jun 30) ──────────────────────────────────
  // Once a TEXT-OVERLAY tile has auto-saved (saveState='saved' + we have the
  // base scene + editor doc + post id), build a minimal Post and hand it to the
  // SAME OverlayEditor used at /creatives/:id. The user edits the rendered tile
  // right here — no save→navigate→edit hop. base_image_url is an in-memory data
  // URL (the text-free scene); saveOverlay() updates the already-saved post by
  // id. Single tiles only (matrix/carousel keep the canvas editor). Memo deps
  // are reactive state; overlayOutRef/savedPostIdRef are populated by the time
  // saveState flips to 'saved'.
  const overlayPost = useMemo<Post | null>(() => {
    if (saveState !== 'saved' || resultIsMatrix) return null;
    const o = overlayOutRef.current;
    const id = savedPostIdRef.current;
    if (!o?.base_image || !o?.text_overlay || !id) return null;
    const composite = displayFrames[activeFrameIdx] ?? displayFrames[0] ?? '';
    // OverlayEditor reads only id / image_url / base_image_url / text_overlay.
    return {
      id,
      image_url: composite,
      base_image_url: `data:image/png;base64,${o.base_image}`,
      text_overlay: o.text_overlay,
    } as Post;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState, resultIsMatrix, displayFrames, activeFrameIdx]);

  // OverlayEditor saved → the server post (same id) now holds the re-baked
  // composite. Don't churn displayFrames here (that would recompute overlayPost
  // → remount the editor mid-session); the persisted tile is already correct.
  const handleOverlaySaved = useCallback((_updated: Post) => {
    setCreativeDirty(false);
  }, []);

  // Product Shot bakes text into the render (Model C flat image). Once auto-saved,
  // hand the flat post to the SHARED CreativeEditor (the "v4" full editor) so the
  // user edits it in-wizard exactly like /creatives/:id — the baked pixels stay,
  // draggable overlays go on top. Scoped to Product Shot single renders (matrix /
  // carousel / Model-B overlay tiles keep the canvas editor). Seeds from
  // displayFrames so there's no reload spinner. deps are reactive; savedPostIdRef
  // is populated by the time saveState flips to 'saved'.
  const flatEditorPost = useMemo<Post | null>(() => {
    if (!EMBED_CREATIVE_EDITOR_FOR_PRODUCT_SHOT) return null;
    // Jul 24 — Model Shot (fashion) finishes in the SAME shared V5 CreativeEditor
    // as Product Shot (was falling through to the old StudioFinishEditor).
    if (routeConfig.category !== 'product-shot' && routeConfig.category !== 'fashion') return null;
    if (saveState !== 'saved' || resultIsMatrix || overlayPost) return null;
    // Carousels are supported too now — CreativeEditor renders all frames via
    // initialFrames (below). After an in-editor edit, editorActiveId points at
    // the forked version.
    const id = editorActiveId ?? savedPostIdRef.current;
    const composite = displayFrames[activeFrameIdx] ?? displayFrames[0] ?? '';
    if (!id || !composite) return null;
    return {
      id,
      image_url: composite,
      aspect_ratio: renderedAspect,
      title: productName.trim() || 'AI creative',
      created_at: new Date().toISOString(),
    } as Post;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeConfig.category, saveState, resultIsMatrix, overlayPost, displayFrames, activeFrameIdx, renderedAspect, editorActiveId]);

  // Download — single → flattened PNG anchor; carousel → .zip of
  // flattened frames. Also kicks the replace-sync so My Creatives holds
  // the same final composite the user just downloaded.
  const handleDownloadResult = useCallback(async () => {
    if (downloadingResult) return;
    setDownloadingResult(true);
    try {
      const resolved = await buildFinalBlob();
      if (!resolved) return;
      const url = URL.createObjectURL(resolved.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resolved.fileExt === 'zip'
        ? (resultIsMatrix ? 'ai-ads.zip' : 'ai-ad-carousel.zip')
        : 'ai-ad.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      void syncSavedCreative();
    } catch (err) {
      console.error('[appv3] download failed', err);
    } finally {
      setDownloadingResult(false);
    }
  }, [downloadingResult, buildFinalBlob, syncSavedCreative, resultIsMatrix]);

  // ---------- Render ----------
  // Studio layout (the new "Image Studio" design) is enabled for held +
  // fashion. Product Shot keeps the legacy layout below until the design
  // is extended to that route too. Early-return keeps each layout
  // self-contained (no shared structural JSX) and avoids a 3000-line
  // mega-return that's hard to read.
  // V3 Carousel (Jun 14) — Step 1 TYPE PRE-STEP gate. When the user
  // lands on /appv3/carousel without a type selected yet, render ONLY
  // the type picker (full-width page, 5 big cards + Continue). After
  // Continue → selectedCarouselType is set + the page re-renders into
  // the same Studio UI used by product-shot below.
  if (routeConfig.category === 'carousel' && !selectedCarouselType) {
    return (
      <CarouselTypePreStep
        onSelect={(id) => setSelectedCarouselType(id)}
        onDashboard={() => navigate('/create')}
      />
    );
  }

  // V3 Social Post (Jun 15) — Step 1 INTENT PRE-STEP gate. Mirrors the
  // Carousel type pre-step above: until the user picks one of 6 intents
  // we render ONLY the intent picker. After pick → selectedIntent set,
  // page re-renders into the Studio UI with the intent surfaced as a
  // summary bar and threaded into wizardAiGenerate as the `intent`
  // form field (routes through INTENT_SCAFFOLDS in ad_brief.py).
  if (routeConfig.category === 'social-post' && !selectedIntent) {
    return (
      <SocialPostIntentPreStep
        onSelect={(id) => setSelectedIntent(id)}
        onDashboard={() => navigate('/create')}
      />
    );
  }

  const isStudioRoute = routeConfig.category === 'product-swap'
    || routeConfig.category === 'fashion'
    || routeConfig.category === 'product-shot'
    // V3 Carousel — once the type pre-step is past, carousel uses the
    // exact same Studio UI as Product Shot (Templates/Create + Product
    // picker + Generate + StudioFinishEditor). Format is locked to
    // 'carousel' via the useEffect above + the Single/Carousel footer
    // toggle is hidden when category='carousel'.
    || routeConfig.category === 'carousel'
    // V3 Social Post (Jun 15) — once the intent pre-step is past,
    // social-post uses the SAME Studio UI but with the Templates/
    // Create tab pill hidden (no templates in v1; intent IS the
    // creative direction). The hide happens inside FashionStudioLayout
    // at the StudioCreateTemplatesPill render site, conditioned on
    // category. designMode is also force-locked to 'ai' in the route-
    // reset effect above.
    || routeConfig.category === 'social-post';
  if (isStudioRoute) {
    return (
      <>
      <StudioLayoutRender
        routeConfig={routeConfig}
        navigate={navigate}
        // Design mode. Jun 12: switching tabs / picking a different
        // template no longer clears the result — the render (and its
        // Save/Schedule/Download actions) stays up until "Back to
        // design" or "Try again"; left-panel edits accumulate on the
        // edits-behind badge instead.
        designMode={designMode}
        onDesignModeChange={handleDesignModeChange}
        // CD + filters
        creativeDirection={creativeDirection}
        onCreativeDirectionChange={setCreativeDirection}
        // Aspect + Quality + Format
        aspectRatio={aspectRatio}
        onAspectRatioChange={setAspectRatio}
        allowedAspects={allowedAspectsNow}
        quality={quality}
        onQualityChange={setQuality}
        isFree={qualityRestricted}
        format={format}
        onFormatChange={setFormat}
        // Fashion model picker
        selectedFashionModelId={selectedFashionModelId}
        onFashionModelSelect={setSelectedFashionModelId}
        // Product Shot Create hero — Patterns (product-only) XOR Poses (held).
        // Mutual-exclusion handlers (Jun 12 v6 merge).
        selectedPattern={selectedPattern}
        onPatternSelect={handlePatternPick}
        onPoseSelect={handlePosePick}
        // AI templates (Jun 11) — Product Shot Templates tab uses
        // pre-rendered AI samples instead of the Recreate library.
        selectedAiTemplateIds={selectedAiTemplateIds}
        lockedTemplateId={lockedTemplateId}
        onClearLockedTemplate={() => {
          setLockedTemplateId(null);
          setSearchParams((p) => { p.delete('aiTemplate'); return p; }, { replace: true });
        }}
        onAiTemplateSelect={handleAiTemplateToggle}
        // Recipes-as-Templates (Jun 18 — Phase 3, Product Shot only).
        recipeTemplates={recipeTemplates}
        recipesLoading={recipesLoading}
        selectedRecipeId={selectedRecipeId}
        lockedRecipeId={lockedRecipeId}
        onClearLockedRecipe={() => {
          setLockedRecipeId(null);
          setSearchParams((p) => { p.delete('recipe'); return p; }, { replace: true });
        }}
        recipeVibeFilter={recipeVibeFilter}
        onRecipeVibeFilterChange={setRecipeVibeFilter}
        onRecipeSelect={handleRecipeSelect}
        recipeProductTypes={recipeProductTypes}
        // Inline product picker (Jun 11) — replaces the SelectProductModal
        // entry point on Studio routes.
        productPickerOpen={productPickerOpen}
        onProductPickerContinue={handleProductPickerContinue}
        onProductEdit={handleProductEdit}
        onStepUploadCallback={handleStepUploadCallback}
        onScrapeSuccess={handleScrapeSuccess}
        // Template picker (Templates pill)
        selectedTemplateId={selectedTemplateId}
        onTemplateSelect={(id) => {
          setSelectedTemplateId(id);
          const tmpl = routeTemplates.find((t) => t.id === id);
          if (tmpl) {
            const a = platformIdToAspect(tmpl.platformId);
            if (routeConfig.allowedAspects.includes(a)) setAspectRatio(a);
          }
        }}
        routeTemplates={routeTemplates}
        // Product
        productName={productName}
        productImageUrl={productImageUrl}
        productFile={productFile}
        productDomain={productDomain}
        fashionProductMismatch={fashionProductMismatch}
        onOpenProductModal={() => setProductModalOpen(true)}
        onClearProduct={() => {
          setProductFile(null);
          setProductImageUrl(null);
          setProductName('');
          setProductDescription('');
          setProductCategory(null);
          setProductTags([]);
          setSelectedSavedProductId(null);
          clearResult();
        }}
        // Result
        generating={generating}
        error={error}
        aiResult={aiResult}
        generatedImage={generatedImage}
        frameCount={frameCount}
        onBackToEdit={clearResult}
        onRetry={handleGenerate}
        // Generate
        canGenerate={canGenerate}
        creditCost={creditCost}
        insufficient={insufficient}
        balance={balance}
        onGenerate={handleGenerate}
        onUpgrade={() => navigate('/checkout')}
        // Matrix (Jun 12 v3) — N templates × 1 product → N images
        matrixImageCount={matrixImageCount}
        matrixActive={matrixActive}
        // Result actions (Jun 12 — Studio footer post-result row)
        onRegenerateWithQuality={handleGenerate}
        onSaveToCreatives={handleSaveToCreatives}
        saveState={saveState}
        onScheduleShare={handleScheduleShare}
        onDownloadResult={handleDownloadResult}
        downloadingResult={downloadingResult}
        // Finishing surface (Jun 12 v2 — render panel = edit screen)
        // Jun 30 — overlay tiles edit in the SAME /creatives OverlayEditor.
        overlayPost={overlayPost}
        onOverlaySaved={handleOverlaySaved}
        flatEditorPost={flatEditorPost}
        editorForked={editorActiveId != null}
        onEditorFork={(pid) => { savedPostIdRef.current = pid; setEditorActiveId(pid); }}
        onRequestAiText={handleRequestAiText}
        productShotFinish={EMBED_CREATIVE_EDITOR_FOR_PRODUCT_SHOT
          && (routeConfig.category === 'product-shot' || routeConfig.category === 'fashion')}
        displayFrames={displayFrames}
        renderedAspect={renderedAspect}
        textOverlays={textOverlays}
        onTextOverlaysChange={handleTextOverlaysChange}
        logoOverlays={logoOverlays}
        onLogoOverlaysChange={handleLogoOverlaysChange}
        brandLogoUrl={brandLogoUrl}
        brandColors={brandColors}
        suggestedFrames={suggestedFrames}
        onActiveFrameChange={setActiveFrameIdx}
        activeFrameIdx={activeFrameIdx}
        editPrompt={editPrompt}
        onEditPromptChange={setEditPrompt}
        promptEditing={promptEditing}
        editError={editError}
        onPromptEdit={handlePromptEdit}
        onSyncCreative={() => { void syncSavedCreative(); }}
        // Manual save (Jun 12 v5) — explicit Save button on the image
        onManualSave={() => { void handleManualSave(); }}
        creativeDirty={creativeDirty}
        // Edits-behind badge
        editsSinceGenerate={editsSinceGenerate}
        // Product picker modal — same modal as legacy layout. studioSavedProducts
        // = savedProducts, except on Fashion where it's filtered to wearables.
        productModalOpen={productModalOpen}
        onCloseProductModal={() => setProductModalOpen(false)}
        savedProducts={studioSavedProducts}
        selectedSavedProductId={selectedSavedProductId}
        onPickSavedProduct={handleSavedProductPick}
        onUploadFile={handleFileUpload}
        // V3 Carousel — type rides through so FashionStudioLayout can
        // render the summary bar + offer "Change type" affordance.
        // Setting back to null re-shows the Step 1 type pre-step.
        selectedCarouselType={selectedCarouselType}
        onEditCarouselType={() => setSelectedCarouselType(null)}
        // V3 Social Post (Jun 15) — intent rides through so the Studio
        // layout can render the summary bar + "Change intent" affordance.
        // Setting back to null re-shows the SocialPostIntentPreStep.
        selectedIntent={selectedIntent}
        onEditIntent={() => setSelectedIntent(null)}
        // V3 Social Post (Jun 15 v2) — master Filters toggle + Shot
        // type controls. Hide-rendered on non-social-post routes (the
        // layout's conditional render gates them by category).
        filtersEnabled={filtersEnabled}
        onFiltersEnabledChange={setFiltersEnabled}
        socialPostShot={socialPostShot}
        onSocialPostShotChange={setSocialPostShot}
      />

      {/* Schedule modal (Jun 12) — the SAME component My Creatives and
          /creatives/:id use. Fresh-schedule mode (editingTiles=null) with
          the just-saved post pre-selected (modal auto-selects when
          posts.length === 1). */}
      {schedulePost && (
        <ScheduleModal
          editingTiles={null}
          createDay={new Date()}
          posts={[schedulePost]}
          fbStatus={fbStatus}
          onClose={() => setSchedulePost(null)}
          onSaved={() => {
            setSchedulePost(null);
            setToast('Scheduled — see it on the Ad Calendar');
          }}
        />
      )}

      {/* Bottom-center toast — mirrors the gallery/detail treatment. */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
            <i className="ri-checkbox-circle-line text-base" />
            {toast}
          </div>
        </div>
      )}
      </>
    );
  }

  // ---------- Legacy layout (product-shot only) ----------
  return (
    <AppLayoutV2>
      <div className="w-full max-w-[1500px] mx-auto px-6 py-8">
        {/* Page header — title + subtitle driven by routeConfig so the
            same component renders cleanly for product-shot / product-swap /
            fashion. */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-dark">{routeConfig.title}</h1>
            <p className="text-sm text-dark-muted mt-0.5">{routeConfig.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/create')}
            className="text-xs font-semibold text-dark-muted hover:text-primary transition-colors"
          >
            ← Dashboard
          </button>
        </div>

        {/* Single-screen layout — 50/50 split. Columns size to content +
            items-stretch keeps them bottom-aligned. No fixed viewport
            height cap, so panels are only as tall as they need to be —
            removes the dead space at the bottom of the filter list. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
          {/* ================= LEFT COLUMN =================
              Jazzed-up panel: soft gradient backdrop pulling the brand
              pink/lavender accent in from corners + ring (not border) so
              the edge reads as glass-like rather than a flat outline.
              The shadow is primary-tinted so the panel feels "lit" by
              the brand color instead of a generic gray box. */}
          <div className="order-2 xl:order-1 relative rounded-2xl bg-gradient-to-br from-white via-primary/[0.025] to-accent-light/40 ring-1 ring-surface-dark/30 shadow-xl shadow-primary/5 flex flex-col overflow-hidden">
            {/* Decorative top accent — a thin gradient strip across the
                top edge so the panel has a "branded" header bar even
                without explicit zone titles. */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-accent to-primary/50 opacity-90" />

            {/* Pill tabs at top */}
            <div className="flex-shrink-0 p-4 border-b border-surface-dark/20">
              <DesignTabs
                designMode={designMode}
                onChange={(m) => { handleDesignModeChange(m); clearResult(); }}
              />
            </div>

            {/* Filter body — natural-height. The appv3-filters CSS class
                neutralizes CreativeDirectionFilters' internal zone headers
                + Surprise me button so the rows lay out uniformly + hides
                the inline thumb labels (rendered below cards via ::after). */}
            <div className="p-5 appv3-filters">
              {designMode === 'ai' ? (
                /* Uniform vertical rhythm: a single 20px gap between
                   every row in the column. Earlier we layered space-y-5
                   + divide-y + pt-5 which stacked to ~40px between
                   FilterZones / Aspect / Quality while inner FilterZone
                   rows stayed at ~10px — visibly jarring. Scoped CSS in
                   index.css also widens FilterZone's inner `space-y-2.5`
                   to 20px so inner rows match this outer rhythm. */
                <div className="space-y-5">
                  {/* Format row — Single vs Carousel. Rendered at the top
                      of the filter list so the user picks output shape
                      before fine-tuning the other knobs. Carousel auto-
                      picks 4 framings from routeConfig.carouselFramings —
                      the user's Framing CD selection is ignored in
                      carousel mode (no point picking one framing when
                      we render 4 distinct angles). */}
                  <FormatRow
                    value={format}
                    onChange={setFormat}
                    carouselFramings={routeConfig.carouselFramings}
                  />
                  {/* Fashion-only: model picker. Rendered ABOVE the CD
                      filters because choosing "who's in the photo" is
                      conceptually upstream of mood/light/framing. */}
                  {routeConfig.category === 'fashion' && (
                    <FashionModelPicker
                      selectedId={selectedFashionModelId}
                      onSelect={setSelectedFashionModelId}
                    />
                  )}
                  <CreativeDirectionFilters
                    creativeDirection={creativeDirection}
                    onCreativeDirectionChange={setCreativeDirection}
                    showTextDensity={routeConfig.showTextDensity}
                    framingValues={routeConfig.framings}
                    withIcons
                  />
                  {/* Aspect + Quality rendered as matching pill rows so
                      they read as part of the same filter list. */}
                  <AspectRow
                    value={aspectRatio}
                    onChange={setAspectRatio}
                    allowed={allowedAspectsNow}
                  />
                  <QualityRow value={quality} onChange={setQuality} isFree={qualityRestricted} />
                </div>
              ) : (
                <TemplateGrid
                  templates={routeTemplates}
                  routeTitle={routeConfig.title}
                  selectedId={selectedTemplateId}
                  onSelect={(id) => {
                    setSelectedTemplateId(id);
                    // Each Recreate template is aspect-locked via its
                    // platformId — auto-sync the page aspect so the
                    // preview pane shows the right shape. User can
                    // still override via AspectRow afterwards. Honors
                    // the route's allowedAspects (skip if the template's
                    // aspect isn't supported for this route — defensive).
                    const tmpl = routeTemplates.find((t) => t.id === id);
                    if (tmpl) {
                      const a = platformIdToAspect(tmpl.platformId);
                      if (routeConfig.allowedAspects.includes(a)) setAspectRatio(a);
                    }
                    clearResult();
                  }}
                />
              )}
            </div>
          </div>

          {/* ================= RIGHT COLUMN ================= */}
          {/* Natural height; items-stretch on the grid keeps it matched
              to the LEFT column's content height. Preview canvas absorbs
              the extra vertical space via flex-1.
              Mobile (single column): Select Product comes FIRST (order-1)
              so the user picks a product before the locked template grid;
              xl restores design-left / product-right (order-2). */}
          <div className="order-1 xl:order-2 flex flex-col gap-4">
            {/* Select Product button */}
            <div className="flex-shrink-0">
              <SelectProductButton
                selectedName={productName}
                selectedImageUrl={productImageUrl}
                onOpen={() => setProductModalOpen(true)}
                onClear={() => {
                  setProductFile(null);
                  setProductImageUrl(null);
                  setProductName('');
                  setProductDescription('');
                  setSelectedSavedProductId(null);
                  clearResult();
                }}
              />
            </div>

            {/* Preview canvas — fills remaining vertical space */}
            <div className="flex-1 min-h-0">
              <PreviewCanvas
                aspectRatio={aspectRatio}
                productImageUrl={productImageUrl}
                generating={generating}
                error={error}
                aiResult={aiResult}
                generatedImage={generatedImage}
                frameCount={frameCount}
                onBackToEdit={clearResult}
                onRetry={handleGenerate}
              />
            </div>

            {/* Generate CTA */}
            <div className="flex-shrink-0">
              <GenerateCta
                canGenerate={canGenerate}
                generating={generating}
                insufficient={insufficient}
                balance={balance}
                creditCost={creditCost}
                productFile={productFile}
                designMode={designMode}
                selectedTemplateId={selectedTemplateId}
                selectedAiTemplateId={selectedAiTemplateId}
                routeTitle={routeConfig.title}
                onGenerate={handleGenerate}
                onUpgrade={() => navigate('/checkout')}
              />
            </div>
          </div>
        </div>
      </div>
      <SelectProductModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        savedProducts={savedProducts}
        selectedSavedProductId={selectedSavedProductId}
        onPickSaved={handleSavedProductPick}
        onUploadFile={handleFileUpload}
      />
    </AppLayoutV2>
  );
}

// =============================================================================
// DesignTabs — top-of-left-column pill tabs (AI Magic / Choose Template)
// =============================================================================

interface DesignTabsProps {
  designMode: DesignMode;
  onChange: (mode: DesignMode) => void;
}

function DesignTabs({ designMode, onChange }: DesignTabsProps) {
  // Templates first (leftmost) — it's the default tab.
  return (
    <div className="grid grid-cols-2 gap-1.5 p-1 rounded-xl bg-surface/60">
      <button
        type="button"
        onClick={() => onChange('template')}
        className={`
          inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer
          ${designMode === 'template'
            ? 'bg-primary text-white shadow-md shadow-primary/25'
            : 'bg-transparent text-dark-muted hover:text-dark'}
        `}
      >
        <i className="ri-palette-line" />
        Choose Template
      </button>
      <button
        type="button"
        onClick={() => onChange('ai')}
        className={`
          inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer
          ${designMode === 'ai'
            ? 'bg-primary text-white shadow-md shadow-primary/25'
            : 'bg-transparent text-dark-muted hover:text-dark'}
        `}
      >
        <i className="ri-sparkling-2-fill" />
        AI Magic
      </button>
    </div>
  );
}

// =============================================================================
// SelectProductButton — top-of-right-column button that opens the picker modal.
// At rest shows "Select Product"; once picked shows thumb + name + "Change".
// =============================================================================

interface SelectProductButtonProps {
  selectedName: string;
  selectedImageUrl: string | null;
  onOpen: () => void;
  onClear: () => void;
}

function SelectProductButton({
  selectedName, selectedImageUrl, onOpen, onClear,
}: SelectProductButtonProps) {
  if (!selectedImageUrl) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-2xl bg-dark hover:bg-dark/90 text-white text-sm font-bold transition-all cursor-pointer shadow-md"
      >
        <i className="ri-add-line text-base" />
        Select Product
      </button>
    );
  }
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-surface-dark/30 shadow-sm">
      <img
        src={selectedImageUrl}
        alt={selectedName || 'product'}
        className="w-12 h-12 rounded-xl object-cover border border-surface-dark/30 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-0.5">Product</p>
        <p className="text-sm font-bold text-dark truncate">{selectedName || 'Untitled product'}</p>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-surface hover:bg-surface-dark/30 text-dark text-xs font-bold transition-colors cursor-pointer"
      >
        Change
      </button>
      <button
        type="button"
        onClick={onClear}
        title="Clear"
        className="flex-shrink-0 w-7 h-7 rounded-full text-dark-muted hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center justify-center"
      >
        <i className="ri-close-line" />
      </button>
    </div>
  );
}

// =============================================================================
// SelectProductModal — opens from the Select Product button. Search + library
// + URL paste. Picking closes the modal and propagates the product upward.
// =============================================================================

interface SelectProductModalProps {
  open: boolean;
  onClose: () => void;
  savedProducts: UserProduct[] | null;
  selectedSavedProductId: string | null;
  onPickSaved: (product: UserProduct) => void;
  onUploadFile: (file: File) => void;
}

function SelectProductModal({
  open, onClose, savedProducts, selectedSavedProductId, onPickSaved, onUploadFile,
}: SelectProductModalProps) {
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const products = savedProducts || [];
  const filtered = search.trim()
    ? products.filter((p) =>
        (p.name || '').toLowerCase().includes(search.trim().toLowerCase())
        || (p.description || '').toLowerCase().includes(search.trim().toLowerCase())
      )
    : products;

  const handleFile = (file: File | null) => {
    if (!file) return;
    onUploadFile(file);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-surface-dark/20 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-dark text-base">Select a product</h3>
            <p className="text-xs text-dark-muted mt-0.5">
              Search your library, paste a URL, or upload a new product image.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full text-dark-muted hover:text-dark hover:bg-surface flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Search + Upload row */}
        <div className="px-6 py-4 border-b border-surface-dark/20 flex items-center gap-2">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm" />
            <input
              type="text"
              autoFocus
              placeholder="Search your products by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-full border border-dark-muted/40 text-sm focus:border-primary outline-none bg-white"
            />
          </div>
          <label className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer shadow-md shadow-primary/25 transition-all">
            <i className="ri-upload-2-line" />
            Upload
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {savedProducts === null ? (
            <p className="text-sm text-dark-muted text-center py-12">Loading your products…</p>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-shopping-bag-3-line text-3xl text-dark-muted/60" />
              <p className="text-sm font-semibold text-dark mt-3">No saved products yet</p>
              <p className="text-xs text-dark-muted mt-1">
                Upload a product image above or add one in My Products first.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-dark-muted text-center py-12">
              No products match “{search}”. Try a different search.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filtered.map((p) => {
                const active = p.id === selectedSavedProductId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { onPickSaved(p); onClose(); }}
                    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      active
                        ? 'border-primary shadow-md shadow-primary/20'
                        : 'border-surface-dark/30 hover:border-primary/40'
                    }`}
                    title={p.name || 'Product'}
                  >
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt={p.name || 'product'}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-surface" />
                    )}
                    {active && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
                        <i className="ri-check-line text-xs" />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] font-bold truncate">
                      {p.name || 'Untitled'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// RecipesSection — production recipe picker (Jun 18 — Phase 3).
// Sits ABOVE the AI template grid on /appv3/product-shot's Templates tab.
// Each tile is a baked preview thumbnail rendered against a curator-picked
// mock product → preview_url (in the public moodboard-previews bucket).
// Click → dispatches through /api/wizard/render-recipe (NOT the scene-place
// pipeline). Single-select; mutually exclusive with AI templates.
// =============================================================================
const STUDIO_RECIPE_VIBES = ['Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial'] as const;

function RecipesSection({
  recipes, selectedId, vibeFilter, onVibeFilterChange, onSelect, productTypes,
  showLegacyDivider = true, modelFilter, textOnly = false, showSynthesize = false,
}: {
  recipes: ProductionRecipe[];
  selectedId: string | null;
  vibeFilter: string | null;
  onVibeFilterChange: (v: string | null) => void;
  onSelect: (id: string) => void;
  /** Allowed product_type values for the picked product. null = no
   *  filter (no product uploaded yet or unknown category). When set,
   *  recipes must have at least one product_types[] tag in this list. */
  productTypes: string[] | null;
  /** Whether to render the "Legacy templates" divider below the grid.
   *  Product Shot shows recipes ABOVE the legacy scene grid (true).
   *  Fashion has no legacy grid — recipes are the whole tab (false). */
  showLegacyDivider?: boolean;
  /** Route-level human-model gate:
   *    'with'    → only recipes that need a human model (Fashion + Product
   *                Swap: the model-first / held-first specialized routes)
   *    'without' → only product-only recipes (no route uses this now; kept
   *                for completeness)
   *    undefined → no gate — show every recipe for the product's type,
   *                worn + product-only + held (Product Shot, Carousel,
   *                Social Post). Jul 13: model/no-model is a property of the
   *                RECIPE, not the route — a garment's product shot is
   *                naturally worn, so a pants product should see its
   *                model-worn recipes on Product Shot too. Recipes self-
   *                select by product type (skincare → bottles, pants → worn). */
  modelFilter?: 'with' | 'without';
  /** Social Post route — show ONLY recipes that render text (every social
   *  post carries a message; text-less product photos are excluded). */
  textOnly?: boolean;
  /** Show the "AI Magic" (Synthesize) card as the first tile — recombines a
   *  vibe's recipe attributes into a brand-new one. Product Shot test surface
   *  (Jun 19). Keeps the section visible even when no recipe matches yet. */
  showSynthesize?: boolean;
}) {
  // textOnly gate (Social Post) → model gate (route) → product-type filter
  // (picked product) → vibe filter (user override). Empty product_types[]
  // (untagged) is invisible to a typed filter.
  const textFiltered = textOnly
    ? recipes.filter((r) => r.supports_text)
    : recipes;
  const modelFiltered =
    modelFilter === 'with'
      ? textFiltered.filter((r) => r.requires_model)
      : modelFilter === 'without'
        ? textFiltered.filter((r) => !r.requires_model)
        : textFiltered;
  // Jul 13 — product-TYPE filtering is now SERVER-side (the recipes passed in
  // are already the product's family, via /api/templates/recipes?product_category).
  // `productTypes` is now just a DISPLAY hint: [category] when a product is
  // picked (drives the "filtered for X" label + the empty gate), null when
  // browsing. We only still filter by model gate (route) + vibe (user) here.
  const productTypeFiltered = modelFiltered;
  const filtered = vibeFilter
    ? productTypeFiltered.filter((r) => r.vibe === vibeFilter)
    : productTypeFiltered;
  // Skip rendering entirely when a product IS picked but zero recipes match
  // its family — nothing to show. (Vibe filter only triggers empty state if
  // the user actively picked an empty vibe.)
  if (productTypes && productTypeFiltered.length === 0 && !showSynthesize) {
    return null;
  }
  return (
    <section className="mb-6">
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <RecipeVibeChip label="All" active={vibeFilter === null} onClick={() => onVibeFilterChange(null)} />
        {STUDIO_RECIPE_VIBES.map((v) => (
          <RecipeVibeChip
            key={v}
            label={v}
            active={vibeFilter === v}
            onClick={() => onVibeFilterChange(v)}
          />
        ))}
      </div>
      {filtered.length === 0 && !showSynthesize ? (
        <p className="text-xs text-dark-muted py-4">No recipes for this vibe yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {/* AI Magic / Synthesize — first slot. Recombines a vibe's recipe
              attributes into a brand-new one (unique every time). Test card. */}
          {showSynthesize && (
            <button
              type="button"
              onClick={() => onSelect(SYNTHESIZE_RECIPE_ID)}
              title="AI Magic — a unique on-brand recipe synthesized every time"
              className="group cursor-pointer"
            >
              <div className={`
                relative aspect-square rounded-2xl overflow-hidden border-2
                transition-all duration-300 ease-out flex flex-col items-center
                justify-center text-center gap-1.5 px-3
                bg-gradient-to-br from-primary/15 via-accent/15 to-sky-200/30
                ${selectedId === SYNTHESIZE_RECIPE_ID
                  ? 'border-primary shadow-lg shadow-primary/30 -translate-y-1'
                  : 'border-transparent group-hover:border-primary/40 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-dark/20'}
              `}>
                <i className="ri-sparkling-2-line text-3xl text-primary" />
                <span className="text-sm font-bold text-dark">AI Magic</span>
                <span className="text-[10px] text-dark-muted leading-tight">
                  A unique recipe every time
                </span>
                {selectedId === SYNTHESIZE_RECIPE_ID && (
                  <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shadow">✓</div>
                )}
              </div>
            </button>
          )}
          {filtered.map((r) => {
            const active = selectedId === `${r.vibe_slug}/${r.id}`;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => onSelect(`${r.vibe_slug}/${r.id}`)}
                title={`${r.vibe}${r.product_types.length ? ` · ${r.product_types.join(', ')}` : ''}`}
                className="group cursor-pointer"
              >
                <div className={`
                  relative aspect-square rounded-2xl overflow-hidden bg-surface
                  border-2 transition-all duration-300 ease-out
                  ${active
                    ? 'border-primary shadow-lg shadow-primary/30 -translate-y-1'
                    : 'border-transparent group-hover:border-primary/40 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-dark/20'}
                `}>
                  <ThumbImage
                    src={r.preview_url}
                    alt={r.vibe}
                    className="transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                    loading="lazy"
                    draggable={false}
                  />
                  {r.preview_stale && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wide">
                      Stale
                    </div>
                  )}
                  {active && (
                    <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shadow">✓</div>
                  )}
                  <div className={`
                    absolute inset-x-0 bottom-0 pt-8 pb-2 px-2.5
                    bg-gradient-to-t from-black/70 via-black/30 to-transparent
                    transition-opacity duration-300
                    ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                  `}>
                    <p className="text-[11px] font-bold text-white text-left leading-tight">
                      {r.vibe}
                    </p>
                    {r.product_types.length > 0 && (
                      <p className="text-[10px] text-white/80 text-left mt-0.5">
                        {r.product_types.slice(0, 2).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {/* Subtle divider before the legacy template grid below. Shown only
          when a legacy grid follows (Product Shot). Fashion = recipes-only,
          no divider. */}
      {showLegacyDivider && (
        <div className="mt-6 pt-4 border-t border-surface-dark/40">
          <p className="text-[11px] font-bold uppercase tracking-wide text-dark-muted">
            Legacy templates
          </p>
        </div>
      )}
    </section>
  );
}

function RecipeVibeChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer ${
        active
          ? 'bg-primary text-white'
          : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark'
      }`}
    >{label}</button>
  );
}

// =============================================================================
// AiTemplateGrid — Pomelli-style AI template library (Jun 11).
// Used by the Templates tab on /appv3/product-shot AND /appv3/product-swap
// (In Hand) — the caller passes the route's subset via `templates`
// (aiTemplatesForRoute). Each tile is a pre-rendered sample (Lumière
// placeholder product) showing what the template's prompt produces.
// Click → backend looks up the prompt and re-renders with the user's
// actual product as input.
// =============================================================================
function AiTemplateGrid({ templates, selectedIds, onSelect, recommendedDomain }: {
  templates: readonly AiTemplate[];
  /** Multi-select (Jun 12 v3) — ordered ids. Each picked real template
   *  shows its 1-based position badge; the matrix = products × these. */
  selectedIds: string[];
  onSelect: (id: string) => void;
  /** When set, templates tagged with this product domain surface first
   *  under a "Recommended for <domain>" header and the rest collapse
   *  behind a "View all" expander. Null/undefined OR zero matches →
   *  flat ungrouped grid (legacy behavior). */
  recommendedDomain?: AiTemplateDomain | null;
}) {
  const recommended = recommendedDomain
    ? templates.filter((t) => t.domain === recommendedDomain)
    : [];
  const grouped = recommended.length > 0;
  const others = grouped
    ? templates.filter((t) => t.domain !== recommendedDomain)
    : [...templates];

  // 1-based badge position for a picked real template (null = not picked).
  const badgeOf = (id: string): number | null => {
    const i = selectedIds.indexOf(id);
    return i >= 0 ? i + 1 : null;
  };
  const freestyleActive = selectedIds.includes(AI_FREESTYLE_TEMPLATE_ID);
  const realCount = selectedIds.filter((x) => x !== AI_FREESTYLE_TEMPLATE_ID).length;

  // "View all" starts collapsed; re-collapses when the domain changes
  // (new product picked). Auto-expands if ANY selected template lives in
  // the collapsed set so the user never loses sight of a pick.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setShowAll(false); }, [recommendedDomain]);
  const selectionHidden = grouped && !showAll
    && others.some((t) => selectedIds.includes(t.id));
  useEffect(() => {
    if (selectionHidden) setShowAll(true);
  }, [selectionHidden]);

  // Header hint communicates the multi-select model up front.
  const headerHint = realCount > 0
    ? `${realCount} selected — one image per template`
    : 'Tap one or more — each makes its own image';

  if (!grouped) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold uppercase tracking-wide text-dark flex items-center gap-1.5">
            <i className="ri-sparkling-2-line text-primary text-base" />
            Pick template(s)
          </p>
          <span className="text-xs text-dark-muted font-semibold flex-shrink-0">
            {realCount > 0 ? `${realCount} selected` : `${templates.length} samples`}
          </span>
        </div>
        <p className="text-xs text-dark-muted -mt-1.5">{headerHint}</p>
        <div className="flex flex-wrap gap-3">
          <AiFreestyleTile active={freestyleActive} onSelect={onSelect} />
          {templates.map((t) => (
            <AiTemplateTile
              key={t.id}
              template={t}
              badgeNumber={badgeOf(t.id)}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Recommended group — domain matched from the picked product's
          classification metadata. The AI Magic freestyle tile always
          leads (it's universal — no template, designed from the product). */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-bold uppercase tracking-wide text-dark flex items-center gap-1.5">
          <i className="ri-sparkling-2-fill text-primary text-base" />
          Recommended for {AI_TEMPLATE_DOMAIN_LABELS[recommendedDomain!]}
        </p>
        <span className="text-xs text-dark-muted font-semibold flex-shrink-0">
          {realCount > 0 ? `${realCount} selected` : `${recommended.length} matches`}
        </span>
      </div>
      <p className="text-xs text-dark-muted -mt-1.5">{headerHint}</p>
      <div className="flex flex-wrap gap-3">
        <AiFreestyleTile active={freestyleActive} onSelect={onSelect} />
        {recommended.map((t) => (
          <AiTemplateTile
            key={t.id}
            template={t}
            badgeNumber={badgeOf(t.id)}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* View-all expander for the rest of the library. */}
      {others.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-dashed border-surface-dark/50 bg-white/70 hover:border-primary/50 hover:text-primary text-dark-muted text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
          >
            <i className={showAll ? 'ri-arrow-up-s-line text-base' : 'ri-arrow-down-s-line text-base'} />
            {showAll ? 'Hide other templates' : `View all templates (${others.length} more)`}
          </button>
          {showAll && (
            <div className="flex flex-wrap gap-3 mt-3">
              {others.map((t) => (
                <AiTemplateTile
                  key={t.id}
                  template={t}
                  badgeNumber={badgeOf(t.id)}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// AiFreestyleTile (Jun 12) — the always-first "AI Magic" tile. Not a real
// template: selecting it generates freestyle from the product's metadata
// (concept LLM writes the scene; no template prompt, no pattern). Pure
// CSS graphic (gradient wash + sparkle badge) so there's no sample asset
// to author or keep in sync.
function AiFreestyleTile({ active, onSelect }: {
  active: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(AI_FREESTYLE_TEMPLATE_ID)}
      title="AI Magic — no template, designed from your product"
      className="group cursor-pointer"
    >
      <div className={`
        relative w-[215px] h-[215px] rounded-2xl overflow-hidden
        border-2 transition-all duration-300 ease-out
        ${active
          ? 'border-primary shadow-lg shadow-primary/30 -translate-y-1'
          : 'border-transparent group-hover:border-primary/40 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-dark/20'}
      `}>
        {/* Gradient wash — same pink→indigo→sky family as the progress
            ring so "AI is doing this" reads instantly. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(244,114,182,0.22), rgba(99,102,241,0.18), rgba(56,189,248,0.22))',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-white/20" />
        {/* Center cluster */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-5 text-center">
          <span className="w-14 h-14 rounded-2xl bg-white/85 backdrop-blur-sm shadow-md flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <i className="ri-sparkling-2-fill text-3xl text-primary" />
          </span>
          <p className="text-sm font-bold text-dark leading-tight">AI Magic</p>
          <p className="text-[11px] text-dark-muted leading-snug">
            No template — AI designs the scene from your product
          </p>
        </div>
        {active && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
            <i className="ri-check-line text-sm" />
          </div>
        )}
      </div>
    </button>
  );
}

// Single 215px sample tile (samples ship at 1024×1024 — comfortably
// above retina density at 215 CSS px). Names are deliberately NOT
// rendered statically (the library will grow); they appear on hover
// over a bottom gradient scrim instead. Hover = slow image zoom +
// tile lift + deeper shadow for a premium gallery feel.
function AiTemplateTile({ template: t, badgeNumber, onSelect }: {
  template: AiTemplate;
  /** 1-based position in the multi-select (null = not picked). When
   *  picked, the corner badge shows this number so the matrix order is
   *  visible at a glance. */
  badgeNumber: number | null;
  onSelect: (id: string) => void;
}) {
  const active = badgeNumber !== null;
  return (
    <button
      type="button"
      onClick={() => onSelect(t.id)}
      title={t.name}
      className="group cursor-pointer"
    >
      <div className={`
        relative w-[215px] h-[215px] rounded-2xl overflow-hidden bg-surface
        border-2 transition-all duration-300 ease-out
        ${active
          ? 'border-primary shadow-lg shadow-primary/30 -translate-y-1'
          : 'border-transparent group-hover:border-primary/40 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-dark/20'}
      `}>
        <img
          src={aiTemplateSamplePath(t)}
          alt={t.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.12]"
          loading="lazy"
          draggable={false}
        />
        {/* "Held" badge — flags model-holding (In Hand) templates so the
            merged library stays scannable (Jun 12 v6). */}
        {t.route === 'product-swap' && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wide">
            <i className="ri-hand-heart-line text-xs" />
            Held
          </div>
        )}
        {/* Dim wash on the unpicked tiles when something IS picked is
            avoided — instead each picked tile just lifts + gets the badge,
            keeping the grid scannable. */}
        {/* Name reveal — bottom gradient scrim, hover-only (always
            shown while selected so the choice stays labeled). */}
        <div className={`
          absolute inset-x-0 bottom-0 pt-10 pb-2.5 px-3
          bg-gradient-to-t from-black/60 via-black/25 to-transparent
          transition-opacity duration-300
          ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
        `}>
          <p className="text-sm font-semibold text-white text-left leading-tight drop-shadow">
            {t.name}
          </p>
        </div>
        {active && (
          <div className="absolute top-2 right-2 min-w-6 h-6 px-1.5 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shadow-md">
            {badgeNumber}
          </div>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// TemplateGrid — 4-up grid of curated Product Shot Recreate templates.
// Pulls from /mocks/recreate-templates.json filtered to category =
// 'product-shot' (same data source as the /templates page in the
// sidebar). Each tile is a square thumbnail with the name rendered
// BELOW (not overlaid) so the preview stays clean — matches the
// /templates page's tile styling more closely than the old gradient-
// overlay scene-picker.
// =============================================================================

interface TemplateGridProps {
  templates: RecreateTemplate[];
  routeTitle: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function TemplateGrid({ templates, routeTitle, selectedId, onSelect }: TemplateGridProps) {
  // Empty state — currently true for product-swap (no authored templates
  // yet). We render a friendly nudge pointing to the AI Magic tab
  // rather than a blank grid.
  if (templates.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold uppercase tracking-wide text-dark flex items-center gap-1.5">
            <i className="ri-layout-grid-line text-primary text-base" />
            Pick a template
          </p>
          <span className="text-xs text-dark-muted font-semibold">0 curated</span>
        </div>
        <div className="rounded-xl border-2 border-dashed border-surface-dark/40 bg-white/70 p-8 text-center">
          <i className="ri-inbox-line text-3xl text-dark-muted/60" />
          <p className="text-sm font-bold text-dark mt-3">
            No {routeTitle} templates yet
          </p>
          <p className="text-xs text-dark-muted mt-1 max-w-xs mx-auto leading-relaxed">
            We haven&apos;t authored curated templates for this category yet.
            Use the <span className="font-bold text-primary">AI Magic</span> tab
            above — it generates the ad freestyle from your product.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold uppercase tracking-wide text-dark flex items-center gap-1.5">
          <i className="ri-layout-grid-line text-primary text-base" />
          Pick a template
        </p>
        <span className="text-xs text-dark-muted font-semibold">
          {templates.length} curated
        </span>
      </div>
      {/* 4-up grid at the prototype's panel width. Tile is the square
          thumbnail; name renders BELOW (gap-y picks up the row gap so
          tile + label sit close together). Smaller text-xs label keeps
          the visual focus on the thumbnail itself. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {templates.map((tmpl) => {
          const isActive = tmpl.id === selectedId;
          return (
            <div key={tmpl.id} className="flex flex-col items-stretch gap-1.5">
              <button
                type="button"
                onClick={() => onSelect(tmpl.id)}
                title={tmpl.name}
                className={`
                  relative aspect-square rounded-xl overflow-hidden
                  border-2 transition-all cursor-pointer bg-white
                  ${isActive
                    ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
                    : 'border-surface-dark/30 hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5'}
                `}
              >
                <img
                  src={tmpl.previewImage}
                  alt={tmpl.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
                {isActive && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs shadow-sm">
                    <i className="ri-check-line" />
                  </span>
                )}
              </button>
              <p className={`
                text-xs leading-tight text-center line-clamp-1 px-1
                ${isActive ? 'text-primary font-bold' : 'text-dark-muted font-medium'}
              `}>
                {tmpl.name}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// FormatRow — Single vs Carousel toggle. Pinned at the top of the AI
// Magic filter list because format determines credit cost + which other
// filters apply. Carousel mode shows a caption explaining the 4 framings
// are auto-picked (user's Framing CD filter is ignored).
// =============================================================================

interface FormatRowProps {
  value: Format;
  onChange: (v: Format) => void;
  carouselFramings: readonly CreativeFraming[];
}

function FormatRow({ value, onChange, carouselFramings }: FormatRowProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="flex-shrink-0 w-32 text-sm font-bold text-dark uppercase tracking-wide pt-2 flex items-center gap-1.5">
          <i className="ri-layout-grid-2-line text-primary text-base" />
          Format
        </span>
        <div className="flex flex-wrap flex-1 min-w-0 gap-2">
          {(['single', 'carousel'] as const).map((opt) => {
            const isActive = opt === value;
            const label = opt === 'single' ? 'Single' : 'Carousel · 4 frames';
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(opt)}
                title={
                  opt === 'single'
                    ? 'One static frame'
                    : '4-frame swipeable carousel · auto-picked framings'
                }
                className={`
                  inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer
                  ${isActive
                    ? 'bg-primary text-white shadow-md shadow-primary/25 -translate-y-0.5'
                    : 'bg-white border border-surface-dark/40 text-dark-muted hover:border-primary/40 hover:text-dark hover:shadow-sm'}
                `}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {value === 'carousel' && (
        <p className="text-xs text-dark-muted pl-[8.5rem] pr-2 leading-relaxed">
          4 framings auto-picked for variety (tight → wide):{' '}
          <span className="font-semibold text-dark">
            {carouselFramings.join(' · ')}
          </span>
          . Your Framing filter below is ignored in carousel mode.
        </p>
      )}
    </div>
  );
}

// =============================================================================
// FashionModelPicker — fashion-only model strip rendered above the CD
// filters. Horizontal scrolling row of the 17 fashionModels with an
// "Auto" (no preference) tile at the start. The picked model's name +
// vibe become the model_hint sent to the backend; AI still invents the
// model, but skewed toward the picked look.
// =============================================================================

interface FashionModelPickerProps {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

function FashionModelPicker({ selectedId, onSelect }: FashionModelPickerProps) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-bold text-dark uppercase tracking-wide">
          <i className="ri-user-line text-primary text-base" />
          Model
        </span>
        <span className="text-xs text-dark-muted">
          {selectedId
            ? fashionModels.find((m) => m.id === selectedId)?.name ?? 'Custom'
            : 'AI picks'}
        </span>
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1 -mb-1">
        {/* Auto tile — no model preference. AI invents freely. */}
        <button
          type="button"
          onClick={() => onSelect(null)}
          title="Let the AI invent the model"
          className={`
            relative flex-shrink-0 w-[72px] h-[96px] rounded-xl overflow-hidden
            border-2 transition-all cursor-pointer flex flex-col items-center
            justify-center gap-1 text-center px-1.5
            ${selectedId === null
              ? 'border-primary bg-primary/5 shadow-md shadow-primary/20'
              : 'border-surface-dark/30 bg-white hover:border-primary/40 hover:shadow-sm'}
          `}
        >
          <i className={`
            ri-sparkling-2-line text-xl
            ${selectedId === null ? 'text-primary' : 'text-dark-muted'}
          `} />
          <span className={`
            text-[10px] font-bold leading-tight
            ${selectedId === null ? 'text-primary' : 'text-dark-muted'}
          `}>
            AI picks
          </span>
          {selectedId === null && (
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
              <i className="ri-check-line text-[10px]" />
            </span>
          )}
        </button>
        {fashionModels.map((model) => {
          const active = model.id === selectedId;
          return (
            <button
              key={model.id}
              type="button"
              onClick={() => onSelect(model.id)}
              title={`${model.name} · ${model.vibe}`}
              className={`
                relative flex-shrink-0 w-[72px] h-[96px] rounded-xl overflow-hidden
                border-2 transition-all cursor-pointer
                ${active
                  ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
                  : 'border-surface-dark/30 hover:border-primary/40 hover:shadow-sm'}
              `}
            >
              <img
                src={fashionAssetUrl(model, model.base)}
                alt={model.name}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />
              {/* Bottom gradient + label so the model name is readable */}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-dark/80 via-dark/40 to-transparent" />
              <span className="absolute inset-x-1 bottom-1 text-[9px] font-bold text-white text-center line-clamp-2 leading-tight drop-shadow">
                {model.name.replace(/^Studio Model /, 'Model ')}
              </span>
              {active && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
                  <i className="ri-check-line text-[10px]" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// AspectChip — sticky-bottom aspect-ratio dropdown
// =============================================================================

// AspectRow / QualityRow — visually match FilterRow (label w-32 on the
// left, pills on the right). Used by the LEFT-column filter list so all
// rows line up vertically. They render as text-pill rows since
// aspect/quality don't have meaningful image previews.

interface AspectRowProps {
  value: ProtoAspect;
  onChange: (v: ProtoAspect) => void;
  /** Which aspect chips to render. /appv3/product-shot allows all four;
   *  Held + Worn currently only support 1:1 / 4:5 / 9:16 (no 16:9
   *  authored templates) so the row's allowed list is route-scoped. */
  allowed: readonly ProtoAspect[];
}

function AspectRow({ value, onChange, allowed }: AspectRowProps) {
  return (
    <div className="flex items-start gap-3 flex-wrap">
      <span className="flex-shrink-0 w-32 text-sm font-bold text-dark uppercase tracking-wide pt-2 flex items-center gap-1.5">
        <i className="ri-aspect-ratio-line text-primary text-base" />
        Aspect
      </span>
      <div className="flex flex-wrap flex-1 min-w-0 gap-2">
        {allowed.map((aspect) => {
          const isActive = aspect === value;
          return (
            <button
              key={aspect}
              type="button"
              onClick={() => onChange(aspect)}
              className={`
                px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer
                ${isActive
                  ? 'bg-primary text-white shadow-md shadow-primary/25 -translate-y-0.5'
                  : 'bg-white border border-surface-dark/40 text-dark-muted hover:border-primary/40 hover:text-dark hover:shadow-sm'}
              `}
              title={ASPECT_LABEL[aspect]}
            >
              {aspect}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================

interface QualityRowProps {
  value: Quality;
  onChange: (v: Quality) => void;
  isFree: boolean;
}

function QualityRow({ value, onChange, isFree }: QualityRowProps) {
  const tiers: { id: Quality; label: string; locked: boolean }[] = [
    { id: 'low', label: 'Low (1cr)', locked: false },
    { id: 'medium', label: 'Med (2cr)', locked: isFree },
    { id: 'high', label: 'High (4cr)', locked: isFree },
  ];
  return (
    <div className="flex items-start gap-3 flex-wrap">
      <span className="flex-shrink-0 w-32 text-sm font-bold text-dark uppercase tracking-wide pt-2 flex items-center gap-1.5">
        <i className="ri-star-line text-primary text-base" />
        Quality
      </span>
      <div className="flex flex-wrap flex-1 min-w-0 gap-2">
        {tiers.map((tier) => {
          const isActive = tier.id === value;
          return (
            <button
              key={tier.id}
              type="button"
              onClick={() => !tier.locked && onChange(tier.id)}
              disabled={tier.locked}
              title={tier.locked ? 'Upgrade to unlock' : tier.label}
              className={`
                inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all
                ${tier.locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                ${isActive
                  ? 'bg-primary text-white shadow-md shadow-primary/25 -translate-y-0.5'
                  : 'bg-white border border-surface-dark/40 text-dark-muted hover:border-primary/40 hover:text-dark hover:shadow-sm'}
              `}
            >
              {tier.locked && <i className="ri-lock-2-line" />}
              {tier.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// ProductPickerSection — URL input row + horizontal scrolling strip of
// saved-product thumbs with a "+ Upload" tile.
// =============================================================================

interface ProductPickerSectionProps {
  savedProducts: UserProduct[] | null;
  selectedSavedProductId: string | null;
  onPickSaved: (product: UserProduct) => void;
  onUploadFile: (file: File) => void;
  productName: string;
  productImageUrl: string | null;
}

function ProductPickerSection({
  savedProducts,
  selectedSavedProductId,
  onPickSaved,
  onUploadFile,
  productName,
  productImageUrl,
}: ProductPickerSectionProps) {
  const [urlInput, setUrlInput] = useState('');
  const onFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUploadFile(file);
    // Reset so the same file can be re-picked.
    e.target.value = '';
  }, [onUploadFile]);

  return (
    <div className="rounded-xl border border-surface-dark/30 bg-white p-3 space-y-2.5">
      {/* URL row (TODO note — not wired in prototype) */}
      <div className="flex items-center gap-2">
        <input
          type="url"
          placeholder="Paste a product page URL…"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-surface-dark/40 text-sm focus:outline-none focus:border-primary"
        />
        <button
          type="button"
          disabled
          title="URL scrape not wired in prototype — use file upload or library"
          className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-primary/30 text-white text-xs font-bold cursor-not-allowed opacity-60"
        >
          Fetch
        </button>
      </div>

      {/* Horizontal scrolling strip */}
      <div className="flex items-center gap-2 overflow-x-auto py-1 -my-1">
        {/* Upload tile — always first/leftmost so it's reachable */}
        <label className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary cursor-pointer flex flex-col items-center justify-center gap-1 transition-all">
          <i className="ri-upload-cloud-line text-2xl text-primary" />
          <span className="text-[10px] font-bold text-primary">Upload</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFilePick}
          />
        </label>

        {/* Currently-uploaded but not-from-library tile (when user file-picked) */}
        {productImageUrl && !selectedSavedProductId && (
          <div
            className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 border-primary shadow-md shadow-primary/25 relative"
            title={productName || 'Uploaded product'}
          >
            <img src={productImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs">
              <i className="ri-check-line" />
            </span>
          </div>
        )}

        {/* Saved-products thumbs */}
        {savedProducts === null && (
          <div className="flex-shrink-0 text-xs text-dark-muted italic px-2">
            Loading library…
          </div>
        )}
        {savedProducts !== null && savedProducts.length === 0 && (
          <div className="flex-shrink-0 text-xs text-dark-muted italic px-2">
            No saved products. <a href="/products" className="text-primary font-semibold underline">Add some →</a>
          </div>
        )}
        {savedProducts !== null && savedProducts.map((product) => {
          const isSelected = product.id === selectedSavedProductId;
          return (
            <button
              key={product.id}
              type="button"
              onClick={() => onPickSaved(product)}
              title={product.name ?? 'Saved product'}
              className={`
                relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2
                transition-all cursor-pointer
                ${isSelected
                  ? 'border-primary shadow-md shadow-primary/25'
                  : 'border-surface-dark/30 hover:border-primary/40 hover:shadow-sm'}
              `}
            >
              <img
                src={product.imageUrl}
                alt={product.name ?? ''}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />
              {isSelected && (
                <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs">
                  <i className="ri-check-line" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// PreviewCanvas — large centered preview. Three states: empty / generating /
// result. Reuses AiResultViewer for the AI path's result + loading skeleton.
// =============================================================================

interface PreviewCanvasProps {
  aspectRatio: ProtoAspect;
  productImageUrl: string | null;
  generating: boolean;
  error: string | null;
  aiResult: WizardAiGenerateResult | null;
  generatedImage: string | null;
  /** 1 for single, 4 for carousel — drives the AiResultViewer skeleton
   *  count and the result-grid layout. */
  frameCount: number;
  onBackToEdit: () => void;
  onRetry: () => void;
  /** When true, suppresses the "PREVIEW · 1:1" eyebrow row + the outer
   *  card chrome (border / gradient bg / padding / shadow). Used by the
   *  Studio layout where the panel itself already supplies the chrome
   *  and the eyebrow reads as redundant noise. Default false preserves
   *  the legacy /appv3 product-shot rendering exactly. */
  bare?: boolean;
  /** When true + product is picked, swap the empty-state message from
   *  "Click Generate to start" to a template-prompting message ("Pick a
   *  template — or switch to Create"). Used by the Studio layout when
   *  the user has settled their product but hasn't picked a template on
   *  the LEFT panel. Default false → existing "Click Generate" message. */
  needsTemplate?: boolean;
}

function PreviewCanvas({
  aspectRatio,
  productImageUrl,
  generating,
  error,
  aiResult,
  generatedImage,
  frameCount,
  onBackToEdit,
  onRetry,
  bare = false,
  needsTemplate = false,
}: PreviewCanvasProps) {
  const aspectClass = ASPECT_TW[aspectRatio];
  const hasResult = !!(aiResult || generatedImage);

  // Hand control to AiResultViewer when AI flow has a result OR is in flight.
  // The template flow renders a simple <img> result without the carousel
  // download buttons (single frame, single PNG).
  // WIDTH ANCHOR (Jun 12, gotcha #50 family): in the Studio layout the
  // parent is `flex items-center justify-center`, so without an explicit
  // width this wrapper shrink-wraps to ~0 and the skeleton's aspect-square
  // collapses to a dot. `w-full max-w-[540px]` keeps the loader/result
  // properly sized there; legacy (non-bare) block flow stays unconstrained.
  if (aiResult || (generating && !generatedImage && !error)) {
    return (
      <div className={`rounded-xl border border-surface-dark/30 bg-white p-4 ${bare ? 'w-full max-w-[540px]' : ''}`}>
        <AiResultViewer
          result={aiResult}
          loading={generating}
          expectedFrameCount={frameCount}
          error={error}
          onRetry={onRetry}
          onBack={onBackToEdit}
          hideCopyBlock
          // Studio (bare) renders its own action row in the panel footer
          // (Back · Try again · Save · Schedule · Download) — the in-card
          // buttons would duplicate it.
          hideActions={bare}
        />
      </div>
    );
  }

  return (
    <div className={
      bare
        // Width anchor for the Studio layout's centered-flex parent —
        // same collapse risk as the loader branch above.
        ? "w-full max-w-[540px]"
        : "rounded-2xl border-2 border-surface-dark/40 bg-gradient-to-br from-surface via-white to-surface-dark/20 p-5 shadow-md"
    }>
      {!bare && (
        <p className="text-[11px] font-bold uppercase tracking-wider text-dark-muted mb-3 flex items-center justify-between">
          <span><i className="ri-eye-line mr-1.5" />Preview</span>
          <span className="text-[10px] text-dark-muted/70 font-medium normal-case tracking-normal">{aspectRatio}</span>
        </p>
      )}
      <div className={`relative w-full ${aspectClass} max-h-[60vh] mx-auto rounded-xl overflow-hidden bg-white ${bare ? '' : 'shadow-inner'}`}>
        {generatedImage ? (
          // Template-path result — just show the image with a Back button.
          <>
            <img
              src={generatedImage}
              alt="Generated"
              className="absolute inset-0 w-full h-full object-contain bg-surface"
            />
          </>
        ) : error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 border-2 border-red-200 rounded-xl gap-2 p-4 text-center">
            <i className="ri-error-warning-line text-3xl text-red-600" />
            <p className="text-sm font-bold text-red-800">Generation failed</p>
            <p className="text-xs text-red-700 max-w-sm">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-1 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all cursor-pointer"
            >
              <i className="ri-refresh-line" /> Try again
            </button>
          </div>
        ) : productImageUrl ? (
          // Jun 22 — Option 2: tight content-sized dashed card centered
          // in the preview area. Previously the dashed card filled the
          // entire inset-0 (later inset-2), making the dashes look way
          // larger than the product image inside (which is only 55%
          // wide). Now the dashed card sizes to its own content (product
          // image + helper text), giving the dashes a sensible perimeter
          // around what's actually being framed, with breathing room
          // above and below in the larger preview area. Outer wrapper
          // (inset-0 + flex centering) handles positioning; inner dashed
          // box handles sizing + styling.
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="flex flex-col items-center justify-center bg-surface/60 border-[3px] border-dashed border-primary/40 rounded-xl gap-3 p-8 max-w-sm">
              <img
                src={productImageUrl}
                alt=""
                className="w-32 h-32 object-contain opacity-70 drop-shadow-lg"
              />
              {needsTemplate ? (
                <>
                  <p className="text-sm text-dark font-bold mt-2 flex items-center gap-1.5">
                    <i className="ri-arrow-left-line text-primary animate-bounce" />
                    Pick a template on the left
                  </p>
                  <p className="text-xs text-dark-muted text-center">
                    Or switch to <b>Create</b> to design your own from scratch.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-dark font-bold mt-2 flex items-center gap-1.5">
                    <i className="ri-arrow-down-line text-primary animate-bounce" />
                    Click Generate to start
                  </p>
                  <p className="text-xs text-dark-muted">
                    Your ad will replace this preview
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          // Same shape as the productImageUrl branch above — tight
          // content-sized dashed card centered in the preview area.
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="flex flex-col items-center justify-center bg-surface/60 border-[3px] border-dashed border-surface-dark/60 rounded-xl gap-3 p-8 max-w-sm text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                <i className="ri-image-add-line text-3xl" />
              </div>
              <p className="text-base font-bold text-dark">No product selected yet</p>
              <p className="text-xs text-dark-muted leading-relaxed">
                Pick from your library on the right, paste a product URL, or upload an image.
                The preview will appear here.
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Hidden in bare/Studio mode — the panel footer owns the result
          action row there (Back · Try again · Save · Schedule · Download). */}
      {!bare && hasResult && generatedImage && (
        <div className="flex justify-end gap-2 mt-3">
          <button
            type="button"
            onClick={onBackToEdit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> Back
          </button>
          <a
            href={generatedImage}
            download="product-shot.png"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
          >
            <i className="ri-download-2-line" /> Download PNG
          </a>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// GenerateCta — full-width sticky-bottom primary button with credit chip
// =============================================================================

interface GenerateCtaProps {
  canGenerate: boolean;
  generating: boolean;
  insufficient: boolean;
  balance: number | null;
  creditCost: number;
  productFile: File | null;
  designMode: DesignMode;
  selectedTemplateId: string | null;
  /** Product Shot's Templates tab selection (AI template library) —
   *  either template kind satisfies template mode. */
  selectedAiTemplateId?: string | null;
  /** Drives the CTA label ("Generate Product Shot" / "Generate Model
   *  Shot") so the button reads as the right action per route. */
  routeTitle: string;
  onGenerate: () => void;
  onUpgrade: () => void;
}

function GenerateCta({
  canGenerate,
  generating,
  insufficient,
  balance,
  creditCost,
  productFile,
  designMode,
  selectedTemplateId,
  selectedAiTemplateId = null,
  routeTitle,
  onGenerate,
  onUpgrade,
}: GenerateCtaProps) {
  // Compute the disabled reason for inline hint text.
  let disabledReason: string | null = null;
  if (!productFile) {
    disabledReason = 'Pick or upload a product first.';
  } else if (designMode === 'template' && !selectedTemplateId && !selectedAiTemplateId) {
    disabledReason = 'Choose a template on the left.';
  } else if (insufficient) {
    disabledReason = `You have ${balance ?? 0} credits — need ${creditCost}.`;
  }

  if (insufficient) {
    return (
      <div className="sticky bottom-4 z-10">
        <button
          type="button"
          onClick={onUpgrade}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base font-bold shadow-lg shadow-amber-500/25 transition-all cursor-pointer"
        >
          <i className="ri-lock-2-line" /> Upgrade — {creditCost} credit{creditCost === 1 ? '' : 's'} needed
        </button>
        {disabledReason && (
          <p className="text-xs text-dark-muted text-center mt-1.5">{disabledReason}</p>
        )}
      </div>
    );
  }

  return (
    <div className="sticky bottom-4 z-10">
      <button
        type="button"
        onClick={onGenerate}
        disabled={!canGenerate}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-base font-bold shadow-lg shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {generating ? (
          <><i className="ri-loader-4-line animate-spin" /> Generating…</>
        ) : (
          <>
            <i className="ri-sparkling-2-line" /> Generate {routeTitle}
            <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold">
              {creditCost} credit{creditCost === 1 ? '' : 's'}
            </span>
          </>
        )}
      </button>
      {disabledReason && !generating && (
        <p className="text-xs text-dark-muted text-center mt-1.5">{disabledReason}</p>
      )}
    </div>
  );
}

// ============================================================================
// ============================================================================
// STUDIO LAYOUT — the new "Image Studio" design from the June 9+ prototype.
// Currently enabled for held + fashion only. Product Shot keeps the legacy
// layout above. Will extend once held + fashion prove out.
//
// Top-level shape (single panel, two columns):
//   LEFT (~64% width):
//     - Pill toggle: Create | Templates  (replaces DesignTabs)
//     - MODEL section (fashion only) — horizontal scroll strip of fashion
//       models with "AI picks" tile first
//     - FRAMING section — image-card row for the route's framings
//     - 5-tab bar: Look / Light / Background / Brand / Output
//       (each tab label shows its current value beneath)
//     - Tab content swaps based on selected tab
//     - Footer: Format toggle + cost + Apply & Generate button
//   RIGHT (~36% width):
//     - "AURA"-style preview placeholder (silhouette + sample copy) OR
//       the AiResultViewer when a result exists
//     - "Preview is N edit behind" badge when editsSinceGenerate > 0
// ============================================================================
// ============================================================================

type StudioTab = 'look' | 'light' | 'background' | 'brand' | 'output';

interface StudioLayoutRenderProps {
  routeConfig: RouteConfig;
  navigate: (to: string) => void;
  designMode: DesignMode;
  onDesignModeChange: (mode: DesignMode) => void;
  creativeDirection: CreativeDirection;
  onCreativeDirectionChange: (cd: CreativeDirection) => void;
  aspectRatio: ProtoAspect;
  onAspectRatioChange: (v: ProtoAspect) => void;
  allowedAspects: readonly ProtoAspect[];
  quality: Quality;
  onQualityChange: (v: Quality) => void;
  isFree: boolean;
  format: Format;
  onFormatChange: (v: Format) => void;
  selectedFashionModelId: string | null;
  onFashionModelSelect: (id: string | null) => void;
  /** Product Shot ONLY (Jun 11) — pattern hero picker selection.
   *  Other routes ignore. */
  selectedPattern: CreativePattern | null;
  onPatternSelect: (p: CreativePattern | null) => void;
  /** Pose pick (Jun 12 v6) — Product Shot's second Create hero group.
   *  Switches the shot to held mode (clears pattern). */
  onPoseSelect: (pose: CreativePose) => void;
  /** Product Shot ONLY (Jun 11) — AI template selection from the
   *  Templates tab. When set with designMode='template', generate uses
   *  the template's hardcoded prompt instead of the LLM-written one. */
  /** Multi-select (Jun 12 v3) — full ordered list of picked AI templates.
   *  Numbered badges in the grid; matrix = products × these. */
  selectedAiTemplateIds: string[];
  lockedTemplateId: string | null;
  onClearLockedTemplate: () => void;
  onAiTemplateSelect: (id: string) => void;
  /** Recipes-as-Templates (Jun 18 — Phase 3). Approved moodboard recipes
   *  with baked previews — surfaced ABOVE the AI template grid on the
   *  Product Shot Templates tab. Single-select; mutually exclusive with
   *  AI template selection (picking either clears the other). null when
   *  not yet loaded; [] when loaded but no recipes match filters. */
  recipeTemplates: ProductionRecipe[] | null;
  recipesLoading: boolean;
  selectedRecipeId: string | null;
  /** Deep-linked single-recipe lock (Jun 18) — when set, the Templates tab
   *  shows ONLY this recipe (mirrors lockedTemplateId). '<vibe_slug>/<id>'. */
  lockedRecipeId: string | null;
  onClearLockedRecipe: () => void;
  recipeVibeFilter: string | null;
  onRecipeVibeFilterChange: (v: string | null) => void;
  onRecipeSelect: (id: string) => void;
  /** Allowed product_type values derived from the uploaded product's
   *  domain. null = no product yet (show all recipes); array = strict
   *  filter (recipes must have at least one matching tag in product_types[]). */
  recipeProductTypes: string[] | null;
  /** Inline product picker mode (Jun 11). When `productPickerOpen` is
   *  true, the right panel renders StepUpload instead of the preview;
   *  the footer shows a Continue button instead of GENERATE. After the
   *  user hits Continue, the picker collapses to preview mode with a
   *  small product thumb + Edit button at the top. */
  productPickerOpen: boolean;
  onProductPickerContinue: () => void;
  onProductEdit: () => void;
  /** Adapter for StepUpload's onUpload signature. */
  onStepUploadCallback: (images: string[], files: File[]) => void;
  /** URL-scrape success forwarded into name/description state. */
  onScrapeSuccess: (
    name: string,
    description: string,
    insights?: { productCategory?: string; tags?: string[] },
  ) => void;
  selectedTemplateId: string | null;
  onTemplateSelect: (id: string) => void;
  routeTemplates: RecreateTemplate[];
  productName: string;
  productImageUrl: string | null;
  productFile: File | null;
  /** Product domain (skincare / consumable / wearable) derived from the
   *  picked product's cached classification. Drives the Templates tab's
   *  "Recommended for <domain>" grouping. Null = no clear domain → flat
   *  ungrouped template grid. */
  productDomain: AiTemplateDomain | null;
  /** Jun 22 — Fashion route safety. True when the picked product has a
   *  classified domain that's NOT wearable (apparel/footwear/accessory/
   *  jewelry). Drives both a red banner above the Generate button AND
   *  the canGenerate gate (already AND'd in the parent). Null-domain
   *  raw uploads are allowed through (no signal to block on). */
  fashionProductMismatch: boolean;
  onOpenProductModal: () => void;
  onClearProduct: () => void;
  generating: boolean;
  error: string | null;
  aiResult: WizardAiGenerateResult | null;
  generatedImage: string | null;
  frameCount: number;
  onBackToEdit: () => void;
  onRetry: () => void;
  canGenerate: boolean;
  creditCost: number;
  insufficient: boolean;
  balance: number | null;
  onGenerate: () => void;
  /** Matrix (Jun 12 v3) — number of images the current selection will
   *  produce (N templates × products) + whether the matrix UI is active. */
  matrixImageCount: number;
  matrixActive: boolean;
  onUpgrade: () => void;
  /** Result-action row (Jun 12). Replaces the footer pills + Generate
   *  once a result is on screen: Back to design · Try again (with
   *  quality picker) · Save to My Creatives · Schedule/Share · Download. */
  onRegenerateWithQuality: (q: Quality) => void;
  onSaveToCreatives: () => void;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  onScheduleShare: () => void;
  onDownloadResult: () => void;
  downloadingResult: boolean;
  editsSinceGenerate: number;
  // ---- Finishing surface (Jun 12 v2 — render panel = edit screen) ----
  /** Jun 30 — when set (text-overlay tile, post auto-saved), the finishing
   *  surface renders the SAME OverlayEditor as /creatives/:id in place of
   *  StudioFinishEditor. null → keep the canvas editor. */
  overlayPost: Post | null;
  onOverlaySaved: (p: Post) => void;
  /** Jul 17 — Product Shot flat render, auto-saved → render the SHARED
   *  CreativeEditor (v4) in place of StudioFinishEditor. null → canvas editor. */
  flatEditorPost: Post | null;
  /** Jul 18 — true once the user saved an in-editor edit (a version fork). When
   *  true, the embedded editor loads the edited version fresh instead of seeding
   *  the stale frame on Back-to-design → return. */
  editorForked: boolean;
  /** Called by the embedded editor when it forks a version (in-editor save). */
  onEditorFork: (pid: string) => void;
  /** Jul 18 — fetch a FRESH batch of AI copy lines for the editor's "AI text" button. */
  onRequestAiText: () => Promise<string[] | null>;
  /** Jul 17 — this run finishes in CreativeEditor (Product Shot). Keeps the
   *  generating takeover up through auto-save so StudioFinishEditor never
   *  flashes before CreativeEditor mounts. */
  productShotFinish: boolean;
  /** Canonical editable base frames (data URLs). Length 1 (single) or 4
   *  (carousel). Prompt-edits mutate these in place; overlays ride on
   *  top client-side and flatten at save/download time. */
  displayFrames: string[];
  /** Aspect the result was rendered at (captured at generate time). */
  renderedAspect: ProtoAspect;
  textOverlays: TextOverlay[];
  onTextOverlaysChange: (o: TextOverlay[]) => void;
  logoOverlays: LogoOverlay[];
  onLogoOverlaysChange: (l: LogoOverlay[]) => void;
  brandLogoUrl: string | null;
  brandColors: string[];
  /** AI-written copy from the generation concept — powers the property
   *  panel's "AI generate" fill (headline ↔ tagline). */
  suggestedFrames: SuggestedFrame[];
  /** Active frame in the preview — the prompt-edit target. */
  activeFrameIdx: number;
  onActiveFrameChange: (i: number) => void;
  /** "Edit with prompt" bar state + submit (1 credit per edit). */
  editPrompt: string;
  onEditPromptChange: (v: string) => void;
  promptEditing: boolean;
  editError: string | null;
  onPromptEdit: () => void;
  /** Flatten + replace the saved creative if edits are pending. Fired on
   *  Back to edit / panel collapse (fire-and-forget; no-op when clean). */
  onSyncCreative: () => void;
  /** Manual Save button on the image (Jun 12 v5) — force-flushes the current
   *  composite + flashes the save status. `creativeDirty` drives the button's
   *  "unsaved edits" emphasis. */
  onManualSave: () => void;
  creativeDirty: boolean;
  productModalOpen: boolean;
  onCloseProductModal: () => void;
  savedProducts: UserProduct[] | null;
  selectedSavedProductId: string | null;
  onPickSavedProduct: (product: UserProduct) => void;
  onUploadFile: (file: File) => void;
  // V3 Carousel (Jun 14) — when category='carousel', the picked
  // carousel type rides through. Null on non-carousel routes (then
  // the type summary bar doesn't render).
  selectedCarouselType: CarouselTypeId | null;
  onEditCarouselType: () => void;
  // V3 Social Post (Jun 15) — when category='social-post', the picked
  // intent rides through so FashionStudioLayout can render the same
  // summary bar pattern as Carousel (folds the pre-step into a slim
  // editable header). Null on non-social-post routes.
  selectedIntent: SocialPostIntentId | null;
  onEditIntent: () => void;
  // V3 Social Post (Jun 15 v2) — master Filters on/off toggle. When
  // false, the LEFT filter row pickers (vibe/light/bg/framing) render
  // dimmed + non-interactive AND no creative_direction values for those
  // fields are sent to backend on Generate. Other routes ignore this
  // prop (filters always interactive).
  filtersEnabled: boolean;
  onFiltersEnabledChange: (v: boolean) => void;
  // V3 Social Post (Jun 15 v2) — Shot type toggle. Drives the apiRoute
  // dispatch: 'in-use' → 'product-swap' (model holding product, safety
  // scaffold); 'product-only' → 'social-post' (no model). Always
  // interactive (independent of filtersEnabled) since it changes routing
  // not creative styling.
  socialPostShot: 'product-only' | 'in-use';
  onSocialPostShotChange: (v: 'product-only' | 'in-use') => void;
}

const QUALITY_LABEL_SHORT: Record<Quality, string> = {
  low: 'Low',
  medium: 'Med',
  high: 'High',
};

function StudioLayoutRender(props: StudioLayoutRenderProps) {
  // Jun 11 — Fashion + Held + Product Shot all get the new Studio
  // layout. Each route renders a different hero picker in the same
  // upper-left slot:
  //   • Fashion → MODEL grid (17 authored models)
  //   • Held → POSE grid (6 hand positions)
  //   • Product Shot → PATTERN grid (6 arrangement styles)
  // All three share the lavender filter bar (Framing / Light /
  // Background / Vibe / Brand), the right-panel preview, the Output
  // footer pill, and GENERATE button.
  if (props.routeConfig.category === 'fashion'
      || props.routeConfig.category === 'product-swap'
      || props.routeConfig.category === 'product-shot'
      // V3 Carousel (Jun 14) — uses the SAME FashionStudioLayout as
      // Product Shot. Carousel-specific behavior (format locked to
      // 'carousel', matrix disabled, Create tab hero swap) is
      // category-conditional inside FashionStudioLayout itself.
      || props.routeConfig.category === 'carousel'
      // V3 Social Post (Jun 15) — same FashionStudioLayout chrome.
      // Templates tab is HIDDEN for social-post (the
      // StudioCreateTemplatesPill render conditional has the matching
      // check); designMode is force-locked to 'ai' in the route-reset
      // effect so the user always lands in AI Magic mode after the
      // intent pre-step.
      || props.routeConfig.category === 'social-post') {
    return <FashionStudioLayout {...props} />;
  }

  const {
    routeConfig, navigate, designMode, onDesignModeChange,
    creativeDirection, onCreativeDirectionChange,
    aspectRatio, onAspectRatioChange, allowedAspects,
    quality, onQualityChange, isFree,
    format, onFormatChange,
    selectedFashionModelId, onFashionModelSelect,
    selectedTemplateId, onTemplateSelect, routeTemplates,
    productName, productImageUrl,
    onOpenProductModal, onClearProduct,
    generating, error, aiResult, generatedImage, frameCount,
    onBackToEdit, onRetry,
    canGenerate, creditCost, insufficient, balance,
    onGenerate, onUpgrade,
    editsSinceGenerate,
    productModalOpen, onCloseProductModal, savedProducts,
    selectedSavedProductId, onPickSavedProduct, onUploadFile,
  } = props;

  // Note: activeTab state was removed when the inline tab bar was
  // replaced with StudioAccordion (Jun 10) — the accordion manages its
  // own open/closed state internally. StudioTab type + StudioTabBar +
  // StudioTabContent components remain in the file as dead code in case
  // we want to swap back to the tab layout.
  const hasResult = !!(aiResult || generatedImage);
  const showEditsBadge = hasResult && editsSinceGenerate > 0;
  const showModelSection = routeConfig.category === 'fashion';

  return (
    <AppLayoutV2>
      <div className="w-full max-w-[1500px] mx-auto px-6 py-8">
        {/* ============ HEADER ============ */}
        <div className="mb-5">
          <h1 className="text-xl font-bold text-dark leading-tight">
            {routeConfig.title}
          </h1>
        </div>

        {/* ============ TWO INDEPENDENT PANELS ============
            Following the Figma cleanup: left + right are separate cards
            (not one combined panel). Both are white, rounded, with a
            subtle shadow + border — no gradient, no pink top accent.
            Each has its own prominent top action bar (Create/Templates
            on the left; Select Product on the right) reading as twin
            "panel actions". items-stretch + viewport-anchored min-h
            keeps both panels the same height regardless of content. */}
        {/* Fixed-height panels: 800px tall, scroll inside the left panel's
            body if accordion sections overflow. Right panel matches. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
          {/* LEFT — filter panel (accordion) */}
          <div className="rounded-2xl bg-white border border-surface-dark/30 shadow-md shadow-dark/[0.04] flex flex-col overflow-hidden h-[800px]">
            {/* Create / Templates — top action bar of the LEFT panel. */}
            <div className="flex-shrink-0 p-3 border-b border-surface-dark/20">
              <StudioCreateTemplatesPill
                designMode={designMode}
                onChange={onDesignModeChange}
              />
            </div>
            <div className="px-3 py-3 flex-1 min-h-0 overflow-y-auto sidebar-scroll">
              {designMode === 'ai' ? (
                <StudioAccordion
                  routeConfig={routeConfig}
                  showModelSection={showModelSection}
                  selectedFashionModelId={selectedFashionModelId}
                  onFashionModelSelect={onFashionModelSelect}
                  creativeDirection={creativeDirection}
                  onCreativeDirectionChange={onCreativeDirectionChange}
                  format={format}
                  aspectRatio={aspectRatio}
                  onAspectRatioChange={onAspectRatioChange}
                  allowedAspects={allowedAspects}
                  quality={quality}
                  onQualityChange={onQualityChange}
                  isFree={isFree}
                />
              ) : (
                <TemplateGrid
                  templates={routeTemplates}
                  routeTitle={routeConfig.title}
                  selectedId={selectedTemplateId}
                  onSelect={onTemplateSelect}
                />
              )}
            </div>
            {/* Footer (Format + cost + Apply & Generate) */}
            <div className="flex-shrink-0 border-t border-surface-dark/20 bg-white/60 backdrop-blur-sm px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
              <StudioFormatSegment
                value={format}
                onChange={onFormatChange}
                disabled={designMode === 'template'}
              />
              <div className="flex items-center gap-3">
                <p className="text-xs font-bold text-dark-muted uppercase tracking-wider">
                  {QUALITY_LABEL_SHORT[quality]} · {creditCost} cr
                </p>
                {insufficient ? (
                  <button
                    type="button"
                    onClick={onUpgrade}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                  >
                    <i className="ri-lock-2-line" /> Upgrade — need {creditCost}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onGenerate}
                    disabled={!canGenerate}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? (
                      <><i className="ri-loader-4-line animate-spin" /> Generating…</>
                    ) : (
                      <>
                        <i className="ri-flashlight-fill" /> Apply &amp; Generate · {creditCost}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT — preview panel (mirror structure of the LEFT panel:
              top action bar + body + footer). Same white card chrome
              + matching 800px height so the panels read as twins. */}
          <div className="rounded-2xl bg-white border border-surface-dark/30 shadow-md shadow-dark/[0.04] flex flex-col overflow-hidden h-[800px]">
            {/* Select Product — top action bar of the RIGHT panel.
                Full-width pink CTA mirroring the Create/Templates pill
                on the LEFT panel. When a product is picked, the button
                shows the product thumb + name + Change/Clear inline. */}
            <div className="flex-shrink-0 p-3 border-b border-surface-dark/20">
              <StudioSelectProductBar
                selectedName={productName}
                selectedImageUrl={productImageUrl}
                onOpen={onOpenProductModal}
                onClear={onClearProduct}
              />
            </div>
            {/* Preview body — reuses PreviewCanvas from the legacy
                Product Shot layout. Handles all states (empty / product
                picked / generating / result / error) identically. flex-1
                + items-center centers the preview vertically so the
                panel height tracks the LEFT panel without leaving a
                pile of whitespace below the aspect-locked preview. */}
            <div className="relative flex-1 min-h-0 flex items-center justify-center p-5">
              {showEditsBadge && (
                <StudioEditsBehindBadge count={editsSinceGenerate} />
              )}
              <div className="w-full">
                <PreviewCanvas
                  aspectRatio={aspectRatio}
                  productImageUrl={productImageUrl}
                  generating={generating}
                  error={error}
                  aiResult={aiResult}
                  generatedImage={generatedImage}
                  frameCount={frameCount}
                  onBackToEdit={onBackToEdit}
                  onRetry={onRetry}
                  bare
                />
              </div>
            </div>
            {/* Footer — credits caption. Sits at the bottom of the panel
                so the right panel's footer slot aligns with the left
                panel's footer (Format + Apply & Generate). */}
            <div className="flex-shrink-0 border-t border-surface-dark/20 bg-white/60 backdrop-blur-sm px-5 py-3 text-center">
              <p className="text-[11px] text-dark-muted">
                {balance !== null
                  ? `${balance} credits available`
                  : 'Loading credits…'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <SelectProductModal
        open={productModalOpen}
        onClose={onCloseProductModal}
        savedProducts={savedProducts}
        selectedSavedProductId={selectedSavedProductId}
        onPickSaved={onPickSavedProduct}
        onUploadFile={onUploadFile}
      />
    </AppLayoutV2>
  );
}

// ===== StudioSelectProductBar =====
// Top action bar of the RIGHT panel. At rest: full-width pink CTA
// "Select Product →" mirroring the LEFT panel's Create/Templates pill.
// Once a product is picked: compact row showing thumb + name + Change
// + Clear, keeping the same height so the panel doesn't reflow.
function StudioSelectProductBar({
  selectedName, selectedImageUrl, onOpen, onClear,
}: {
  selectedName: string;
  selectedImageUrl: string | null;
  onOpen: () => void;
  onClear: () => void;
}) {
  if (!selectedImageUrl) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="w-full inline-flex items-center justify-between gap-2 px-5 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
      >
        <span className="inline-flex items-center gap-2">
          <i className="ri-add-line text-base" />
          Select Product
        </span>
        <i className="ri-arrow-right-line text-base" />
      </button>
    );
  }
  return (
    <div className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-full bg-surface/60 border border-surface-dark/30">
      <img
        src={selectedImageUrl}
        alt={selectedName || 'product'}
        className="w-9 h-9 rounded-full object-cover border border-surface-dark/40 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-[9px] font-bold uppercase tracking-wider text-primary leading-none mb-0.5">
          Product
        </p>
        <p className="text-sm font-bold text-dark truncate leading-tight">
          {selectedName || 'Untitled product'}
        </p>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white hover:bg-surface text-dark text-xs font-bold transition-colors cursor-pointer border border-surface-dark/30"
      >
        Change
      </button>
      <button
        type="button"
        onClick={onClear}
        title="Clear product"
        className="flex-shrink-0 w-7 h-7 rounded-full text-dark-muted hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center justify-center"
      >
        <i className="ri-close-line" />
      </button>
    </div>
  );
}

// ============================================================================
// CarouselTypePreStep — V3 Carousel Step 1 (Jun 14).
//
// Full-width page rendered BEFORE the Studio UI when category='carousel'
// and selectedCarouselType is null. 5 big type cards in 1 row with a
// short description per card + a 4-frame structural mock (sourced from
// the shared carouselTypes module so the standalone v1 fallback at
// /appv3/carousel/page.tsx renders the same cards).
//
// User clicks a card → onSelect fires → the page re-renders into the
// Studio UI below. There's intentionally no separate "Continue" click
// — picking a type is the trigger. (The wireframe showed a Continue
// button but with only 5 mutually-exclusive cards the pick IS the
// commit. Easy to add a separate Continue gate later if testing shows
// users want to preview their pick first.)
// ============================================================================

function CarouselTypePreStep({
  onSelect,
  onDashboard,
}: {
  onSelect: (id: CarouselTypeId) => void;
  onDashboard: () => void;
}) {
  return (
    <AppLayoutV2>
      <div className="w-full max-w-[1500px] mx-auto px-6 py-8">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-dark leading-tight">Carousel</h1>
            <p className="text-sm text-dark-muted mt-1">
              Pick a carousel type to start. Each one structures the 4 frames differently.
            </p>
          </div>
          <button
            type="button"
            onClick={onDashboard}
            className="text-xs font-semibold text-dark-muted hover:text-primary transition-colors"
          >
            ← Dashboard
          </button>
        </div>

        <div className="rounded-2xl bg-white border border-surface-dark/30 shadow-md shadow-dark/[0.04] p-6 min-h-[700px] flex flex-col">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-dark-muted mb-4">
            Select carousel type
          </p>
          {/* Center the card grid vertically — makes the picker feel
              "approachable" instead of crowding the top per user Jun 14.
              4×2 layout at 250px-per-card (user lock Jun 14, second
              pass) — 4 cards in row 1, 2 centered in row 2. Math:
              4 × 250 + 3 × 16 = 1048px container width. The 5th card
              gets `col-start-2` so row 2 reads as centered (cols 2-3)
              rather than orphaned left (cols 1-2). */}
          <div className="flex-1 flex items-center justify-center">
            <div className="grid grid-cols-4 gap-4 w-[1048px] max-w-full">
              {CAROUSEL_TYPES.map((type, i) => (
                <div
                  key={type.id}
                  className={i === 4 ? 'col-start-2' : undefined}
                >
                  <CarouselTypeCard
                    type={type}
                    selected={false}
                    onClick={() => onSelect(type.id)}
                    size="large"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayoutV2>
  );
}

// ============================================================================
// SocialPostIntentPreStep — V3 Social Post Step 1 (Jun 15).
//
// Full-width page rendered BEFORE the Studio UI when category='social-post'
// and selectedIntent is null. 6 intent cards in a 3×2 grid; picking one
// commits + transitions to the Studio (no separate Continue button —
// same UX as CarouselTypePreStep).
//
// Cards source from the shared socialPostIntents module.
// ============================================================================

function SocialPostIntentPreStep({
  onSelect,
  onDashboard,
}: {
  onSelect: (id: SocialPostIntentId) => void;
  onDashboard: () => void;
}) {
  return (
    <AppLayoutV2>
      <div className="w-full max-w-[1500px] mx-auto px-6 py-8">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-dark leading-tight">Social Post</h1>
            <p className="text-sm text-dark-muted mt-1">
              Pick an intent. AI shapes the ad's composition + headline + CTA to match.
            </p>
          </div>
          <button
            type="button"
            onClick={onDashboard}
            className="text-xs font-semibold text-dark-muted hover:text-primary transition-colors"
          >
            ← Dashboard
          </button>
        </div>

        <div className="rounded-2xl bg-white border border-surface-dark/30 shadow-md shadow-dark/[0.04] p-6 min-h-[700px] flex flex-col">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-dark-muted mb-4">
            Select intent
          </p>
          {/* 4×2 grid at 250px-per-card (Jun 15 v2 — was 3×2, felt
              cramped with too much side space). 6 intents fits as
              4 on row 1 + 2 centered (cols 2–3) on row 2 via
              `col-start-2` on card index 4. Math: 4 × 250 + 3 × 16
              = 1048px container — fills the panel without sprawling.
              Same shape as the Carousel Type picker's 4+1 layout. */}
          <div className="flex-1 flex items-center justify-center">
            <div className="grid grid-cols-4 gap-4 w-[1048px] max-w-full">
              {SOCIAL_POST_INTENTS.map((intent, i) => (
                <div
                  key={intent.id}
                  className={i === 4 ? 'col-start-2' : undefined}
                >
                  <IntentCard
                    intent={intent}
                    selected={false}
                    onClick={() => onSelect(intent.id)}
                    size="large"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayoutV2>
  );
}

// ===== StudioCreateTemplatesPill =====
// Top action bar of the LEFT panel — full-width pill toggle with a
// prominent pink "Create" default and a quieter "Templates" alternate.
// Sized to match the visual weight of the right panel's "Select Product"
// pink CTA so both panels feel like they have equivalent action bars.
function StudioCreateTemplatesPill({ designMode, onChange }: {
  designMode: DesignMode;
  onChange: (m: DesignMode) => void;
}) {
  // Templates renders FIRST (leftmost) — it's the default tab, and the
  // default belongs in the leading position.
  return (
    <div className="grid grid-cols-2 gap-2 p-1.5 rounded-full bg-surface/60">
      <button
        type="button"
        onClick={() => onChange('template')}
        className={`
          inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold transition-all cursor-pointer
          ${designMode === 'template'
            ? 'bg-primary text-white shadow-md shadow-primary/25'
            : 'bg-transparent text-dark-muted hover:text-dark'}
        `}
      >
        <i className="ri-layout-grid-line" /> Templates
      </button>
      <button
        type="button"
        onClick={() => onChange('ai')}
        className={`
          inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold transition-all cursor-pointer
          ${designMode === 'ai'
            ? 'bg-primary text-white shadow-md shadow-primary/25'
            : 'bg-transparent text-dark-muted hover:text-dark'}
        `}
      >
        <i className="ri-equalizer-line" /> Create
      </button>
    </div>
  );
}

// ===== StudioSectionHeader =====
// "Primary" header for the Model + Framing sections — these are the main
// selectors so they get a bigger, more prominent label that reads
// clearly as a section heading (not body text). Pink dot + larger
// tracking-wider label + count chip on the right.
function StudioSectionHeader({ label, count, countSuffix }: {
  label: string;
  count: number;
  countSuffix: string;
}) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-dark flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-primary" />
        {label}
      </h2>
      <span className="text-xs font-semibold text-dark-muted/80">
        {count} {countSuffix}
      </span>
    </div>
  );
}

// ===== StudioModelSection (fashion only) =====
// Horizontal scrolling strip — 17 models won't fit in a single row at
// panel width, so we scroll horizontally. Scrollbar is hidden via the
// `.studio-h-scroll` utility (CSS in index.css) so the strip reads as
// "swipe to see more" without a visible chrome. Tile sizing matches
// Framing tiles (no fixed-width — uses flex-basis from the parent's
// implicit min-content) so MODEL and FRAMING read as twin sections per
// the Figma mockup.
function StudioModelSection({ selectedId, onSelect }: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div>
      {/* StudioSectionHeader removed Jun 10 — the outer StudioSection
          wrapper (in StudioAccordion) already renders the section title
          + current value, so the inner header was a confusing duplicate. */}
      {/* studio-h-scroll: hidden scrollbar (CSS in index.css). gap + pb
          provide visual breathing room without showing the scroll chrome. */}
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 studio-h-scroll">
        <StudioModelTile
          isActive={selectedId === null}
          onClick={() => onSelect(null)}
          name="AI picks"
          bgClass="bg-primary-light"
          imageSrc={null}
        />
        {fashionModels.map((m) => (
          <StudioModelTile
            key={m.id}
            isActive={m.id === selectedId}
            onClick={() => onSelect(m.id)}
            name={m.name}
            bgClass="bg-surface"
            // fashionAssetUrl expects the FULL model object (not just
            // folder). Passing folder string gave undefined → broken
            // image src → empty placeholders.
            imageSrc={fashionAssetUrl(m, m.base)}
          />
        ))}
      </div>
    </div>
  );
}

function StudioModelTile({ isActive, onClick, name, bgClass, imageSrc }: {
  isActive: boolean;
  onClick: () => void;
  name: string;
  bgClass: string;
  imageSrc: string | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      // Width matches the effective Framing tile width on this panel
      // (~96-104px depending on viewport). Both PRIMARY sections (Model
      // + Framing) read as uniform per the Figma — same tile size, same
      // label treatment, just one scrolls horizontally because there
      // are 17 models vs 6 framings.
      className="group flex flex-col items-stretch gap-1.5 w-[96px] flex-shrink-0 cursor-pointer"
    >
      <div className={`
        relative aspect-square rounded-xl overflow-hidden ${bgClass}
        border-2 transition-all
        ${isActive
          ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
          : 'border-transparent group-hover:border-primary/30 group-hover:shadow-md'}
      `}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="ri-sparkling-2-fill text-3xl text-primary/40" />
          </div>
        )}
        {isActive && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
            <i className="ri-check-line text-xs" />
          </div>
        )}
      </div>
      <p className={`
        text-xs leading-tight text-center truncate px-1
        ${isActive ? 'text-primary font-bold' : 'text-dark-muted font-medium'}
      `}>
        {name}
      </p>
    </button>
  );
}

// ===== StudioFramingSection =====
function StudioFramingSection({
  framings, selected, onSelect, disabledByCarousel, carouselFramings,
}: {
  framings: readonly CreativeFraming[];
  selected: CreativeFraming;
  onSelect: (v: CreativeFraming) => void;
  disabledByCarousel: boolean;
  carouselFramings: readonly CreativeFraming[];
}) {
  return (
    <div>
      {/* StudioSectionHeader removed Jun 10 — the outer StudioSection
          wrapper (in StudioAccordion) already renders the section title
          + current value, so the inner header was a confusing duplicate. */}
      {/* 3-col base, 6-col on wide. Tile size matches the tab-content
          image grids — they're both fine-tune selectors now (sitting in
          the same stacked-section list). */}
      <div className={`
        grid gap-3 grid-cols-3 sm:grid-cols-6
        ${disabledByCarousel ? 'opacity-50 pointer-events-none' : ''}
      `}>
        {framings.map((f) => (
          <StudioFramingTile
            key={f}
            name={f}
            imageSrc={`/filter-previews/${studioFramingSlug(f)}.webp`}
            isActive={f === selected}
            onClick={() => onSelect(f)}
            size="primary"
          />
        ))}
      </div>
      {disabledByCarousel && (
        <p className="text-xs text-dark-muted leading-relaxed">
          Carousel uses 4 auto-picked framings (tight → wide):{' '}
          <span className="font-semibold text-dark">
            {carouselFramings.join(' · ')}
          </span>
        </p>
      )}
    </div>
  );
}

function studioFramingSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\+/g, 'plus')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Two sizes:
//   - 'primary' (default): used by Framing — bigger tiles, bigger label
//   - 'secondary': used by tab-content image grids (Vibe / Light /
//     Background) — smaller tiles so they read as "fine-tune" not "main"
function StudioFramingTile({ name, imageSrc, isActive, onClick, size = 'secondary' }: {
  name: string;
  imageSrc: string;
  isActive: boolean;
  onClick: () => void;
  size?: 'primary' | 'secondary';
}) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [imageSrc]);
  const isPrimary = size === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      title={name}
      className="group flex flex-col items-stretch gap-1.5 cursor-pointer"
    >
      <div className={`
        relative aspect-square rounded-xl overflow-hidden bg-surface
        border-2 transition-all
        ${isActive
          ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
          : 'border-transparent group-hover:border-primary/30 group-hover:shadow-md'}
      `}>
        {!imgFailed ? (
          <img
            src={imageSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgFailed(true)}
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-dark-muted text-center px-1">
            {name}
          </div>
        )}
        {isActive && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
            <i className="ri-check-line text-xs" />
          </div>
        )}
      </div>
      <p className={`
        leading-tight text-center truncate px-0.5
        ${isPrimary ? 'text-sm' : 'text-xs'}
        ${isActive ? 'text-primary font-bold' : 'text-dark-muted font-medium'}
      `}>
        {name}
      </p>
    </button>
  );
}

// ===== StudioTabBar =====
// Per the Figma cleanup: NO outer pill container — tabs sit directly on
// the FINE-TUNE block's tinted bg. Active tab gets a rounded pink fill;
// inactive tabs are plain text with the value beneath in muted ink.
// Reads as a row of buttons, not as a segmented control inside a chip.
function StudioTabBar({
  activeTab, onChange, creativeDirection, aspectRatio, quality,
}: {
  activeTab: StudioTab;
  onChange: (t: StudioTab) => void;
  creativeDirection: CreativeDirection;
  aspectRatio: ProtoAspect;
  quality: Quality;
}) {
  const tabs: Array<{ id: StudioTab; label: string; value: string }> = [
    { id: 'look', label: 'Look', value: creativeDirection.mood },
    { id: 'light', label: 'Light', value: creativeDirection.light },
    { id: 'background', label: 'Background', value: creativeDirection.background },
    { id: 'brand', label: 'Brand', value: creativeDirection.brand_presence },
    { id: 'output', label: 'Output', value: `${aspectRatio} · ${QUALITY_LABEL_SHORT[quality]}` },
  ];
  return (
    <div className="flex gap-1 items-stretch">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`
              flex-1 px-3 py-2 rounded-full text-xs font-bold transition-all cursor-pointer
              flex flex-col items-center justify-center gap-0
              ${isActive
                ? 'bg-primary text-white shadow-md shadow-primary/25'
                : 'bg-transparent text-dark-muted hover:text-dark'}
            `}
          >
            <span className="leading-tight">{tab.label}</span>
            <span className={`
              text-[10px] leading-tight font-normal truncate max-w-full px-1
              ${isActive ? 'text-white/85' : 'text-dark-muted/70'}
            `}>
              {tab.value}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ===== StudioTabContent =====
function StudioTabContent({
  activeTab, creativeDirection, onCreativeDirectionChange,
  aspectRatio, onAspectRatioChange, allowedAspects,
  quality, onQualityChange, isFree, showTextDensity,
}: {
  activeTab: StudioTab;
  creativeDirection: CreativeDirection;
  onCreativeDirectionChange: (cd: CreativeDirection) => void;
  aspectRatio: ProtoAspect;
  onAspectRatioChange: (v: ProtoAspect) => void;
  allowedAspects: readonly ProtoAspect[];
  quality: Quality;
  onQualityChange: (v: Quality) => void;
  isFree: boolean;
  showTextDensity: boolean;
}) {
  const setField = <K extends keyof CreativeDirection>(key: K, value: CreativeDirection[K]) => {
    onCreativeDirectionChange({ ...creativeDirection, [key]: value });
  };

  if (activeTab === 'look') {
    return (
      <StudioTabImageGrid
        title="Vibe"
        values={['Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial']}
        selected={creativeDirection.mood}
        onSelect={(v) => setField('mood', v as CreativeDirection['mood'])}
      />
    );
  }
  if (activeTab === 'light') {
    return (
      <StudioTabImageGrid
        title="Lighting"
        values={['Soft daylight', 'Golden hour', 'Studio bright', 'Moody low-key']}
        selected={creativeDirection.light}
        onSelect={(v) => setField('light', v as CreativeDirection['light'])}
      />
    );
  }
  if (activeTab === 'background') {
    return (
      <StudioTabImageGrid
        title="Background"
        values={['Studio', 'Lifestyle', 'Surface', 'Outdoors', 'Abstract']}
        selected={creativeDirection.background}
        onSelect={(v) => setField('background', v as CreativeDirection['background'])}
      />
    );
  }
  if (activeTab === 'brand') {
    return (
      <div className="space-y-3">
        <StudioPillRow
          label="Brand colors"
          values={['Off', 'On']}
          selected={creativeDirection.brand_presence}
          onSelect={(v) => setField('brand_presence', v as CreativeDirection['brand_presence'])}
        />
        {/* Jun 22 — Logo pill removed. See FashionStudioLayout footer
            chip removal for context. */}
        {showTextDensity && (
          <StudioPillRow
            label="Text density"
            values={['None', 'Text only', 'Text + CTA']}
            selected={creativeDirection.text_density ?? 'Text + CTA'}
            onSelect={(v) => setField('text_density', v as CreativeDirection['text_density'])}
          />
        )}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <StudioPillRow
        label="Aspect ratio"
        values={[...allowedAspects]}
        selected={aspectRatio}
        onSelect={(v) => onAspectRatioChange(v as ProtoAspect)}
      />
      <StudioPillRow
        label="Quality"
        values={['low', 'medium', 'high']}
        labels={['Low (1cr)', 'Med (2cr)', 'High (4cr)']}
        selected={quality}
        onSelect={(v) => onQualityChange(v as Quality)}
        lockedValues={isFree ? ['medium', 'high'] : []}
      />
    </div>
  );
}

function StudioTabImageGrid({ values, selected, onSelect }: {
  title: string;  // accepted but unused — tab pill above carries the active value now
  values: readonly string[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  // No section header — the tab pill above already shows label + active
  // value ("Look · Editorial"). Skipping the redundant title keeps the
  // fine-tune block visually compact.
  return (
    // Tighter, denser grid than Framing's — these are fine-tune
    // knobs, not primary picks. 5-7 cols means each tile is ~60-75px
    // (Framing tiles are ~140px). Visually subordinate at a glance.
    <div className="grid gap-2 grid-cols-5 sm:grid-cols-6 lg:grid-cols-7">
      {values.map((v) => (
        <StudioFramingTile
          key={v}
          name={v}
          imageSrc={`/filter-previews/${studioFramingSlug(v)}.webp`}
          isActive={v === selected}
          onClick={() => onSelect(v)}
          size="secondary"
        />
      ))}
    </div>
  );
}

function StudioPillRow({ label, values, labels, selected, onSelect, lockedValues = [] }: {
  label: string;
  values: readonly string[];
  labels?: readonly string[];
  selected: string;
  onSelect: (v: string) => void;
  lockedValues?: readonly string[];
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex-shrink-0 w-32 text-xs font-bold text-dark uppercase tracking-wider">
        {label}
      </span>
      <div className="flex-1 grid auto-cols-fr grid-flow-col gap-1 p-1 rounded-full bg-surface/70">
        {values.map((v, idx) => {
          const isActive = v === selected;
          const isLocked = lockedValues.includes(v);
          const display = labels?.[idx] ?? v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => !isLocked && onSelect(v)}
              disabled={isLocked}
              title={isLocked ? 'Upgrade to unlock' : display}
              className={`
                inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap
                ${isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                ${isActive
                  ? 'bg-primary text-white shadow-sm shadow-primary/25'
                  : 'bg-transparent text-dark-muted hover:text-dark'}
              `}
            >
              {isLocked && <i className="ri-lock-2-line" />}
              {display}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// StudioAccordion — DEPRECATED NAME, kept as the export identifier so the
// call site stays untouched. This is now a STACKED SECTION LIST (not an
// accordion): every section's body renders always, no collapse/expand.
// User direction Jun 10: "leave it all open all the time".
//
// Sections: Model (fashion only) · Framing · Look · Light · Background ·
// Brand · Output. Each has a card header (icon + title + current value)
// and an always-visible body. The whole list scrolls inside the LEFT
// panel's 800px height.
// ============================================================================

interface StudioAccordionProps {
  routeConfig: RouteConfig;
  showModelSection: boolean;
  selectedFashionModelId: string | null;
  onFashionModelSelect: (id: string | null) => void;
  creativeDirection: CreativeDirection;
  onCreativeDirectionChange: (cd: CreativeDirection) => void;
  format: Format;
  aspectRatio: ProtoAspect;
  onAspectRatioChange: (v: ProtoAspect) => void;
  allowedAspects: readonly ProtoAspect[];
  quality: Quality;
  onQualityChange: (v: Quality) => void;
  isFree: boolean;
}

function StudioAccordion({
  routeConfig, showModelSection,
  selectedFashionModelId, onFashionModelSelect,
  creativeDirection, onCreativeDirectionChange,
  format, aspectRatio, onAspectRatioChange, allowedAspects,
  quality, onQualityChange, isFree,
}: StudioAccordionProps) {
  // Selected-model display name for the Model section header.
  const selectedModelName = selectedFashionModelId
    ? (fashionModels.find((m) => m.id === selectedFashionModelId)?.name ?? 'Picked')
    : 'AI picks';

  const setCD = <K extends keyof CreativeDirection>(key: K, value: CreativeDirection[K]) => {
    onCreativeDirectionChange({ ...creativeDirection, [key]: value });
  };

  return (
    <div className="space-y-2.5">
      {showModelSection && (
        <StudioSection
          icon="ri-user-line"
          title="Model"
          value={selectedModelName}
        >
          <StudioModelSection
            selectedId={selectedFashionModelId}
            onSelect={onFashionModelSelect}
          />
        </StudioSection>
      )}

      <StudioSection
        icon="ri-camera-lens-line"
        title="Framing"
        value={format === 'carousel' ? '4 auto-picked' : creativeDirection.framing}
      >
        <StudioFramingSection
          framings={routeConfig.framings}
          selected={creativeDirection.framing}
          onSelect={(v) => setCD('framing', v)}
          disabledByCarousel={format === 'carousel'}
          carouselFramings={routeConfig.carouselFramings}
        />
      </StudioSection>

      <StudioSection
        icon="ri-sparkling-2-line"
        title="Look"
        value={creativeDirection.mood}
      >
        <StudioTabImageGrid
          title="Vibe"
          values={['Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial']}
          selected={creativeDirection.mood}
          onSelect={(v) => setCD('mood', v as CreativeDirection['mood'])}
        />
      </StudioSection>

      <StudioSection
        icon="ri-sun-line"
        title="Light"
        value={creativeDirection.light}
      >
        <StudioTabImageGrid
          title="Lighting"
          values={['Soft daylight', 'Golden hour', 'Studio bright', 'Moody low-key']}
          selected={creativeDirection.light}
          onSelect={(v) => setCD('light', v as CreativeDirection['light'])}
        />
      </StudioSection>

      <StudioSection
        icon="ri-landscape-line"
        title="Background"
        value={creativeDirection.background}
      >
        <StudioTabImageGrid
          title="Background"
          values={['Studio', 'Lifestyle', 'Surface', 'Outdoors', 'Abstract']}
          selected={creativeDirection.background}
          onSelect={(v) => setCD('background', v as CreativeDirection['background'])}
        />
      </StudioSection>

      <StudioSection
        icon="ri-shield-star-line"
        title="Brand"
        value={creativeDirection.brand_presence}
      >
        <div className="space-y-3">
          <StudioPillRow
            label="Brand colors"
            values={['Off', 'On']}
            selected={creativeDirection.brand_presence}
            onSelect={(v) => setCD('brand_presence', v as CreativeDirection['brand_presence'])}
          />
          {/* Jun 22 — Logo pill removed. See StudioSettingChip removal
              comment in FashionStudioLayout for context. */}
          {routeConfig.showTextDensity && (
            <StudioPillRow
              label="Text density"
              values={['None', 'Text only', 'Text + CTA']}
              selected={creativeDirection.text_density ?? 'Text + CTA'}
              onSelect={(v) => setCD('text_density', v as CreativeDirection['text_density'])}
            />
          )}
        </div>
      </StudioSection>

      <StudioSection
        icon="ri-layout-grid-2-line"
        title="Output"
        value={`${aspectRatio} · ${QUALITY_LABEL_SHORT[quality]}`}
      >
        <div className="space-y-3">
          <StudioPillRow
            label="Aspect ratio"
            values={[...allowedAspects]}
            selected={aspectRatio}
            onSelect={(v) => onAspectRatioChange(v as ProtoAspect)}
          />
          <StudioPillRow
            label="Quality"
            values={['low', 'medium', 'high']}
            labels={['Low (1cr)', 'Med (2cr)', 'High (4cr)']}
            selected={quality}
            onSelect={(v) => onQualityChange(v as Quality)}
            lockedValues={isFree ? ['medium', 'high'] : []}
          />
        </div>
      </StudioSection>
    </div>
  );
}

// One section in the always-open stacked list. Header (icon + title +
// current value) sits on a slightly-tinted strip; body renders directly
// below. No click handler — sections are never collapsed.
function StudioSection({
  icon, title, value, children,
}: {
  icon: string;
  title: string;
  value: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-surface-dark/30 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-3.5 py-2.5 border-b border-surface-dark/20 bg-surface/40">
        <i className={`${icon} text-base text-primary`} />
        <span className="flex-1 text-sm font-bold uppercase tracking-[0.06em] text-dark">
          {title}
        </span>
        <span className="text-xs font-semibold text-primary truncate max-w-[180px]">
          {value}
        </span>
      </div>
      <div className="px-3.5 pt-3 pb-3.5">
        {children}
      </div>
    </div>
  );
}

// ===== StudioFormatSegment =====
function StudioFormatSegment({ value, onChange, disabled }: {
  value: Format;
  onChange: (v: Format) => void;
  disabled: boolean;
}) {
  return (
    <div className={`
      inline-flex items-center gap-0.5 p-1 rounded-full bg-surface/70 border border-surface-dark/20
      ${disabled ? 'opacity-50 pointer-events-none' : ''}
    `}>
      {(['single', 'carousel'] as const).map((opt) => {
        const isActive = opt === value;
        const label = opt === 'single' ? 'Single' : 'Carousel · 4';
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`
              inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer
              ${isActive
                ? 'bg-white text-dark shadow-sm border border-surface-dark/30'
                : 'bg-transparent text-dark-muted hover:text-dark'}
            `}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ===== StudioPreviewPane =====
function StudioPreviewPane({
  aspectRatio, productImageUrl, generating, error, aiResult,
  generatedImage, frameCount, onBackToEdit, onRetry,
  editsBehind, headline,
}: {
  aspectRatio: ProtoAspect;
  productImageUrl: string | null;
  generating: boolean;
  error: string | null;
  aiResult: WizardAiGenerateResult | null;
  generatedImage: string | null;
  frameCount: number;
  onBackToEdit: () => void;
  onRetry: () => void;
  editsBehind: number;
  headline: string;
}) {
  const aspectClass = ASPECT_TW[aspectRatio];

  if (aiResult || (generating && !generatedImage && !error)) {
    return (
      <div className="relative rounded-2xl border border-surface-dark/30 bg-white p-4 shadow-md">
        {editsBehind > 0 && <StudioEditsBehindBadge count={editsBehind} />}
        <AiResultViewer
          result={aiResult}
          loading={generating}
          expectedFrameCount={frameCount}
          error={error}
          onRetry={onRetry}
          onBack={onBackToEdit}
          hideCopyBlock
        />
      </div>
    );
  }

  if (generatedImage || error) {
    return (
      <div className="relative rounded-2xl border border-surface-dark/30 bg-white p-4 shadow-md">
        {editsBehind > 0 && <StudioEditsBehindBadge count={editsBehind} />}
        <div className={`relative w-full ${aspectClass} max-h-[70vh] mx-auto rounded-xl overflow-hidden bg-white`}>
          {generatedImage ? (
            <img
              src={generatedImage}
              alt="Generated"
              className="absolute inset-0 w-full h-full object-contain bg-surface"
            />
          ) : error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 border-2 border-red-200 rounded-xl gap-2 p-4 text-center">
              <i className="ri-error-warning-line text-3xl text-red-600" />
              <p className="text-sm font-bold text-red-800">Generation failed</p>
              <p className="text-xs text-red-700 max-w-sm">{error}</p>
              <button
                type="button"
                onClick={onRetry}
                className="mt-1 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold cursor-pointer"
              >
                <i className="ri-refresh-line" /> Try again
              </button>
            </div>
          ) : null}
        </div>
        {generatedImage && (
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={onBackToEdit}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line" /> Back
            </button>
            <a
              href={generatedImage}
              download="output.png"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
            >
              <i className="ri-download-2-line" /> Download
            </a>
          </div>
        )}
      </div>
    );
  }

  // Placeholder mock — AURA / silhouette / "New Season" / Shop now.
  return (
    <div className="relative rounded-2xl border border-surface-dark/30 overflow-hidden shadow-md bg-gradient-to-br from-primary-light via-surface to-surface-dark/40">
      {editsBehind > 0 && <StudioEditsBehindBadge count={editsBehind} />}
      <div className={`relative w-full ${aspectClass} max-h-[70vh] mx-auto`}>
        <p className="absolute top-3 left-4 z-10 text-xs font-bold uppercase tracking-[0.3em] text-dark/40">
          AURA
        </p>
        <div className="absolute inset-0 flex items-center justify-center p-6">
          {productImageUrl ? (
            <img
              src={productImageUrl}
              alt=""
              className="max-w-[60%] max-h-[60%] object-contain opacity-80 drop-shadow-lg"
            />
          ) : (
            <i className="ri-image-2-line text-7xl text-dark/15" />
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 z-10">
          <h3 className="text-2xl font-bold text-dark mb-2 leading-tight drop-shadow-sm">
            {headline}
          </h3>
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary text-white text-xs font-bold cursor-not-allowed"
          >
            Shop now
          </button>
        </div>
      </div>
    </div>
  );
}

function StudioEditsBehindBadge({ count, corner = 'right' }: {
  count: number;
  /** 'left' on the result takeover (the Edit button owns top-right there). */
  corner?: 'left' | 'right';
}) {
  return (
    <div className={`absolute top-3 ${corner === 'left' ? 'left-3' : 'right-3'} z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-dark/80 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider shadow-sm`}>
      <i className="ri-time-line" />
      Preview is {count} edit{count === 1 ? '' : 's'} behind
    </div>
  );
}

// ===========================================================================
// FASHION STUDIO LAYOUT (Jun 11)
// ===========================================================================
// Replaces the legacy stacked-section Studio for the Fashion route only.
//
// Structure (per Figma reference):
//   LEFT panel (h-[800px]):
//     - Top tabs: Templates / Create (StudioCreateTemplatesPill reused)
//     - Body (Create mode):
//         MODEL big 4-col wrapping grid (scrollable) — hero of the layout
//         4 COMPACT PICKERS row (pinned to bottom): FRAMING / LIGHT /
//           BACKGROUND / VIBE — each is a single tile showing the current
//           selection. Click → popover opens ABOVE the tile with all
//           options as image tiles.
//     - Body (Templates mode): existing TemplateGrid
//
//   RIGHT panel (h-[800px]):
//     - Preview body (flex-1): empty state OR PreviewCanvas. Click anywhere
//       on the empty state OR the floating "Change product" button (top-right
//       corner) opens the SelectProductModal.
//     - Footer: two outline pills (OUTPUT + BRAND) + filled pink GENERATE
//       button (290px). Click each pill → popover opens ABOVE with the
//       relevant controls (Output = format+aspect+quality;
//       Brand = brand_presence+show_logo+text_density).
//
// Single popover at a time via `popover` union state. ESC + click-outside
// + explicit X button all close. Click-outside uses the picker/pill
// container as the boundary so the trigger itself doesn't close it on
// re-click — toggle works correctly.
//
// CSS: Figma colors/fonts are reference only. Everything below uses the
// project's existing Tailwind tokens (primary, surface, dark-muted, etc.)
// and the existing typography scale (no `text-[Npx]`).
// ===========================================================================

type FashionStudioPopover =
  | 'framing'
  | 'light'
  | 'background'
  | 'vibe'
  // Footer setting chips (Jun 12 v3 — each setting is its own labeled
  // chip instead of two combined "Output"/"Brand" pills).
  | 'aspect'
  | 'format'
  | 'quality'
  | 'logo'
  | 'text'
  | 'brand'
  | 'retry'
  | null;

const STUDIO_VIBES = ['Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial'] as const;
const STUDIO_LIGHTS = ['Soft daylight', 'Golden hour', 'Studio bright', 'Moody low-key'] as const;
const STUDIO_BACKGROUNDS = ['Studio', 'Lifestyle', 'Surface', 'Outdoors', 'Abstract'] as const;

function FashionStudioLayout(props: StudioLayoutRenderProps) {
  // Create studio is desktop-only on mobile (< lg) — its arrangement/pose
  // rows + light/background/vibe filter popovers need width they don't have on
  // a phone (the popovers open off-screen). isNarrow gates the Create tab body
  // to a "best on desktop" notice with a jump to Templates.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const {
    routeConfig, navigate, designMode, onDesignModeChange,
    creativeDirection, onCreativeDirectionChange,
    aspectRatio, onAspectRatioChange, allowedAspects,
    quality, onQualityChange, isFree,
    format, onFormatChange,
    selectedFashionModelId, onFashionModelSelect,
    selectedPattern, onPatternSelect, onPoseSelect,
    selectedAiTemplateIds, onAiTemplateSelect,
    lockedTemplateId, onClearLockedTemplate,
    lockedRecipeId, onClearLockedRecipe,
    selectedTemplateId, onTemplateSelect, routeTemplates,
    productName, productImageUrl, productDomain,
    fashionProductMismatch,
    onOpenProductModal,
    generating, error, aiResult, generatedImage, frameCount,
    onBackToEdit, onRetry,
    canGenerate, creditCost, insufficient, balance,
    onGenerate, onUpgrade, matrixImageCount, matrixActive,
    onRegenerateWithQuality, onSaveToCreatives, saveState,
    onScheduleShare, onDownloadResult, downloadingResult,
    editsSinceGenerate,
    productModalOpen, onCloseProductModal, savedProducts,
    selectedSavedProductId, onPickSavedProduct, onUploadFile,
    // Inline product picker (Jun 11)
    productPickerOpen, onProductPickerContinue, onProductEdit,
    onStepUploadCallback, onScrapeSuccess,
    // V3 Carousel (Jun 14) — type + Edit handler. Null on non-carousel
    // routes (summary bar doesn't render).
    selectedCarouselType, onEditCarouselType,
    // V3 Social Post (Jun 15) — intent + Edit handler. Null on non-
    // social-post routes (summary bar doesn't render).
    selectedIntent, onEditIntent,
    // V3 Social Post (Jun 15 v2) — master Filters toggle + Shot type.
    // Only consumed by the social-post branch of the filter row + the
    // Generate handler.
    filtersEnabled, onFiltersEnabledChange,
    socialPostShot, onSocialPostShotChange,
    // Finishing surface (Jun 12 v2 — render panel = edit screen)
    overlayPost, onOverlaySaved, flatEditorPost, editorForked, onEditorFork, onRequestAiText, productShotFinish,
    displayFrames, renderedAspect,
    textOverlays, onTextOverlaysChange,
    logoOverlays, onLogoOverlaysChange,
    brandLogoUrl, brandColors, suggestedFrames,
    activeFrameIdx, onActiveFrameChange,
    editPrompt, onEditPromptChange, promptEditing, editError, onPromptEdit,
    onSyncCreative, onManualSave, creativeDirty,
  } = props;

  const [popover, setPopover] = useState<FashionStudioPopover>(null);

  // ── Fixed 3-panel mobile flow (Jul 2) ───────────────────────────────
  // Mobile stacks THREE fixed panels that never reorder:
  //   Panel 1 · Product (top)  → Panel 2 · Templates (mid) → Panel 3 · Generate (bottom)
  // Movement is page-scroll only (anchors), the panels stay put:
  //   • Product picker "Continue" → scroll to the Templates panel;
  //   • Templates "Continue" → scroll to the Generate panel.
  // No-op on desktop (matchMedia gates to < xl; panels are side-by-side and
  // the generate controls live in the right-panel footer).
  const mobileTemplatesRef = useRef<HTMLDivElement>(null);
  const mobileGenerateRef = useRef<HTMLDivElement>(null);
  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1279px)').matches;
  const scrollToMobileGenerate = () => {
    if (isMobileViewport()) {
      requestAnimationFrame(() =>
        mobileGenerateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    }
  };

  // Product picked ("Continue" → productPickerOpen true→false): bring the
  // now-unlocked Templates panel up.
  const prevPickerOpenRef = useRef(productPickerOpen);
  useEffect(() => {
    if (prevPickerOpenRef.current && !productPickerOpen && isMobileViewport()) {
      requestAnimationFrame(() =>
        mobileTemplatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    }
    prevPickerOpenRef.current = productPickerOpen;
  }, [productPickerOpen]);

  // Templates panel lock (Jun 14) — keep the Templates body greyed-out
  // until the user has BOTH picked a product AND clicked Continue on
  // the right-side picker. Without this, users can pick a template
  // before settling on a product → confusing flow + the Templates'
  // "Recommended for you" domain-matched recs would be wrong for the
  // wrong product. Locked covers both branches (lockedTemplateId deep-
  // link single view AND the full grid). Create tab is intentionally
  // NOT locked — filter exploration is OK before product is settled.
  const templatesLocked = !productImageUrl || productPickerOpen;

  // Primary template (first of the multi-select) — drives the single-image
  // confirm preview + the single-render path. The matrix (>1 template or
  // >1 product) renders the deck preview instead (see below).
  const selectedAiTemplateId = selectedAiTemplateIds[0] ?? null;
  // Carousel is blocked only for Fashion's single-frame Recreate templates.
  // V3 Carousel (Jun 14) — carousel reads from the same AI Template
  // library as product-shot (no Recreate templates). Without this
  // include, recreateTemplateMode → TRUE for carousel, effectiveFormat
  // gets forced to 'single' (footer reads "SINGLE · 1 CR" instead of
  // "CAROUSEL · 4 CR"), AND Generate dispatches through the Recreate
  // template path which tries to load `templates/null/frame1_empty.png`
  // because selectedTemplateId is null on the AI-template path.
  const aiTemplateRoute = routeConfig.category === 'product-shot'
    || routeConfig.category === 'product-swap'
    || routeConfig.category === 'carousel';
  const recreateTemplateMode = designMode === 'template' && !aiTemplateRoute;

  // ESC closes any open popover
  useEffect(() => {
    if (!popover) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopover(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popover]);

  // displayFrames covers the matrix path (sets neither aiResult nor
  // generatedImage — the finishing editor renders from displayFrames).
  const hasResult = !!(aiResult || generatedImage) || displayFrames.length > 0;
  const hasMedia = !!productImageUrl;

  // ---------- Render accordion (Jun 12 v2) ----------
  // The render no longer lives inside the right panel — it gets its own
  // FULL-WIDTH section below the two design panels (better use of the
  // ~1450px for singles, and carousels lay out 4-across). Exactly one
  // section is expanded at a time:
  //   • renderOpen=true  → design panels collapse to a slim summary bar
  //     (template thumb + product thumb + "Back to edit" overlay); the
  //     render panel below is expanded (generating / error / result).
  //   • renderOpen=false → design panels expanded; if a render exists
  //     (or is in flight) it collapses to its own slim bar below
  //     ("Render ready · view") so the result is never lost.
  // Generate auto-opens the render section. Back to edit only collapses
  // it — the result (already auto-saved) stays one click away.
  const [renderOpen, setRenderOpen] = useState(false);
  const showRenderSection = generating || hasResult || !!error;
  const designCollapsed = renderOpen && showRenderSection;
  // Product Shot finishes in CreativeEditor, which needs a saved post id. Between
  // "render done" and "auto-saved" we keep the generating takeover up (rather
  // than flashing StudioFinishEditor) so it's ONE editor per run. On save error
  // we fall through to StudioFinishEditor (which has its own save retry).
  const finishSaving = productShotFinish && hasResult && !generating
    && saveState !== 'saved' && saveState !== 'error';
  // Product Shot render/edit takes over the whole content area (full-bleed,
  // full-height below the persistent header) so the editor + takeover use every
  // pixel like /creatives:id. True while generating OR editing a product shot.
  const editorMode = productShotFinish && renderOpen;
  // Viewport-fit heights: the design panels + editor fill the space below the
  // sticky header instead of a fixed 800px (which buried the Generate button at
  // 1500×800). Floor + cap keep them sane on very short / very tall screens.
  // One height for the design panels, the loading panel, and the editor panel so
  // they're all the same size (and the same width, since all sit in the same
  // max-w-[1500px] content box as the Template + Product picker panels).
  const fitPanelH = 'h-[calc(100vh-128px)] min-h-[520px] max-h-[860px]';
  // The exact panel chrome used by the generating/loading takeover — reused for
  // the editor so they're visually identical (white + 4px halo + drop shadow).
  const loadingPanelClass = 'rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)]';
  useEffect(() => {
    if (generating) setRenderOpen(true);
  }, [generating]);
  const renderSectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!renderOpen || !showRenderSection) return;
    const t = window.setTimeout(() => {
      renderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [renderOpen, showRenderSection]);
  // Back to edit also flatten-syncs the saved creative (fire-and-forget;
  // no-op while clean) so My Creatives never holds a stale composite.
  const handleBackToEdit = () => {
    onSyncCreative();
    setRenderOpen(false);
  };
  // First frame of the current base — thumbnail for the collapsed render
  // bar. displayFrames reflects prompt-edits; falls back to the raw
  // template-path result during the brief window before frames are set.
  const resultThumbSrc = displayFrames[0]
    ?? (aiResult?.frames?.length
      ? `data:image/png;base64,${aiResult.frames[0]}`
      : generatedImage);

  // Selected-template preview resolution (Jun 12) — drives the
  // confirmation view in the right panel: template sample as the hero +
  // the picked product inset, so the user sees exactly what's about to
  // be combined BEFORE spending credits on Generate. AI templates
  // (Product Shot + In Hand) resolve from the static sample library;
  // Fashion's Recreate templates from their previewImage.
  const selectedTemplatePreview = useMemo((): { name: string; imageUrl: string | null } | null => {
    // Fashion (Jun 14) — when a model is picked on the MODEL grid,
    // treat it like a template pick so the right panel shows the
    // bigger TemplateConfirmPreview (model image as the "scene
    // authority" + product chip overlay). Without this, fashion has
    // no template surface at all and the right panel sits in the
    // empty PreviewCanvas state even after the user picks a model.
    if (routeConfig.category === 'fashion' && selectedFashionModelId) {
      const m = fashionModels.find((x) => x.id === selectedFashionModelId);
      if (m) return { name: m.name, imageUrl: fashionAssetUrl(m, m.base) };
    }
    if (designMode !== 'template') return null;
    // AI Magic freestyle (Jun 12) — no sample image; the confirm view
    // renders its gradient hero instead.
    if (selectedAiTemplateId === AI_FREESTYLE_TEMPLATE_ID) {
      return { name: 'AI Magic', imageUrl: null };
    }
    if (selectedAiTemplateId) {
      const t = PRODUCT_SHOT_AI_TEMPLATES.find((x) => x.id === selectedAiTemplateId);
      if (t) return { name: t.name, imageUrl: aiTemplateSamplePath(t) };
    }
    if (selectedTemplateId) {
      const t = routeTemplates.find((x) => x.id === selectedTemplateId);
      if (t) return { name: t.name, imageUrl: t.previewImage };
    }
    return null;
  }, [
    designMode, selectedAiTemplateId, selectedTemplateId, routeTemplates,
    routeConfig.category, selectedFashionModelId,
  ]);

  // Resolved list of ALL picked AI templates (for the matrix confirm grid).
  // Excludes the freestyle sentinel (no sample to show). Each carries its
  // sample image + name. Order = pick order (matches the badge numbers).
  const selectedAiTemplatesResolved = useMemo(() => {
    return selectedAiTemplateIds
      .filter((id) => id !== AI_FREESTYLE_TEMPLATE_ID)
      .map((id) => {
        const t = PRODUCT_SHOT_AI_TEMPLATES.find((x) => x.id === id);
        return t ? { id, name: t.name, imageUrl: aiTemplateSamplePath(t) } : null;
      })
      .filter((x): x is { id: string; name: string; imageUrl: string } => x !== null);
  }, [selectedAiTemplateIds]);
  // Effective format in the footer (Recreate templates can't carousel).
  const footerFormat: Format = recreateTemplateMode ? 'single' : format;
  // The DECK preview shows whenever 2+ AI templates are picked — regardless
  // of format. Single → N independent images; Carousel → 1 carousel whose
  // 4 frames cycle the templates.
  const showDeck = designMode === 'template' && aiTemplateRoute
    && selectedAiTemplatesResolved.length > 1 && !!productImageUrl;
  // Confirmation shows whenever template + product are both picked. The
  // generating / result / error states all live in the full-width render
  // section below (Jun 12 v2) — the right panel stays a pure design
  // surface, so the confirm view is also what the user returns to when
  // they click "Back to edit" with a render collapsed underneath.
  const showTemplateConfirm = !!selectedTemplatePreview && !!productImageUrl;

  const setCD = <K extends keyof CreativeDirection>(key: K, value: CreativeDirection[K]) => {
    onCreativeDirectionChange({ ...creativeDirection, [key]: value });
    setPopover(null);
  };
  const togglePop = (id: Exclude<FashionStudioPopover, null>) =>
    setPopover((prev) => (prev === id ? null : id));
  const closePop = () => setPopover(null);

  // Footer pill labels — compact summaries of the grouped settings.
  // Brand lives in the RIGHT panel footer (Jun 11) because it applies
  // to BOTH Create and Templates modes (logo compositing + brand
  // presence run on AI-template renders too); the left filter bar is
  // Create-tab-only.
  const outputPillLabel = `${format === 'carousel' ? 'CAROUSEL' : 'SINGLE'} · ${QUALITY_LABEL_SHORT[quality].toUpperCase()} · ${aspectRatio}`;
  // Jun 22 — Logo segment dropped from this label; the user-facing
  // logo toggle was removed (gpt-image-2 unreliable on logo refs).
  const brandPillLabel = `BRAND COLORS ${creativeDirection.brand_presence.toUpperCase()}`;

  // V3 Carousel — display label for the picked type ("Story arc",
  // "Step by step", etc.). Lookup tolerates a null carouselType
  // (returns null → bar doesn't render).
  const carouselTypeLabel = selectedCarouselType
    ? getCarouselType(selectedCarouselType).label
    : null;

  // V3 Social Post — display label + description for the picked intent.
  // Mirrors carouselTypeLabel — bar doesn't render when null.
  const selectedIntentObj = selectedIntent
    ? getSocialPostIntent(selectedIntent)
    : null;

  // Fashion (Jun 14) — Light / Background / Vibe pickers swap to
  // `/filter-previews-fashion/<slug>.webp` (model-subject per-value
  // previews) instead of the default `/filter-previews/<slug>.webp`
  // (bottle subject). Mirrors how Framing already works on the Fashion
  // route — each Framing value has its own model preview. Generated by
  // `backend/scripts/render_filter_previews.py` (run with no args to
  // pick up new directives idempotently).
  const fashionFilterPath = routeConfig.category === 'fashion'
    ? '/filter-previews-fashion'
    : '/filter-previews';

  // Jul 24 — Product Shot + Model Shot use the binary Text chip (Off/On direct
  // toggle, no popover). Other routes keep the 3-option popover.
  const binaryTextChip = routeConfig.category === 'product-shot'
    || routeConfig.category === 'fashion';

  // Generate button group (Jul 2) — extracted so it renders in TWO places:
  // the DESKTOP right-panel footer (hidden xl:block) AND, on MOBILE, its own
  // fixed Panel 3 (xl:hidden) below Templates. Shared popover state is fine —
  // only one instance is visible per breakpoint, so a stray popover on the
  // hidden copy can't show.
  const generateControls = (
    <>
      {/* Mobile: a 2-per-row grid of stretched chips (the justify-between row
          scattered them one-per-line). The group <div>s use `contents` so the
          chips become direct grid items (grid stretches them to fill each
          cell); the Format toggle spans the full row. xl: the original
          left/right justify-between flex row. */}
      <div className="grid grid-cols-2 gap-2 xl:flex xl:items-center xl:justify-between xl:gap-2">
        {/* LEFT — output settings (Format toggle + Aspect + Quality). */}
        <div className="contents xl:flex xl:items-center xl:gap-2">
          {routeConfig.category !== 'carousel' && routeConfig.category !== 'social-post' && (
            <div className="col-span-2 xl:contents">
              <StudioFormatToggle
                value={recreateTemplateMode ? 'single' : format}
                onChange={onFormatChange}
                carouselDisabled={recreateTemplateMode}
              />
            </div>
          )}
          <StudioSettingChip
            glyph={<i className="ri-aspect-ratio-line" />}
            value={aspectRatio}
            label="Aspect ratio"
            isOpen={popover === 'aspect'}
            onToggle={() => togglePop('aspect')}
            onClose={closePop}
            align="left"
          >
            <StudioChipOptionList
              options={allowedAspects.map((a) => ({ value: a, label: ASPECT_LABEL[a] }))}
              selected={aspectRatio}
              onSelect={(v) => { onAspectRatioChange(v as ProtoAspect); closePop(); }}
            />
          </StudioSettingChip>
          <StudioSettingChip
            glyph={<i className="ri-vip-diamond-line" />}
            value={QUALITY_LABEL_SHORT[quality]}
            label="Quality"
            isOpen={popover === 'quality'}
            onToggle={() => togglePop('quality')}
            onClose={closePop}
            align="left"
          >
            <StudioChipOptionList
              options={[
                { value: 'low', label: 'Low', sublabel: '1 cr' },
                { value: 'medium', label: 'Medium', sublabel: '2 cr', locked: isFree },
                { value: 'high', label: 'High', sublabel: '4 cr', locked: isFree },
              ]}
              selected={quality}
              onSelect={(v) => { onQualityChange(v as Quality); closePop(); }}
            />
          </StudioSettingChip>
        </div>

        {/* RIGHT — brand settings (Text + Brand colors). */}
        <div className="contents xl:flex xl:items-center xl:gap-2">
          {routeConfig.showTextDensity && (
            <StudioSettingChip
              glyph={<i className="ri-text" />}
              value={
                binaryTextChip
                  ? `Text: ${(creativeDirection.text_density ?? 'None') === 'None' ? 'OFF' : 'ON'}`
                  : textDensityChipLabel(creativeDirection.text_density)
              }
              label="Text"
              dimmed={(creativeDirection.text_density ?? 'Text + CTA') === 'None'}
              isOpen={popover === 'text'}
              onToggle={() => togglePop('text')}
              onClose={closePop}
              align="right"
              // Jul 14 / Jul 24 — Product Shot + Model Shot are binary Off/On →
              // direct toggle (no popover). Other routes keep the 3-option popover.
              onDirectToggle={
                binaryTextChip
                  ? () => onCreativeDirectionChange({
                      ...creativeDirection,
                      text_density: (creativeDirection.text_density ?? 'None') === 'None'
                        ? 'Text + CTA'
                        : 'None',
                    })
                  : undefined
              }
            >
              <StudioChipOptionList
                options={[
                  { value: 'None', label: 'No text' },
                  { value: 'Text only', label: 'Headline only' },
                  { value: 'Text + CTA', label: 'Headline + CTA' },
                ]}
                selected={creativeDirection.text_density ?? 'Text + CTA'}
                onSelect={(v) => { onCreativeDirectionChange({ ...creativeDirection, text_density: v as CreativeDirection['text_density'] }); closePop(); }}
              />
            </StudioSettingChip>
          )}

          {/* Jul 14 — binary Off/On, so it's a direct toggle (no popover). */}
          <StudioSettingChip
            glyph={<i className="ri-palette-line" />}
            value={`Brand: ${creativeDirection.brand_presence.toUpperCase()}`}
            label="Brand colors"
            dimmed={creativeDirection.brand_presence === 'Off'}
            isOpen={false}
            onToggle={() => {}}
            onClose={() => {}}
            align="right"
            onDirectToggle={() => onCreativeDirectionChange({
              ...creativeDirection,
              brand_presence: creativeDirection.brand_presence === 'Off' ? 'On' : 'Off',
            })}
          />
        </div>
      </div>

      {fashionProductMismatch && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-800 text-sm font-medium border border-red-200">
          <i className="ri-shield-cross-line mt-0.5 flex-shrink-0 text-base" />
          <div className="flex-1 min-w-0">
            <p className="font-bold mb-0.5">
              This product isn&apos;t a wearable.
            </p>
            <p className="text-red-700 text-xs leading-relaxed">
              Fashion only supports apparel, footwear, accessories,
              and jewelry. Edit the product or switch to Product
              Shot for this item.
            </p>
          </div>
        </div>
      )}
      <FashionGenerateButton
        creditCost={creditCost}
        insufficient={insufficient}
        canGenerate={canGenerate}
        generating={generating}
        balance={balance}
        onGenerate={onGenerate}
        onUpgrade={onUpgrade}
        fullWidth
        label={(recreateTemplateMode ? 'single' : format) === 'carousel' ? 'Generate Carousel' : 'Generate Image'}
        imageCount={matrixImageCount}
      />
    </>
  );

  return (
    <AppLayoutV2>
      {/* HEADER — one persistent, FULL-WIDTH white bar, flush at the very top,
          across the whole flow (picker → generate → edit). Matches /creatives:id:
          edge-to-edge white bg + border + shadow, inner content centered to the
          same max-w as the body. The route title ("Product Shot") is ALWAYS
          shown; "Back to design" + product context appear only while editing a
          flat render, so the chrome never jumps. */}
      <div className="sticky top-0 z-30 bg-white border-b border-surface-dark/40 shadow-[0_2px_14px_-6px_rgba(15,15,30,0.18)]">
        {/* Inner max-w aligns the title with the content box below. */}
        <div className="w-full max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-3">
          {productShotFinish && renderOpen && (
            <button
              type="button"
              onClick={handleBackToEdit}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-dark hover:bg-surface-dark/40 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-arrow-left-line text-lg" />
              <span className="hidden sm:inline text-sm font-medium">Back to design</span>
            </button>
          )}
          {/* Gradient icon tile — anchors the title so the header reads as a
              proper section heading, not a bare line of text. */}
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center shadow-md shadow-primary/25">
            <i className={`${CATEGORY_ICON[routeConfig.category] ?? 'ri-image-line'} text-xl`} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-dark truncate leading-tight">
              {routeConfig.title}
            </h1>
            {productShotFinish && renderOpen ? (
              <p className="text-xs text-dark-muted truncate">
                {productName?.trim() || 'AI creative'}
                {renderedAspect ? ` · ${renderedAspect}` : ''}
              </p>
            ) : (
              <p className="hidden sm:block text-xs text-dark-muted truncate">
                {routeConfig.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Padded content box — same for picker + editor, so the editor card sits
          in the same max-w column with page margin around it (the "overall
          panel" look from the mockup). */}
      <div className="w-full max-w-[1500px] mx-auto px-6 pt-6 pb-8">
        {/* V3 CAROUSEL TYPE SUMMARY (Jun 14) — collapsed bar shown after
            Step 1. Visually mirrors the StudioDesignSummaryBar below it
            (same white bg + halo shadow + thumb/label/pill scale) so
            the two read as the same accordion family. Only the LEFT
            content differs — one icon tile here vs. template+product
            thumbs there. Click "Change type" → onEditCarouselType()
            sets selectedCarouselType=null → page re-renders into the
            Step 1 type pre-step. */}
        {routeConfig.category === 'carousel' && carouselTypeLabel && (
          <button
            type="button"
            onClick={onEditCarouselType}
            title="Change carousel type"
            className="group relative mb-4 w-full rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden text-left cursor-pointer"
          >
            <div className="flex items-center gap-4 px-5 py-3.5">
              {/* Type icon tile — same dimensions as the template/
                  product thumb on the Design summary bar (56×56). */}
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <i className="ri-stack-line text-2xl" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary leading-none mb-1">
                  Carousel type
                </p>
                <p className="text-sm font-bold text-dark truncate">
                  {carouselTypeLabel}
                </p>
                <p className="text-[11px] text-dark-muted mt-0.5 truncate">
                  {getCarouselType(selectedCarouselType!).description}
                </p>
              </div>
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-surface-dark/40 text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all">
                <i className="ri-arrow-left-line text-base" />
                Change type
              </span>
            </div>
            {/* Hover overlay — matches the Design bar's frosted wash. */}
            <div className="absolute inset-0 bg-white/55 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-dark text-white text-xs font-bold uppercase tracking-[0.12em] shadow-lg">
                <i className="ri-arrow-left-line text-base" />
                Change type
              </span>
            </div>
          </button>
        )}

        {/* V3 SOCIAL POST INTENT SUMMARY (Jun 15) — collapsed bar shown
            after the intent pre-step. Same visual + interaction shape
            as the Carousel summary bar above: icon tile + eyebrow +
            label + truncated description + "Change intent" pill, with
            a frosted hover overlay. Clicking anywhere calls
            onEditIntent() which sets selectedIntent=null → page re-
            renders into the SocialPostIntentPreStep. */}
        {routeConfig.category === 'social-post' && selectedIntentObj && (
          <button
            type="button"
            onClick={onEditIntent}
            title="Change intent"
            className="group relative mb-4 w-full rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden text-left cursor-pointer"
          >
            <div className="flex items-center gap-4 px-5 py-3.5">
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <i className="ri-sparkling-2-line text-2xl" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary leading-none mb-1">
                  {selectedIntentObj.eyebrow ?? 'Social post intent'}
                </p>
                <p className="text-sm font-bold text-dark truncate">
                  {selectedIntentObj.label}
                </p>
                <p className="text-[11px] text-dark-muted mt-0.5 truncate">
                  {selectedIntentObj.description}
                </p>
              </div>
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-surface-dark/40 text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all">
                <i className="ri-arrow-left-line text-base" />
                Change intent
              </span>
            </div>
            <div className="absolute inset-0 bg-white/55 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-dark text-white text-xs font-bold uppercase tracking-[0.12em] shadow-lg">
                <i className="ri-arrow-left-line text-base" />
                Change intent
              </span>
            </div>
          </button>
        )}

        {/* DESIGN SUMMARY BAR (Jun 12 v2) — when the render section is
            expanded the two design panels collapse into this slim bar:
            template thumbnail + product thumbnail + hover overlay that
            says "Back to edit". Clicking anywhere re-expands the design
            panels (the render collapses to its own bar below). */}
        {/* Product Shot uses the persistent header's "Back to design" instead of
            this bar (no double header) — skip it for the whole product-shot flow.
            Every other route still uses it; it's preserved as the revert path. */}
        {designCollapsed && !productShotFinish && (
          <StudioDesignSummaryBar
            templateName={selectedTemplatePreview?.name ?? null}
            templateImageUrl={selectedTemplatePreview?.imageUrl ?? null}
            productImageUrl={productImageUrl}
            productName={productName}
            outputLabel={`${outputPillLabel} · ${brandPillLabel}`}
            onBackToEdit={handleBackToEdit}
          />
        )}

        {/* TWO PANELS — kept MOUNTED (display:none) while collapsed so
            internal component state (StepUpload tabs, scroll positions)
            survives the round-trip to the render view. */}
        <div className={designCollapsed ? 'hidden' : 'grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch'}>

          {/* ============ LEFT PANEL ============
              Stacked box-shadow: 4px solid white halo right at the panel
              edge (creates the "white stroke" effect against the lavender
              page bg), then a soft elevated drop shadow further out so
              the panel lifts off the page. Right panel mirrors the same
              chrome below to keep them as visual twins.
              Note: shadow-[...] arbitrary value used so the white halo
              and drop shadow compose into one box-shadow stack without
              one overriding the other. */}
          <div ref={mobileTemplatesRef} className={`order-2 xl:order-1 rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] flex flex-col overflow-hidden ${fitPanelH}`}>
            {/* Top tabs — hidden on Fashion (no templates authored yet
                for the Worn Item route, Jun 14) AND Social Post V3
                (Jun 15 user lock — no Templates tab in v1; intent IS
                the creative direction, AI Magic is the only path).
                When fashion templates land OR Social Post v2 surfaces
                curated examples, drop the matching conditional + the
                matching designMode lock in the route-reset effect. */}
            {/* Jun 18 — ALL studio routes now have a Templates tab backed by
                recipes. Social Post: Create = intent-driven AI freestyle,
                Templates = recipe (visual) × the picked intent (copy). */}
            <div className="flex-shrink-0 p-3 border-b border-surface-dark/20">
              <StudioCreateTemplatesPill
                designMode={designMode}
                onChange={onDesignModeChange}
              />
            </div>

            {designMode === 'ai' ? (
              isNarrow ? (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-8 py-12 text-center">
                  {/* Create studio needs desktop width — arrangement rows +
                      filter popovers break on a phone. Notice + jump to Templates. */}
                  <i className="ri-computer-line text-5xl text-primary/40 mb-4" />
                  <div className="text-base font-bold text-dark mb-1.5">
                    Create is best on desktop
                  </div>
                  <p className="text-sm text-dark-muted max-w-xs leading-relaxed mb-5">
                    The Create studio — arrangements, poses, and the light /
                    background / vibe controls — needs a wider screen. On
                    mobile, use <b>Templates</b> to pick a ready-made scene, or
                    open this on a laptop or desktop.
                  </p>
                  <button
                    type="button"
                    onClick={() => onDesignModeChange('template')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors cursor-pointer shadow-md shadow-primary/25"
                  >
                    <i className="ri-layout-grid-line" />
                    Browse Templates
                  </button>
                </div>
              ) : (
              <>
                {/* HERO picker — different per route:
                    • Fashion → MODEL grid (17 models)
                    • Held → POSE grid (6 hand positions)
                    • Product Shot → PATTERN grid (6 arrangements)
                    All three render in the same upper-left slot of the
                    panel so the structure is consistent across routes. */}
                {routeConfig.category === 'carousel' ? (
                  // V3 Carousel (Jun 14) — Pattern/Pose/Model don't apply
                  // to carousels (each frame's structure is type-driven,
                  // not arrangement-driven). The Create tab for carousel
                  // is reserved for "Product arrangements per frame"
                  // (product-left / product-right / etc.) — deferred per
                  // user direction (built last). Placeholder for v1.
                  <div className="relative flex-1 min-h-0">
                    <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
                      <i className="ri-layout-grid-line text-4xl text-dark-muted/40 mb-3" />
                      <div className="text-sm font-bold text-dark mb-1">
                        Product arrangements
                      </div>
                      <p className="text-xs text-dark-muted max-w-xs leading-relaxed">
                        Pick where the product sits per frame — left, right,
                        top, bottom. Coming in the next iteration. For now,
                        use <b>Templates</b> and pick a scene.
                      </p>
                    </div>
                  </div>
                ) : routeConfig.category === 'fashion' ? (
                  <div className="relative flex-1 min-h-0">
                    <div className="absolute inset-0 px-5 pt-4 pb-8 overflow-y-auto sidebar-scroll">
                      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark mb-3">
                        Model
                      </h2>
                      <StudioBigGrid
                        selectedId={selectedFashionModelId}
                        onSelect={onFashionModelSelect}
                      />
                    </div>
                  </div>
                ) : routeConfig.category === 'product-shot' && routeConfig.patterns && routeConfig.poses ? (
                  // Product Shot (merged, Jun 12 v6) — TWO stacked hero
                  // groups: Product arrangements (patterns) + In hand (poses).
                  // Exactly one is active (pattern XOR pose); the inactive
                  // group shows nothing highlighted. Pose mode = held shot.
                  (() => {
                    const poseMode = !!creativeDirection.pose;
                    return (
                      <div className="relative flex-1 min-h-0">
                        <div className="absolute inset-0 px-5 pt-4 pb-8 overflow-y-auto sidebar-scroll space-y-5">
                          <div>
                            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark mb-3 flex items-center gap-2">
                              <i className="ri-shapes-line text-base text-primary" />
                              Product arrangements
                            </h2>
                            <StudioPatternGrid
                              patterns={routeConfig.patterns!}
                              selected={poseMode ? null : (selectedPattern ?? routeConfig.defaultPattern ?? routeConfig.patterns![0])}
                              onSelect={onPatternSelect}
                            />
                          </div>
                          <div>
                            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark mb-1 flex items-center gap-2">
                              <i className="ri-hand-heart-line text-base text-primary" />
                              In hand
                            </h2>
                            <p className="text-xs text-dark-muted mb-3">
                              Show a real model holding your product.
                            </p>
                            <StudioPoseGrid
                              poses={routeConfig.poses!}
                              selected={poseMode ? creativeDirection.pose! : null}
                              onSelect={onPoseSelect}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : routeConfig.poses ? (
                  // Held route (legacy, pre-redirect) — POSE picker as the hero.
                  <div className="relative flex-1 min-h-0">
                    <div className="absolute inset-0 px-5 pt-4 pb-8 overflow-y-auto sidebar-scroll">
                      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark mb-3">
                        Pose
                      </h2>
                      <StudioPoseGrid
                        poses={routeConfig.poses}
                        selected={creativeDirection.pose ?? routeConfig.defaultPose ?? routeConfig.poses[0]}
                        onSelect={(p) => onCreativeDirectionChange({ ...creativeDirection, pose: p })}
                      />
                    </div>
                  </div>
                ) : routeConfig.patterns ? (
                  // (Unreached now that product-shot has its own branch — kept
                  // for safety if a future route declares patterns only.)
                  <div className="relative flex-1 min-h-0">
                    <div className="absolute inset-0 px-5 pt-4 pb-8 overflow-y-auto sidebar-scroll">
                      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark mb-3">
                        Pattern
                      </h2>
                      <StudioPatternGrid
                        patterns={routeConfig.patterns}
                        selected={selectedPattern ?? routeConfig.defaultPattern ?? routeConfig.patterns[0]}
                        onSelect={onPatternSelect}
                      />
                    </div>
                  </div>
                ) : routeConfig.category === 'social-post' ? (
                  // V3 Social Post (Jun 15 v3) — Shot Type as the hero
                  // picker. Hoisted out of the FILTERS section since it
                  // changes ROUTING (in-use → product-swap backend) not
                  // just creative styling. Two large thumbnail tiles
                  // mirroring StudioPatternGrid's visual treatment.
                  <div className="relative flex-1 min-h-0">
                    <div className="absolute inset-0 px-5 pt-4 pb-8 overflow-y-auto sidebar-scroll">
                      <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark mb-3 flex items-center gap-2">
                        <i className="ri-aperture-line text-base text-primary" />
                        Shot type
                      </h2>
                      <StudioShotTypeGrid
                        selected={socialPostShot}
                        onSelect={onSocialPostShotChange}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-h-0" />
                )}

                {/* 4 compact pickers row — pinned bottom, popovers open above.
                    Full-width lavender filter bar (bg-surface-sidebar) that
                    extends to the LEFT, RIGHT, and BOTTOM edges of the
                    panel — reads as a distinct "filter bar" docked at the
                    bottom. Flat top (no rounded corners) so it reads as
                    docked toolbar, not a floating card. Bottom + side
                    corners are clipped by the parent panel's rounded-2xl
                    + overflow-hidden so the bar inherits the panel's
                    corner radius cleanly. Tiles capped at max-w-[100px]
                    (significantly smaller than MODEL tiles ~155px) to
                    reinforce the hierarchy. */}
                <div className="flex-shrink-0 bg-surface-sidebar/90 px-5 py-4">
                  {/* FILTERS title.
                      V3 Social Post (Jun 15 v3) — master ON/OFF toggle
                      sits on the LEFT of the "Filters" word (was right
                      Jun 15 v2). Filter icon removed — the toggle pill
                      IS the affordance + the label reads cleaner. */}
                  <div className="flex items-center gap-3 mb-3">
                    {routeConfig.category === 'social-post' && (
                      <StudioMasterFiltersToggle
                        value={filtersEnabled}
                        onChange={onFiltersEnabledChange}
                      />
                    )}
                    <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark">
                      Filters
                    </h2>
                  </div>

                  {/* (V3 Social Post — Shot Type row was here Jun 15 v2.
                      Hoisted to its own hero picker at top of left panel
                      in Jun 15 v3 since it controls routing not just
                      creative styling. Filter row stays purely about
                      visual styling now.) */}

                  {/* Filters per route:
                      • Fashion + Held → 4 (Framing / Light / Background / Vibe)
                      • Product Shot → 3 (Framing dropped — PATTERN hero
                        already drives composition, Framing would
                        contradict the arrangement)
                      Brand moved to the RIGHT panel footer (Jun 11) since
                      it applies to BOTH Create and Templates modes.
                      Left-packed flex row (NOT an evenly-stretched grid)
                      so the tiles hug the left edge regardless of count.
                      V3 Social Post (Jun 15 v2) — when filtersEnabled is
                      false on social-post, the entire row dims +
                      pointer-events-none so users can't fiddle with
                      stale values. The dim class is conditional so other
                      routes (where filtersEnabled defaults true) keep
                      working unchanged. */}
                  <div
                    className={`flex gap-5 transition-opacity ${
                      routeConfig.category === 'social-post' && !filtersEnabled
                        ? 'opacity-40 pointer-events-none select-none'
                        : ''
                    }`}
                    aria-disabled={routeConfig.category === 'social-post' && !filtersEnabled}
                  >
                    {routeConfig.category !== 'product-shot' && (
                      <FashionCompactPicker
                        label="Framing"
                        currentValue={format === 'carousel' ? '4 auto-picked' : creativeDirection.framing}
                        imageSrc={`/filter-previews/${studioFramingSlug(format === 'carousel' ? routeConfig.carouselFramings[0] : creativeDirection.framing)}.webp`}
                        isOpen={popover === 'framing'}
                        onToggle={() => togglePop('framing')}
                        onClose={closePop}
                        disabled={format === 'carousel'}
                        disabledHint={
                          format === 'carousel'
                            ? `Carousel uses: ${routeConfig.carouselFramings.join(' · ')}`
                            : undefined
                        }
                      >
                        <FashionImageOptionGrid
                          values={routeConfig.framings}
                          selected={creativeDirection.framing}
                          onSelect={(v) => setCD('framing', v as CreativeDirection['framing'])}
                        />
                      </FashionCompactPicker>
                    )}

                    <FashionCompactPicker
                      label="Light"
                      currentValue={creativeDirection.light}
                      imageSrc={`${fashionFilterPath}/${studioFramingSlug(creativeDirection.light)}.webp`}
                      isOpen={popover === 'light'}
                      onToggle={() => togglePop('light')}
                      onClose={closePop}
                    >
                      <FashionImageOptionGrid
                        values={STUDIO_LIGHTS}
                        selected={creativeDirection.light}
                        onSelect={(v) => setCD('light', v as CreativeDirection['light'])}
                        pathPrefix={fashionFilterPath}
                      />
                    </FashionCompactPicker>

                    <FashionCompactPicker
                      label="Background"
                      currentValue={creativeDirection.background}
                      imageSrc={`${fashionFilterPath}/${studioFramingSlug(creativeDirection.background)}.webp`}
                      isOpen={popover === 'background'}
                      onToggle={() => togglePop('background')}
                      onClose={closePop}
                    >
                      <FashionImageOptionGrid
                        values={STUDIO_BACKGROUNDS}
                        selected={creativeDirection.background}
                        onSelect={(v) => setCD('background', v as CreativeDirection['background'])}
                        pathPrefix={fashionFilterPath}
                      />
                    </FashionCompactPicker>

                    <FashionCompactPicker
                      label="Vibe"
                      currentValue={creativeDirection.mood}
                      imageSrc={`${fashionFilterPath}/${studioFramingSlug(creativeDirection.mood)}.webp`}
                      isOpen={popover === 'vibe'}
                      onToggle={() => togglePop('vibe')}
                      onClose={closePop}
                    >
                      <FashionImageOptionGrid
                        values={STUDIO_VIBES}
                        selected={creativeDirection.mood}
                        onSelect={(v) => setCD('mood', v as CreativeDirection['mood'])}
                        pathPrefix={fashionFilterPath}
                      />
                    </FashionCompactPicker>

                  </div>
                </div>
              </>
              )
            ) : (
              // Templates tab body. GATED on product selection (Jun 12):
              // until a product is picked we can't surface domain-matched
              // recommendations, so the grid renders greyed-out +
              // non-interactive with a centered hint pointing at the
              // product picker on the right.
              <div className="relative flex-1 min-h-0">
                {lockedTemplateId ? (
                  // Locked single-template view (deep-linked from /templates):
                  // show ONLY the chosen template. "Browse all" reopens the grid.
                  (() => {
                    // V3 Carousel reads from product-shot's library too
                    // (no separate carousel library).
                    const _lookupCat = routeConfig.category === 'carousel' ? 'product-shot' : routeConfig.category;
                    const lt = aiTemplatesForStudioRoute(_lookupCat).find((x) => x.id === lockedTemplateId);
                    return lt ? (
                      <div
                        className={`
                          h-full px-3 py-3 overflow-y-auto sidebar-scroll
                          ${templatesLocked ? 'opacity-40 grayscale pointer-events-none select-none' : ''}
                        `}
                        aria-disabled={templatesLocked}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-dark-muted">Selected template</p>
                          <button
                            type="button"
                            onClick={onClearLockedTemplate}
                            className="text-xs font-bold text-primary hover:underline cursor-pointer"
                          >
                            Browse all templates →
                          </button>
                        </div>
                        <div className="rounded-2xl overflow-hidden border-2 border-primary shadow-md shadow-primary/20">
                          <img src={aiTemplateSamplePath(lt)} alt={lt.name} className="block w-full h-auto" draggable={false} />
                        </div>
                        <p className="mt-2.5 text-sm font-bold text-dark">{lt.name}</p>
                        <p className="text-xs text-dark-muted mt-0.5">Pick your product on the right, then hit Generate.</p>
                      </div>
                    ) : null;
                  })()
                ) : lockedRecipeId ? (
                  // Locked single-RECIPE view (deep-linked from /templates
                  // via ?recipe=). Mirrors the locked-template view above:
                  // show ONLY the chosen recipe + "Browse all recipes →".
                  // The product picker is on the right (shared panel).
                  (() => {
                    const lr = (props.recipeTemplates ?? []).find(
                      (r) => `${r.vibe_slug}/${r.id}` === lockedRecipeId,
                    );
                    return (
                      <div
                        className={`
                          h-full px-3 py-3 overflow-y-auto sidebar-scroll
                          ${templatesLocked ? 'opacity-40 grayscale pointer-events-none select-none' : ''}
                        `}
                        aria-disabled={templatesLocked}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-dark-muted">Selected template</p>
                          <button
                            type="button"
                            onClick={onClearLockedRecipe}
                            className="text-xs font-bold text-primary hover:underline cursor-pointer"
                          >
                            Browse all templates →
                          </button>
                        </div>
                        {lr ? (
                          <>
                            <div className="rounded-2xl overflow-hidden border-2 border-primary shadow-md shadow-primary/20">
                              <img src={lr.preview_url} alt={lr.vibe} className="block w-full h-auto" draggable={false} />
                            </div>
                            <p className="mt-2.5 text-sm font-bold text-dark">{lr.vibe}</p>
                            <p className="text-xs text-dark-muted mt-0.5">Pick your product on the right, then hit Generate.</p>
                          </>
                        ) : props.recipesLoading ? (
                          // Recipe list still loading — show a placeholder
                          // until we can resolve the locked recipe.
                          <div className="aspect-square rounded-2xl bg-surface-dark/40 animate-pulse" />
                        ) : (
                          // Sep 4 — the locked recipe isn't in the current
                          // product-filtered list (it was built for a different
                          // kind of product). Don't dead-end: reassure + let
                          // them Generate anyway (right-panel Generate uses the
                          // locked recipe via render-recipe), or browse templates
                          // made for their product.
                          <div className="rounded-2xl border border-amber-300/70 bg-amber-50/60 px-4 py-3">
                            <p className="text-xs text-dark leading-relaxed">
                              <i className="ri-information-line mr-1 text-amber-600" />
                              This template was built for a different kind of product, so it may not fit this one perfectly. You can still hit{' '}
                              <span className="font-bold text-dark">Generate</span> to try it — results may vary.
                            </p>
                            <button
                              type="button"
                              onClick={onClearLockedRecipe}
                              className="mt-2 text-xs font-bold text-primary hover:underline cursor-pointer"
                            >
                              Or browse templates made for your product →
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <div
                    className={`
                      h-full px-3 py-3 overflow-y-auto sidebar-scroll
                      ${templatesLocked ? 'opacity-40 grayscale pointer-events-none select-none' : ''}
                    `}
                    aria-disabled={templatesLocked}
                  >
                    {/* Loading state (Jun 18) — recipe list is a network
                        fetch + GCS walk, so it lands a beat after the tab
                        opens. Show a skeleton instead of flashing the
                        empty-state ("No recipes yet"). Only when we have
                        NOTHING to show yet; a re-fetch with recipes already
                        present keeps the old grid visible (no flicker). */}
                    {props.recipesLoading
                      && (!props.recipeTemplates || props.recipeTemplates.length === 0) ? (
                      <div className="px-1 py-2">
                        <div className="flex items-center gap-2 px-1 mb-3">
                          <span className="inline-block w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          <p className="text-xs font-semibold text-dark-muted">Loading templates…</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {Array.from({ length: 6 }).map((_, i) => (
                            <div
                              key={i}
                              className="aspect-square rounded-xl bg-surface-dark/40 animate-pulse"
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                    {/* Recipes-as-Templates (Jun 18) ──────────────────────
                        Production recipe library. Product Shot shows it
                        ABOVE the scene templates; Fashion shows it as the
                        WHOLE Templates tab (fashion has no AI templates or
                        recreate templates). Single-select; clears any AI
                        template selection on pick. Empty array hides the
                        section (curator must bake previews first). The
                        product-type filter (props.recipeProductTypes)
                        narrows to the uploaded product's category —
                        apparel/footwear/accessory/wearable for Fashion. */}
                    {/* Recipes are the template library for EVERY studio
                        route now (product-shot / fashion / carousel /
                        product-swap). Product Swap filters to requires_model
                        recipes (model holding/using the product). Carousel
                        renders the picked recipe as a 4-frame hero+variants
                        set at generate time. */}
                    {/* Jul 13 — on a showSynthesize route (Product Shot /
                        Fashion) render RecipesSection even when the pool is
                        EMPTY: its internal showSynthesize logic surfaces the
                        AI Magic tile (→ gpt-image-2 freestyle) instead of a
                        dead "No recipes yet" panel. recipes={[]} is handled
                        inside. */}
                    {props.recipeTemplates
                      && (props.recipeTemplates.length > 0
                        || routeConfig.category === 'product-shot'
                        || routeConfig.category === 'fashion') && (
                      <RecipesSection
                        recipes={props.recipeTemplates}
                        selectedId={props.selectedRecipeId}
                        vibeFilter={props.recipeVibeFilter}
                        onVibeFilterChange={props.onRecipeVibeFilterChange}
                        onSelect={props.onRecipeSelect}
                        productTypes={props.recipeProductTypes}
                        modelFilter={
                          // Jul 24 — model-worn recipes belong to the model-first
                          // routes ONLY. Fashion / Product-Swap show 'with' (model
                          // wearing/holding); every other route shows 'without'
                          // (product-only — the item by itself on a surface/scene).
                          // Reverses the Jul-13 "apparel product shot is naturally
                          // worn" behavior: a Chunky Hoodie on Product Shot was
                          // surfacing model-in-a-sweater fashion templates, which
                          // read as Fashion bleeding into Product Shot.
                          routeConfig.category === 'fashion'
                          || routeConfig.category === 'product-swap'
                            ? 'with'
                            : 'without'
                        }
                        textOnly={routeConfig.category === 'social-post'}
                        showSynthesize={
                          // Jun 22 — Fashion gets the same AI Magic tile
                          // as Product Shot. Same dispatch (synthesize-
                          // recipe + variance 4), same behavior; the
                          // synth's strict 3-tier picker will narrow to
                          // wearable recipes automatically (Fashion's
                          // recipeProductTypes is pinned to the wearable
                          // family above).
                          routeConfig.category === 'product-shot'
                          || routeConfig.category === 'fashion'
                        }
                        showLegacyDivider={showLegacyForRoute(routeConfig.category)}
                      />
                    )}
                    {/* Empty-state — recipes-only route with nothing baked
                        yet (and legacy isn't filling the tab via the flag).
                        Jul 13 — suppressed on Product Shot / Fashion: those
                        show the AI Magic freestyle tile instead (above). */}
                    {!showLegacyForRoute(routeConfig.category)
                      && routeConfig.category !== 'product-shot'
                      && routeConfig.category !== 'fashion'
                      && (!props.recipeTemplates || props.recipeTemplates.length === 0) && (
                      <div className="px-2 py-10 text-center">
                        <i className={`text-3xl text-dark-muted/40 ${routeConfig.category === 'fashion' ? 'ri-shirt-line' : 'ri-image-line'}`} />
                        <p className="text-sm font-bold text-dark mt-2">
                          {routeConfig.category === 'fashion' ? 'No apparel recipes yet' : 'No recipe templates yet'}
                        </p>
                        <p className="text-xs text-dark-muted mt-1 max-w-xs mx-auto leading-relaxed">
                          {routeConfig.category === 'product-swap' ? (
                            <>Bake recipes with <b>Requires model</b> on the moodboard
                            previews tool, or use the <b>Create</b> tab.</>
                          ) : routeConfig.category === 'fashion' ? (
                            <>Bake apparel/footwear/accessory recipe previews on
                            the moodboard previews tool, or use the <b>Create</b>
                            tab to put your garment on a model.</>
                          ) : (
                            <>Bake recipe previews on the moodboard previews tool,
                            or use the <b>Create</b> tab to design from scratch.</>
                          )}
                        </p>
                      </div>
                    )}
                    {/* Legacy AI-template grid — ONLY when the flag still
                        retains it for this route (recoverable; off by
                        default). All studio routes are recipes-only now. */}
                    {showLegacyForRoute(routeConfig.category) && (
                      <AiTemplateGrid
                        templates={aiTemplatesForStudioRoute(
                          routeConfig.category === 'carousel' ? 'product-shot' : routeConfig.category,
                        )}
                        selectedIds={selectedAiTemplateIds}
                        onSelect={onAiTemplateSelect}
                        recommendedDomain={productImageUrl ? productDomain : null}
                      />
                    )}
                      </>
                    )}
                  </div>
                )}
                {/* Lock overlay — covers BOTH branches (locked single
                    view + full grid). Two messages: "pick a product"
                    when nothing uploaded, "confirm with Continue" when
                    the picker is still open on the right. */}
                {templatesLocked && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40">
                    <div className="mx-6 px-5 py-4 rounded-2xl bg-white border border-surface-dark/30 shadow-lg text-center max-w-[320px]">
                      <i className="ri-lock-2-line text-2xl text-primary" />
                      {!productImageUrl ? (
                        <>
                          <p className="text-sm font-bold text-dark mt-1.5">
                            Select a product first
                          </p>
                          <p className="text-xs text-dark-muted mt-1 leading-relaxed">
                            Once we know your product, we&apos;ll surface the
                            templates that fit it best.
                            <i className="ri-arrow-right-line ml-1 align-middle" />
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-bold text-dark mt-1.5">
                            Confirm your product
                          </p>
                          <p className="text-xs text-dark-muted mt-1 leading-relaxed">
                            Click <b>Continue</b> on the right to lock in your
                            product, then pick a template.
                            <i className="ri-arrow-right-line ml-1 align-middle" />
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Templates "Continue" (mobile only, Jul 2). Pinned footer that
                anchor-scrolls down to Panel 3 (Generate). Shown only after a
                product is picked (before that, templates are locked, nothing
                to continue to). Desktop has no such button — generate lives
                in the always-visible right-panel footer. */}
            {!productPickerOpen && (
              <div className="flex-shrink-0 xl:hidden border-t border-surface-dark/20 px-5 py-4 flex items-center justify-end">
                <button
                  type="button"
                  onClick={scrollToMobileGenerate}
                  className="h-11 min-w-[200px] px-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer"
                >
                  Continue
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            )}
          </div>

          {/* ============ RIGHT PANEL ============
              Same chrome as the LEFT panel — 4px white halo + drop shadow
              so the panels read as twins lifted off the page bg.
              Body branches by `productPickerOpen`:
                • TRUE (default): inline StepUpload picker + Continue
                • FALSE (after Continue): product header + preview +
                  Output pill + GENERATE
              Edit button on the product header re-opens the picker. */}
          <div className={`order-1 xl:order-2 rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] flex flex-col overflow-hidden ${fitPanelH}`}>

            {/* Panel 1 · PRODUCT (mobile, Jul 2). FIXED at the top on mobile
                (order-1) — it never moves. Picker → product summary. The
                Generate button group is NOT here on mobile: the footer below
                is hidden xl:block, and the controls render in their own
                Panel 3 below Templates. Desktop keeps this in the right
                column (order-2) with the footer visible. */}
            {/* Jun 12 v2 — generating / result / error all moved to the
                full-width RENDER SECTION below the panels. The right
                panel is now a pure design surface: product picker OR
                product header + confirm preview + Generate footer. */}
            {productPickerOpen ? (
              <>
                {/* PICKER MODE — inline StepUpload (extracted reuse from
                    /appv2/recreate). Defaults to Library tab when products
                    exist; falls back to URL/upload otherwise. */}
                <div className="flex-shrink-0 px-5 py-3 border-b border-surface-dark/20">
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark flex items-center gap-2">
                    <i className="ri-image-add-line text-base text-primary" />
                    Select Product
                  </h2>
                  {/* Subtext is redundant with the tab labels below on mobile —
                      hide it there to reclaim vertical space; keep on sm+. */}
                  <p className="hidden sm:block text-xs text-dark-muted mt-1">
                    Pick from your library, paste a product URL, or upload
                    an image.
                  </p>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto sidebar-scroll px-5 py-3">
                  <StepUpload
                    uploadedImages={productImageUrl ? [productImageUrl] : []}
                    maxImages={1}
                    onUpload={onStepUploadCallback}
                    platformId={`appv3-${routeConfig.category}`}
                    savedProducts={savedProducts ?? []}
                    savedProductsLoading={savedProducts === null}
                    onSavedProductSelect={onPickSavedProduct}
                    selectedSavedProductId={selectedSavedProductId}
                    onScrapeSuccess={onScrapeSuccess}
                    tabsMode
                    compact
                    compactPreview
                  />
                </div>
                {/* Footer: Continue — disabled with no product, or (Fashion)
                    when the picked product isn't a fashion item. */}
                <div className="flex-shrink-0 border-t border-surface-dark/20 px-5 py-3 flex items-center justify-between gap-3">
                  {fashionProductMismatch ? (
                    <p className="text-xs text-amber-600 flex items-center gap-1.5 flex-1 min-w-0">
                      <i className="ri-error-warning-line text-sm flex-shrink-0" />
                      <span>This isn't a fashion item — pick an apparel, footwear, or accessory product to continue.</span>
                    </p>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={onProductPickerContinue}
                    disabled={!productImageUrl || fashionProductMismatch}
                    className="h-11 min-w-[200px] px-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    Continue
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* PRODUCT HEADER — small thumb + name + Edit button. Tops
                    the right panel in PREVIEW mode so the user always sees
                    which product is in play. */}
                <div className="flex-shrink-0 px-5 py-3 border-b border-surface-dark/20 flex items-center gap-3">
                  {productImageUrl && (
                    <img
                      src={productImageUrl}
                      alt={productName || 'product'}
                      className="w-12 h-12 rounded-lg object-cover border border-surface-dark/30 bg-surface flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-primary leading-none mb-0.5">
                      Product
                    </p>
                    <p className="text-sm font-bold text-dark truncate leading-tight">
                      {productName || 'Untitled product'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onProductEdit}
                    disabled={generating}
                    title={generating ? 'Locked while generating' : undefined}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-xs font-bold uppercase tracking-wider transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-surface-dark/40 disabled:hover:text-dark"
                  >
                    <i className="ri-edit-2-line" />
                    Edit
                  </button>
                </div>

                {/* Preview body. CONSTANT box that does NOT resize when a
                    product loads — empty state and PreviewCanvas both
                    render inside the same outer container. */}
                <div className="relative flex-1 min-h-0 p-5">
                  <div className="w-full h-full rounded-xl bg-surface/40 border border-surface-dark/30 flex items-center justify-center overflow-hidden">
                    {!hasMedia ? (
                      <FashionPreviewEmpty />
                    ) : showDeck ? (
                      // Deck preview (Jun 12 v4) — 2+ AI templates stack like
                      // a deck of cards (newest on top, older peeking behind).
                      // Caption adapts: single → N images, carousel → 1 post.
                      <TemplateDeckPreview
                        templates={selectedAiTemplatesResolved}
                        productImageUrl={productImageUrl!}
                        productName={productName}
                        imageCount={matrixImageCount}
                        isCarousel={footerFormat === 'carousel'}
                      />
                    ) : showTemplateConfirm ? (
                      // Template + product confirmation (Jun 12) — the
                      // pre-Generate review. "Back to design" after a
                      // result returns exactly here (template + product
                      // are still selected; only the result clears).
                      <TemplateConfirmPreview
                        templateName={selectedTemplatePreview!.name}
                        templateImageUrl={selectedTemplatePreview!.imageUrl}
                        productImageUrl={productImageUrl!}
                        productName={productName}
                      />
                    ) : (
                      // "Click Generate" placeholder (Create tab with a
                      // product but no template preview). Generating /
                      // result / error states all render in the
                      // full-width section below — never here.
                      // Jun 14 — when Templates tab is active but no
                      // template is picked yet, swap the message to
                      // guide the user back to the LEFT panel.
                      <PreviewCanvas
                        aspectRatio={aspectRatio}
                        productImageUrl={productImageUrl}
                        generating={false}
                        error={null}
                        aiResult={null}
                        generatedImage={null}
                        frameCount={frameCount}
                        onBackToEdit={handleBackToEdit}
                        onRetry={onRetry}
                        bare
                        needsTemplate={
                          designMode === 'template'
                          && !selectedTemplateId
                          && selectedAiTemplateIds.length === 0
                        }
                      />
                    )}
                  </div>
                </div>

                {/* Footer (Jun 12 v3) — TWO rows:
                    Row 1 = six individual labeled setting chips. Left
                    group = output settings (Aspect / Format / Quality);
                    right group = brand settings (Logo / Text / Brand
                    colors). Each chip shows its current value + opens its
                    own popover (nothing truncated into combined pills).
                    Row 2 = ONE full-width GENERATE IMAGE button.
                    The generating + result states never render this far —
                    both take over via the full-width render section. */}
                {/* Desktop-only footer (Jul 2). The generate controls now
                    live in `generateControls`; on desktop they render here in
                    the right-panel footer, on mobile in their own fixed
                    Panel 3 below Templates (xl:hidden panel after the grid). */}
                <div className="hidden xl:block flex-shrink-0 border-t border-surface-dark/20 px-5 py-4 space-y-3">
                  {generateControls}
                </div>
              </>
            )}
          </div>

          {/* ===== Panel 3 · GENERATE (mobile only, Jul 2) ===== */}
          {/* Desktop shows the generate controls in the right-panel footer
              above. On mobile they get their OWN fixed panel here — the 3rd
              grid child (order-3), below Templates — so the flow reads
              product → templates → generate. mobileGenerateRef is the anchor
              the Templates "Continue" button scrolls to. Hidden at xl (the
              footer covers desktop). */}
          <div
            ref={mobileGenerateRef}
            className="order-3 xl:hidden rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden"
          >
            <div className="px-5 py-3 border-b border-surface-dark/20">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark flex items-center gap-2">
                <i className="ri-sparkling-2-line text-base text-primary" />
                Generate
              </h2>
              <p className="text-xs text-dark-muted mt-1">
                Set your output options, then generate.
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              {generateControls}
            </div>
          </div>
        </div>

        {/* ============ RENDER SECTION (Jun 12 v2 — accordion) ============
            Full-width panel (left + right combined) that opens from below
            when Generate fires. Hosts the generating takeover, the result
            (floating action row), and errors. Carousels lay out 4-across
            here — the whole point of going full-width. When the user goes
            Back to edit, this collapses to a slim summary bar instead of
            disappearing (the render is auto-saved + one click away). */}
        {showRenderSection && (
          <div
            ref={renderSectionRef}
            className={`${editorMode ? '' : 'mt-5'} ${
              // Mobile/tablet result view is short — center it in the remaining
              // viewport so the page doesn't leave a big empty gap below the
              // card (the h-screen sidebar forces the shell to full height).
              isNarrow && renderOpen && hasResult
                ? 'min-h-[calc(100dvh-9rem)] flex flex-col justify-center'
                : ''
            }`}
          >
            <style>{`
              @keyframes stv-reveal {
                from { opacity: 0; transform: translateY(28px); }
                to { opacity: 1; transform: none; }
              }
              .studio-render-reveal { animation: stv-reveal 0.35s cubic-bezier(0.22, 1, 0.36, 1); }
            `}</style>
            {renderOpen && hasResult && displayFrames.length > 0 && !generating && !finishSaving ? (
              // FINISHING SURFACE (Jun 12 v2 — matches the Figma mock
              // exactly): LEFT = layer rail + black property panel card,
              // prompt card with EDIT (1 CR), one row of four buttons.
              // RIGHT = ONLY the rendered image (+ frame thumbs on
              // carousels). No presets, no extra chrome.
              <div className="studio-render-reveal">
                {/* Jun 30 — text-overlay tile → the SAME editor as /creatives/:id,
                    backed by the auto-saved post. One editor, edited in-wizard.
                    Non-overlay tiles keep the canvas editor below. The top panel
                    (template + product + back/change-template) is unchanged. */}
                {isNarrow ? (
                  <div className="rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden">
                    {/* Mobile/tablet: skip the layer editor (the LAYERS /
                        PROPERTIES panels + drag canvas need desktop width). Show
                        the rendered ad + Post/Download + a note that editing
                        lives on desktop. */}
                    <div className="p-3">
                      <img
                        src={displayFrames[activeFrameIdx] ?? displayFrames[0]}
                        alt="Your generated ad"
                        className="w-full rounded-xl border border-surface-dark/15"
                      />
                      {displayFrames.length > 1 && (
                        <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1">
                          {displayFrames.map((f, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => onActiveFrameChange(i)}
                              className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${i === activeFrameIdx ? 'border-primary' : 'border-transparent'}`}
                            >
                              <img src={f} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="px-4 pb-4 space-y-3">
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-primary-light/40 text-primary text-xs leading-relaxed">
                        <i className="ri-computer-line text-sm mt-0.5 flex-shrink-0" />
                        <span>Editing (text, logo &amp; CTA) is available on desktop. Here you can post or download your ad.</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={onScheduleShare}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors cursor-pointer shadow-md shadow-primary/25"
                        >
                          <i className="ri-send-plane-line" />
                          Post
                        </button>
                        <button
                          type="button"
                          onClick={onDownloadResult}
                          disabled={downloadingResult}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border border-surface-dark/40 text-dark text-sm font-bold hover:border-primary hover:text-primary transition-colors cursor-pointer disabled:opacity-50"
                        >
                          {downloadingResult ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-download-2-line" />
                          )}
                          Download
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handleBackToEdit}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-dark-muted hover:text-dark text-sm font-medium transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line" />
                        Back to design
                      </button>
                    </div>
                  </div>
                ) : overlayPost ? (
                  <OverlayEditor post={overlayPost} onSaved={onOverlaySaved} />
                ) : flatEditorPost ? (
                  // Jul 17 — Product Shot flat render → the SHARED CreativeEditor
                  // (v4), INLINE + embedded (its own header hidden). The persistent
                  // page header above ("Product Shot" + "Back to design") is the
                  // single consistent header across the whole flow. Baked pixels
                  // stay; overlays go on top. Sized to fill the viewport height.
                  <div className={`relative ${fitPanelH} ${loadingPanelClass} overflow-hidden`}>
                    <CreativeEditor
                      initialPostId={flatEditorPost.id}
                      // Seed only until the user saves an edit (fork). After a
                      // fork, drop the seed so a Back-to-design → return loads
                      // the EDITED version from the DB (not the stale frame).
                      initialPost={editorForked ? undefined : flatEditorPost}
                      // Carousel → hand the editor all frames (it renders a
                      // thumbnail strip + per-frame overlays/AI-edit). Singles
                      // pass a 1-frame array = the single-image editor.
                      initialFrames={!editorForked && displayFrames.length > 1 ? displayFrames : undefined}
                      onActivePostChange={onEditorFork}
                      embedded
                      backLabel="Back to design"
                      onBack={handleBackToEdit}
                      showDelete={false}
                      suggestedHeadline={suggestedFrames[activeFrameIdx]?.headline ?? suggestedFrames[0]?.headline ?? null}
                      suggestedTagline={suggestedFrames[activeFrameIdx]?.tagline ?? suggestedFrames[0]?.tagline ?? null}
                      onRequestAiText={onRequestAiText}
                    />
                  </div>
                ) : (
                <StudioFinishEditor
                  frames={displayFrames}
                  aspectRatio={renderedAspect}
                  activeFrameIdx={activeFrameIdx}
                  onActiveFrameChange={onActiveFrameChange}
                  overlays={textOverlays}
                  onOverlaysChange={onTextOverlaysChange}
                  logoOverlays={logoOverlays}
                  onLogoOverlaysChange={onLogoOverlaysChange}
                  brandLogoUrl={brandLogoUrl}
                  brandColors={brandColors}
                  suggestedHeadline={suggestedFrames[activeFrameIdx]?.headline ?? suggestedFrames[0]?.headline ?? null}
                  suggestedTagline={suggestedFrames[activeFrameIdx]?.tagline ?? suggestedFrames[0]?.tagline ?? null}
                  editPrompt={editPrompt}
                  onEditPromptChange={onEditPromptChange}
                  promptEditing={promptEditing}
                  editError={editError}
                  onPromptEdit={onPromptEdit}
                  onBackToDesign={handleBackToEdit}
                  isFree={isFree}
                  frameCount={frameCount}
                  retryOpen={popover === 'retry'}
                  onToggleRetry={() => togglePop('retry')}
                  onCloseRetry={closePop}
                  onRegenerateWithQuality={(q) => { closePop(); onRegenerateWithQuality(q); }}
                  saveState={saveState}
                  onRetrySave={onSaveToCreatives}
                  onSchedule={onScheduleShare}
                  onDownload={onDownloadResult}
                  downloading={downloadingResult}
                  onManualSave={onManualSave}
                  creativeDirty={creativeDirty}
                />
                )}
              </div>
            ) : renderOpen ? (
              <div className={`studio-render-reveal ${loadingPanelClass} flex flex-col overflow-hidden ${editorMode ? fitPanelH : isNarrow ? 'h-[520px]' : 'h-[800px]'}`}>
                {generating || finishSaving ? (
                  <StudioGeneratingTakeover frameCount={frameCount} />
                ) : (
                  /* Error card — generation failed, no result to show. */
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="max-w-md text-center">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center">
                        <i className="ri-error-warning-line text-2xl text-red-500" />
                      </div>
                      <p className="text-base font-bold text-dark mt-4">
                        Generation failed
                      </p>
                      <p className="text-sm text-dark-muted mt-1.5 leading-relaxed">
                        {error}
                      </p>
                      <div className="mt-5 flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={handleBackToEdit}
                          className="h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer"
                        >
                          <i className="ri-arrow-left-line text-base" />
                          Back to edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onRetry()}
                          className="h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer"
                        >
                          <i className="ri-refresh-line text-base" />
                          Try again
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <StudioRenderSummaryBar
                generating={generating}
                error={hasResult ? null : error}
                thumbSrc={resultThumbSrc}
                isCarousel={frameCount > 1}
                editsBehind={editsSinceGenerate}
                onOpen={() => setRenderOpen(true)}
              />
            )}
          </div>
        )}
      </div>

      <SelectProductModal
        open={productModalOpen}
        onClose={onCloseProductModal}
        savedProducts={savedProducts}
        selectedSavedProductId={selectedSavedProductId}
        onPickSaved={onPickSavedProduct}
        onUploadFile={onUploadFile}
      />
    </AppLayoutV2>
  );
}

// ===========================================================================
// FASHION STUDIO SUB-COMPONENTS
// ===========================================================================

// StudioDesignSummaryBar (Jun 12 v2) — the COLLAPSED state of the two
// design panels while the render section is expanded. Shows the picked
// template thumbnail + product thumbnail + a compact settings summary.
// The whole bar is one button; hovering reveals the "Back to edit"
// overlay (user-spec'd), with a static pill on the right as the
// always-visible affordance for touch / non-hover.
function StudioDesignSummaryBar({
  templateName, templateImageUrl, productImageUrl, productName,
  outputLabel, onBackToEdit,
}: {
  templateName: string | null;
  templateImageUrl: string | null;
  productImageUrl: string | null;
  productName: string;
  outputLabel: string;
  onBackToEdit: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onBackToEdit}
      title="Back to edit"
      className="group relative w-full rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden text-left cursor-pointer"
    >
      <div className="flex items-center gap-4 px-5 py-3.5">
        {/* Template + product thumbnails. Freestyle / Create-tab designs
            have no sample image → gradient sparkle tile instead. */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {templateImageUrl ? (
            <img
              src={templateImageUrl}
              alt={templateName ?? 'template'}
              className="w-14 h-14 rounded-xl object-cover border border-surface-dark/30"
              draggable={false}
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-200 via-indigo-200 to-sky-200 border border-surface-dark/20 flex items-center justify-center">
              <i className="ri-sparkling-2-fill text-xl text-white drop-shadow" />
            </div>
          )}
          {productImageUrl && (
            <>
              <i className="ri-add-line text-base text-dark-muted/60" />
              <img
                src={productImageUrl}
                alt={productName || 'product'}
                className="w-14 h-14 rounded-xl object-cover border border-surface-dark/30 bg-surface"
                draggable={false}
              />
            </>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary leading-none mb-1">
            Design
          </p>
          <p className="text-sm font-bold text-dark truncate">
            {templateName ?? 'AI design'}
            {productName ? ` · ${productName}` : ''}
          </p>
          <p className="text-[11px] text-dark-muted mt-0.5 truncate">
            {outputLabel}
          </p>
        </div>
        <span className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-surface-dark/40 text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all">
          <i className="ri-arrow-left-line text-base" />
          Back to edit
        </span>
      </div>
      {/* Hover overlay — frosted wash + centered CTA over the whole bar. */}
      <div className="absolute inset-0 bg-white/55 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
        <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-dark text-white text-xs font-bold uppercase tracking-[0.12em] shadow-lg">
          <i className="ri-arrow-left-line text-base" />
          Back to edit
        </span>
      </div>
    </button>
  );
}

// StudioRenderSummaryBar (Jun 12 v2) — the COLLAPSED state of the render
// section while the design panels are expanded. Keeps the latest render
// (or the in-flight / failed one) one click away instead of discarding
// it on "Back to edit". Shows the edits-behind count so the user knows
// the collapsed render no longer matches their current settings.
function StudioRenderSummaryBar({
  generating, error, thumbSrc, isCarousel, editsBehind, onOpen,
}: {
  generating: boolean;
  error: string | null;
  thumbSrc: string | null;
  isCarousel: boolean;
  editsBehind: number;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title="View render"
      className="group w-full rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] overflow-hidden text-left cursor-pointer hover:shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(214,41,125,0.22)] transition-shadow"
    >
      <div className="flex items-center gap-4 px-5 py-3.5">
        {generating ? (
          <div className="w-14 h-14 rounded-xl bg-surface border border-surface-dark/30 flex items-center justify-center flex-shrink-0">
            <i className="ri-loader-4-line animate-spin text-xl text-primary" />
          </div>
        ) : error ? (
          <div className="w-14 h-14 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-xl text-red-500" />
          </div>
        ) : thumbSrc ? (
          <img
            src={thumbSrc}
            alt="Latest render"
            className="w-14 h-14 rounded-xl object-cover border border-surface-dark/30 flex-shrink-0"
            draggable={false}
          />
        ) : null}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary leading-none mb-1">
            Render
          </p>
          <p className="text-sm font-bold text-dark truncate">
            {generating
              ? 'Rendering your design…'
              : error
                ? 'Generation failed'
                : isCarousel
                  ? 'Carousel render ready'
                  : 'Render ready'}
          </p>
          {!generating && !error && editsBehind > 0 && (
            <p className="text-[11px] font-bold text-amber-600 mt-0.5 truncate">
              {editsBehind} edit{editsBehind === 1 ? '' : 's'} since this
              render — Generate to refresh
            </p>
          )}
          {error && (
            <p className="text-[11px] text-red-600 mt-0.5 truncate">{error}</p>
          )}
        </div>
        <span className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-surface-dark/40 group-hover:border-primary group-hover:text-primary text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all">
          View render
          <i className="ri-arrow-down-s-line text-base" />
        </span>
      </div>
    </button>
  );
}

// StudioGeneratingTakeover (Jun 12) — replaces the ENTIRE right panel
// (product header + preview + footer) while a render is in flight. Same
// visual family as AiResultViewer's loading skeleton (drifting gradient +
// shimmer + sparkles + gradient progress ring with the exponential-
// approach curve) but full-panel so output/brand/product controls can't
// change mid-generation. The LEFT panel deliberately stays live — edits
// there don't affect the in-flight render and apply to the NEXT generate.
// Keyframes prefixed `stk-` so this stays self-sufficient.
function StudioGeneratingTakeover({ frameCount }: { frameCount: number }) {
  const isCarousel = frameCount > 1;
  const expectedMs = isCarousel ? 70_000 : 18_000;
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const t0 = Date.now();
    const id = window.setInterval(() => setElapsedMs(Date.now() - t0), 200);
    return () => window.clearInterval(id);
  }, []);

  // Honest exponential approach capped at 92% — never "fake-completes"
  // before the API actually returns. Same math as AiLoadingSkeleton, so
  // both panels' percentages stay in lockstep (both timers start when
  // `generating` flips true).
  const tau = expectedMs / Math.log(5);
  const progress = Math.min(92, 92 * (1 - Math.exp(-elapsedMs / tau)));
  const progressPct = Math.round(progress);

  const sparkles = [
    { top: '12%', left: '18%', size: 9, delay: '0s',   dur: '3.2s' },
    { top: '22%', left: '80%', size: 7, delay: '0.6s', dur: '2.8s' },
    { top: '58%', left: '10%', size: 11, delay: '1.1s', dur: '3.6s' },
    { top: '72%', left: '86%', size: 8, delay: '0.3s', dur: '3.0s' },
    { top: '38%', left: '92%', size: 6, delay: '1.4s', dur: '2.5s' },
    { top: '86%', left: '40%', size: 10, delay: '0.9s', dur: '3.4s' },
  ];

  return (
    <div className="relative flex-1 min-h-0 overflow-hidden">
      {/* Drifting gradient backdrop */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
          backgroundSize: '300% 300%',
          animation: 'stk-bgshift 8s ease-in-out infinite',
        }}
      />
      {/* Shimmer sweep */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
          backgroundSize: '200% 100%',
          animation: 'stk-shimmer 2.4s linear infinite',
        }}
      />
      {/* Floating sparkle particles */}
      <div className="absolute inset-0 pointer-events-none">
        {sparkles.map((p, i) => (
          <i
            key={i}
            className="ri-sparkling-2-fill text-primary absolute"
            style={{
              top: p.top,
              left: p.left,
              fontSize: `${p.size * 2}px`,
              animation: `stk-float ${p.dur} ease-in-out ${p.delay} infinite, stk-twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
              opacity: 0.7,
            }}
          />
        ))}
      </div>
      {/* Center cluster — ring + copy */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-8 text-center">
        <div className="relative w-28 h-28">
          <svg className="w-28 h-28 -rotate-90" viewBox="0 0 112 112">
            <defs>
              <linearGradient id="stk-ring" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="50%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#38bdf8" />
              </linearGradient>
            </defs>
            <circle cx="56" cy="56" r="48" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="8" />
            <circle
              cx="56" cy="56" r="48" fill="none"
              stroke="url(#stk-ring)" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 48}
              strokeDashoffset={2 * Math.PI * 48 * (1 - progress / 100)}
              style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <i
              className="ri-sparkling-2-fill text-primary text-3xl"
              style={{ animation: 'stk-spin 4s linear infinite, stk-twinkle 1.6s ease-in-out infinite' }}
            />
          </div>
        </div>
        <div>
          <p className="font-display font-bold text-dark text-2xl tabular-nums">{progressPct}%</p>
          <p className="text-base font-bold text-dark mt-1.5">
            AI is crafting your ad…
          </p>
          <p className="text-sm text-dark-muted mt-1 max-w-[320px] mx-auto leading-relaxed">
            Controls are locked while we render — usually
            {isCarousel ? ' about a minute for 4 frames.' : ' around 20 seconds.'}
          </p>
        </div>
      </div>
      <style>{`
        @keyframes stk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes stk-bgshift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes stk-float   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes stk-twinkle { 0%,100% { opacity: 0.35; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.15); } }
        @keyframes stk-spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ===========================================================================
// StudioFinishEditor (Jun 12 v2.1) — the render panel's RESULT state.
// Layout per the user's Figma prototype (STRUCTURE only — styling uses the
// app's existing design system, not the wireframe's black panel):
//   LEFT (589px) — ONE white panel (same chrome as every Studio panel):
//     • Layer rail: Logo tile + CTA tile (each tile IS its layer — add on
//       first click, select afterwards; no separate "add" buttons), then
//       the text line items with a ghost "+ Text" row LAST (using the
//       last item spawns the next one below — no wasted vertical space).
//     • Property editor for the selected layer (text + AI fill, font
//       size, weight, case, color — or size/opacity/tint for the logo).
//     • "Edit with prompt…" + EDIT (1 CR).
//     • ONE action row: BACK TO DESIGN · ADJ QUALITY · SCHEDULE · DOWNLOAD.
//   RIGHT (843px) — ONLY the rendered image (+ frame thumbs on carousels).
// Drag/clamp/scale machinery is the canonical StepText implementation
// (memory: feedback_text_drag) copied verbatim.
// ===========================================================================

// width/height ratio per aspect — caps the preview height (~850px) while
// width anchors the aspect box (gotcha #50 family).
const FINISH_ASPECT_RATIO_W_OVER_H: Record<ProtoAspect, number> = {
  '1:1': 1,
  '4:5': 0.8,
  '9:16': 0.5625,
  '16:9': 16 / 9,
};

function StudioFinishEditor({
  frames, aspectRatio, activeFrameIdx, onActiveFrameChange,
  overlays, onOverlaysChange,
  logoOverlays, onLogoOverlaysChange,
  brandLogoUrl, brandColors,
  suggestedHeadline, suggestedTagline,
  editPrompt, onEditPromptChange, promptEditing, editError, onPromptEdit,
  onBackToDesign,
  isFree, frameCount,
  retryOpen, onToggleRetry, onCloseRetry, onRegenerateWithQuality,
  saveState, onRetrySave,
  onSchedule, onDownload, downloading,
  onManualSave, creativeDirty,
}: {
  frames: string[];
  aspectRatio: ProtoAspect;
  activeFrameIdx: number;
  onActiveFrameChange: (i: number) => void;
  overlays: TextOverlay[];
  onOverlaysChange: (o: TextOverlay[]) => void;
  logoOverlays: LogoOverlay[];
  onLogoOverlaysChange: (l: LogoOverlay[]) => void;
  brandLogoUrl: string | null;
  brandColors: string[];
  suggestedHeadline: string | null;
  suggestedTagline: string | null;
  editPrompt: string;
  onEditPromptChange: (v: string) => void;
  promptEditing: boolean;
  editError: string | null;
  onPromptEdit: () => void;
  onBackToDesign: () => void;
  isFree: boolean;
  frameCount: number;
  retryOpen: boolean;
  onToggleRetry: () => void;
  onCloseRetry: () => void;
  onRegenerateWithQuality: (q: Quality) => void;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  onRetrySave: () => void;
  onSchedule: () => void;
  onDownload: () => void;
  downloading: boolean;
  onManualSave: () => void;
  creativeDirty: boolean;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [previewW, setPreviewW] = useState(0);
  // Stage (the filled right-panel box) measured size — the image box is
  // sized to fit INSIDE it (object-contain math) so non-1:1 aspects letterbox
  // on the stage instead of leaving a gap next to the editor (point 5).
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // Refs to avoid stale closures inside long-lived drag handlers — same
  // shape as StepText.
  const overlaysRef = useRef(overlays); overlaysRef.current = overlays;
  const logoOverlaysRef = useRef(logoOverlays); logoOverlaysRef.current = logoOverlays;
  const activeIdRef = useRef(activeId); activeIdRef.current = activeId;
  const draggingLogoRef = useRef(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragBoxRef = useRef<{ w: number; h: number } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  // ---- Drag-to-position (canonical StepText implementation) ----
  useEffect(() => {
    if (!draggingId) return;
    const updateFromPoint = (clientX: number, clientY: number) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;

      // Logo path — top-left anchor model, clamp directly with box dims.
      if (draggingLogoRef.current) {
        const logo = logoOverlaysRef.current.find((l) => l.id === draggingId);
        if (!logo) return;
        const cursorX = (clientX - rect.left) / rect.width;
        const cursorY = (clientY - rect.top) / rect.height;
        const off = dragOffsetRef.current;
        const targetX = off ? cursorX - off.dx : cursorX;
        const targetY = off ? cursorY - off.dy : cursorY;
        const box = dragBoxRef.current;
        const wFrac = box ? box.w / rect.width : logo.widthFrac;
        const hFrac = box ? box.h / rect.height : logo.widthFrac;
        const clampedX = Math.max(0, Math.min(1 - wFrac, targetX));
        const clampedY = Math.max(0, Math.min(1 - hFrac, targetY));
        onLogoOverlaysChange(
          logoOverlaysRef.current.map((l) =>
            l.id === draggingId ? { ...l, x: clampedX, y: clampedY } : l,
          ),
        );
        return;
      }

      // Text path — anchor-aware clamp by the rendered box bounds so the
      // block can never escape the frame.
      const dragged = overlaysRef.current.find((o) => o.id === draggingId);
      if (!dragged) return;
      const cursorX = (clientX - rect.left) / rect.width;
      const cursorY = (clientY - rect.top) / rect.height;
      const off = dragOffsetRef.current;
      const targetX = off ? cursorX - off.dx : cursorX;
      const targetY = off ? cursorY - off.dy : cursorY;
      const box = dragBoxRef.current;
      const wFrac = box ? box.w / rect.width : 0;
      const hFrac = box ? box.h / rect.height : 0;
      const { x, y } = clampAnchor(targetX, targetY, wFrac, hFrac, dragged.anchor);
      onOverlaysChange(
        overlaysRef.current.map((o) =>
          o.id === draggingId ? { ...o, x, y, positionId: '' } : o,
        ),
      );
    };
    const onMouseMove = (e: MouseEvent) => updateFromPoint(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) updateFromPoint(t.clientX, t.clientY);
    };
    const onUp = () => {
      setDraggingId(null);
      dragBoxRef.current = null;
      dragOffsetRef.current = null;
      draggingLogoRef.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId, onOverlaysChange, onLogoOverlaysChange]);

  // ---- Preview width → font scale (DOM wrap ≡ canvas wrap) ----
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setPreviewW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const previewFontScale = previewW > 0 ? previewW / REFERENCE_CANVAS_WIDTH : 1;

  // ---- Stage size → image box size (object-contain inside the filled stage) ----
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setStageSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Derived per-frame data ----
  // CTA is a text layer with an id prefix — the rail's CTA tile maps to
  // it 1:1 (one CTA per frame); everything else is a free text item.
  const currentOverlays = overlays.filter((o) => o.imageIndex === activeFrameIdx);
  const ctaLayer = currentOverlays.find((o) => o.id.startsWith('cta-')) || null;
  const textLayers = currentOverlays.filter((o) => !o.id.startsWith('cta-'));
  const currentLogos = logoOverlays.filter((l) => l.imageIndex === activeFrameIdx);
  const logoLayer = currentLogos[0] || null;
  const refH = aspectToRefHeight(aspectRatio);
  const resolvedCurrent = useMemo(
    () => resolveStackedLayers(currentOverlays, REFERENCE_CANVAS_WIDTH, refH) as TextOverlay[],
    [currentOverlays, refH],
  );
  const activeOverlay = currentOverlays.find((o) => o.id === activeId) || null;
  const activeLogo = currentLogos.find((l) => l.id === activeId) || null;
  const activeImage = frames[activeFrameIdx] || '';

  // Complementary text-color suggestions sampled from the active frame's
  // background. Recomputed whenever the frame image changes (prompt-edit,
  // frame switch, new render). [] when the image is all-neutral.
  const [bgColors, setBgColors] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!activeImage) { setBgColors([]); return; }
    suggestTextColorsFromImage(activeImage, 3)
      .then((cols) => { if (!cancelled) setBgColors(cols); })
      .catch(() => { if (!cancelled) setBgColors([]); });
    return () => { cancelled = true; };
  }, [activeImage]);

  const logoFilterCss = (tint: LogoTint | undefined): string | undefined => {
    if (tint === 'black') return 'brightness(0)';
    if (tint === 'white') return 'brightness(0) invert(1)';
    return undefined;
  };

  // ---- Layer mutation ----
  const updateOverlay = (id: string, updates: Partial<TextOverlay>) => {
    onOverlaysChange(overlaysRef.current.map((o) => (o.id === id ? { ...o, ...updates } : o)));
  };
  const updateLogo = (id: string, updates: Partial<LogoOverlay>) => {
    onLogoOverlaysChange(
      logoOverlaysRef.current.map((l) => (l.id === id ? { ...l, ...updates } : l)),
    );
  };
  const removeLayer = (id: string) => {
    onOverlaysChange(overlaysRef.current.filter((o) => o.id !== id));
    onLogoOverlaysChange(logoOverlaysRef.current.filter((l) => l.id !== id));
    if (activeIdRef.current === id) setActiveId(null);
  };

  // ---- Layer creation ----
  // New text items default to the next UNUSED piece of AI copy (headline
  // first, then tagline) so adding layers builds the post instead of
  // dropping placeholders.
  const nextDefaultText = (): string => {
    const used = new Set(textLayers.map((o) => o.text));
    if (suggestedHeadline && !used.has(suggestedHeadline)) return suggestedHeadline;
    if (suggestedTagline && !used.has(suggestedTagline)) return suggestedTagline;
    return 'Your text here';
  };
  const addTextLayer = () => {
    const next: TextOverlay = {
      kind: 'text',
      id: `text-${Date.now()}`,
      imageIndex: activeFrameIdx,
      text: nextDefaultText(),
      x: 0.5,
      y: 0.4,
      anchor: 'mc',
      maxWidthFrac: 0.82,
      textAlign: 'center',
      fontSize: 64,
      fontWeight: 'bold',
      fontFamily: 'Manrope',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      maxLines: 3,
    };
    onOverlaysChange([...overlaysRef.current, next]);
    setActiveId(next.id);
  };
  const addCtaLayer = () => {
    if (ctaLayer) { setActiveId(ctaLayer.id); return; }
    const next: TextOverlay = {
      kind: 'text',
      id: `cta-${Date.now()}`,
      imageIndex: activeFrameIdx,
      text: 'SHOP NOW',
      x: 0.5,
      y: 0.84,
      anchor: 'mc',
      maxWidthFrac: 0.6,
      textAlign: 'center',
      fontSize: 34,
      fontWeight: 'bold',
      fontFamily: 'Manrope',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      maxLines: 1,
      backgroundColor: '#0F0F1E',
      backgroundPadding: 18,
    };
    onOverlaysChange([...overlaysRef.current, next]);
    setActiveId(next.id);
  };
  const addLogoLayer = () => {
    if (logoLayer) { setActiveId(logoLayer.id); return; }
    if (!brandLogoUrl) return;
    const defaultW = 0.18;
    const next: LogoOverlay = {
      id: `logo-${Date.now()}`,
      imageIndex: activeFrameIdx,
      src: brandLogoUrl,
      x: 1 - defaultW - 0.04,
      y: 1 - defaultW - 0.04,
      widthFrac: defaultW,
      opacity: 1,
      tint: 'original',
    };
    onLogoOverlaysChange([...logoOverlaysRef.current, next]);
    setActiveId(next.id);
  };

  // AI fill: first press inserts the AI headline, next press the tagline
  // (toggles when both exist).
  const aiFillText = () => {
    if (!activeOverlay || !suggestedHeadline) return;
    const next = activeOverlay.text === suggestedHeadline && suggestedTagline
      ? suggestedTagline
      : suggestedHeadline;
    updateOverlay(activeOverlay.id, { text: next });
  };

  const WEIGHT_OPTIONS: { value: string; cls: string; title: string }[] = [
    { value: 'normal', cls: 'font-normal', title: 'Regular' },
    { value: 'semibold', cls: 'font-semibold', title: 'Semibold' },
    { value: 'bold', cls: 'font-extrabold', title: 'Bold' },
  ];
  const CASE_OPTIONS: { value: TextOverlay['textTransform']; label: string; title: string }[] = [
    { value: 'none', label: 'Aa', title: 'As typed' },
    { value: 'uppercase', label: 'AA', title: 'UPPERCASE' },
    { value: 'lowercase', label: 'aa', title: 'lowercase' },
  ];
  // Font families offered in the text editor. All self-hosted (fonts.ts) so
  // they're loaded for both the DOM preview and the canvas flatten; matches
  // the social-template-builder's authoring set. `fontFamily` is already a
  // field on TextOverlay and honored by both renderers.
  const FONT_OPTIONS = [
    'Manrope', 'Inter', 'DM Sans', 'Archivo', 'Space Grotesk', 'Space Mono',
    'JetBrains Mono', 'Fraunces', 'Cormorant Garamond', 'Bodoni Moda',
    'Playfair Display', 'Instrument Serif', 'Bebas Neue', 'Anton',
    // Typewolf "40 Best Google Fonts" set
    'Chivo', 'Work Sans', 'Syne', 'Libre Franklin', 'Cormorant', 'Fira Sans',
    'Eczar', 'Alegreya Sans', 'Alegreya', 'Source Sans Pro', 'Source Serif Pro',
    'Roboto', 'Inknut Antiqua', 'BioRhyme', 'Poppins', 'Archivo Narrow',
    'Libre Baskerville', 'Karla', 'Lora', 'Proza Libre', 'Spectral',
    'IBM Plex Sans', 'Montserrat', 'Lato', 'PT Sans', 'PT Serif', 'Cardo',
    'Neuton', 'Rubik', 'Open Sans', 'Inconsolata', 'Raleway', 'Merriweather',
  ];
  // 3×3 placement grid (rows top→bottom, cols left→center→right). Each id is
  // fed to snapForPosition() → {x, y, anchor, textAlign} so the text snaps to
  // the inset edge/corner and aligns inward. Mirrors the Animation placement tool.
  const PLACEMENT_CELLS = [
    'top-left', 'top-center', 'top-right',
    'middle-left', 'middle-center', 'middle-right',
    'bottom-left', 'bottom-center', 'bottom-right',
  ];
  const retryOptions: { q: Quality; label: string }[] = [
    { q: 'low', label: 'Low' },
    { q: 'medium', label: 'Medium' },
    { q: 'high', label: 'High' },
  ];

  // App design system tokens (NOT the wireframe's black panel).
  const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.18em] text-dark-muted';
  const actionPillClass = 'h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
  const chipClass = (active: boolean) => `
    w-9 h-9 inline-flex items-center justify-center rounded-lg border text-sm transition-all cursor-pointer
    ${active
      ? 'bg-primary-light text-primary border-primary'
      : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'}
  `;
  const railRowClass = (selected: boolean) => `
    w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide truncate transition-all cursor-pointer border
    ${selected
      ? 'bg-primary-light text-primary border-primary/40'
      : 'bg-surface text-dark border-transparent hover:bg-surface-dark/40'}
  `;
  const railGhostClass = 'w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border-2 border-dashed border-surface-dark/60 text-dark-muted hover:border-primary/50 hover:text-primary transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';

  // Image box size = the largest that fits INSIDE the measured stage while
  // keeping aspect (object-contain). The stage is the filled right-panel box
  // (point 5) that fills the 800px column; non-1:1 images letterbox on the
  // stage bg instead of leaving a gap. Falls back before the first measure.
  const aspectWOverH = FINISH_ASPECT_RATIO_W_OVER_H[aspectRatio] ?? 1;
  const boxW = stageSize.w > 0 && stageSize.h > 0
    ? Math.floor(Math.min(stageSize.w, stageSize.h * aspectWOverH))
    : Math.round(700 * aspectWOverH);

  return (
    // 589 + 20 (gap-5) + 843 = 1452 = the page container's inner width
    // (user-locked column split). Fixed 800px height so both columns fill it
    // and the right stage can letterbox non-1:1 images without a gap.
    /* Image-LEFT / controls-RIGHT (Jun 17 user-locked swap). Same content,
       visual order flipped via grid template + order utilities so the DOM
       (and refs/state) stays unchanged. */
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,843px)_589px] gap-5 items-stretch xl:h-[800px]">

      {/* ============ Controls panel (now on the RIGHT via order) ============ */}
      <div className="xl:order-2 rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] p-5 flex flex-col gap-4 min-h-0">

        {/* Editor — layer rail + property panel. flex-1 so the editor
            absorbs all the vertical room the image column provides. */}
        <div className="flex gap-3 flex-1 min-h-0">

          {/* Layer rail. Logo + CTA are PERSISTENT tiles (the tile IS the
              layer — first click creates, after that it selects). Text
              line items follow, ghost "+ Text" row LAST: using it spawns
              the next one below. */}
          <div className="w-[170px] flex-shrink-0 flex flex-col gap-2 overflow-y-auto sidebar-scroll">
            <button
              type="button"
              onClick={addLogoLayer}
              disabled={!logoLayer && !brandLogoUrl}
              title={!brandLogoUrl && !logoLayer
                ? 'Set up your logo in My Brand first'
                : 'Your brand logo — drag it on the image to reposition'}
              className={logoLayer ? railRowClass(activeId === logoLayer.id) : railGhostClass}
            >
              <i className={`${logoLayer ? 'ri-vip-crown-line' : 'ri-add-line'} mr-1`} />
              Logo
            </button>
            <button
              type="button"
              onClick={addCtaLayer}
              title="Call-to-action pill — drag it on the image to reposition"
              className={ctaLayer ? railRowClass(activeId === ctaLayer.id) : railGhostClass}
            >
              {ctaLayer ? ctaLayer.text || 'CTA' : (<><i className="ri-add-line mr-1" />CTA</>)}
            </button>

            {textLayers.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setActiveId(o.id)}
                className={railRowClass(activeId === o.id)}
              >
                {o.text || 'Text'}
              </button>
            ))}
            {/* Ghost row LAST — becomes a real text item on click, and a
                fresh ghost appears below it. */}
            <button type="button" onClick={addTextLayer} className={railGhostClass}>
              <i className="ri-add-line mr-1" />
              Text
            </button>
          </div>

          {/* Property panel for the selected layer — surface-tinted card
              (matches the filter-bar family), full remaining width. */}
          <div className="flex-1 min-w-0 rounded-xl bg-surface/60 border border-surface-dark/30 p-4 flex flex-col overflow-y-auto sidebar-scroll">
            {activeLogo ? (
              <>
                <p className={eyebrowClass}>Size</p>
                <input
                  type="range"
                  min={5}
                  max={50}
                  value={Math.round(activeLogo.widthFrac * 100)}
                  onChange={(e) => updateLogo(activeLogo.id, { widthFrac: Number(e.target.value) / 100 })}
                  className="w-full accent-primary mt-2"
                />
                <p className={`${eyebrowClass} mt-4`}>Opacity</p>
                <input
                  type="range"
                  min={20}
                  max={100}
                  value={Math.round(activeLogo.opacity * 100)}
                  onChange={(e) => updateLogo(activeLogo.id, { opacity: Number(e.target.value) / 100 })}
                  className="w-full accent-primary mt-2"
                />
                <p className={`${eyebrowClass} mt-4 mb-2`}>Color</p>
                <div className="flex gap-2">
                  {(['original', 'black', 'white'] as LogoTint[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => updateLogo(activeLogo.id, { tint: t })}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-bold capitalize transition-all cursor-pointer ${
                        (activeLogo.tint ?? 'original') === t
                          ? 'bg-primary-light text-primary border-primary'
                          : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => removeLayer(activeLogo.id)}
                  className="mt-auto pt-4 self-start text-xs font-bold text-red-500 hover:text-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1" />
                  Delete layer
                </button>
              </>
            ) : activeOverlay ? (
              <>
                <p className={`${eyebrowClass} mb-2`}>
                  Text content{suggestedHeadline ? ' + AI generate' : ''}
                </p>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={activeOverlay.text}
                    onChange={(e) => updateOverlay(activeOverlay.id, { text: e.target.value })}
                    className="flex-1 min-w-0 h-10 px-3 rounded-lg bg-white border border-dark-muted/40 focus:border-primary focus:outline-none text-sm text-dark placeholder:text-dark-muted/60"
                    placeholder="Type your text…"
                  />
                  {suggestedHeadline && (
                    <button
                      type="button"
                      onClick={aiFillText}
                      title="Fill with AI-written copy (press again for the tagline)"
                      className="w-10 h-10 flex-shrink-0 inline-flex items-center justify-center rounded-lg bg-primary hover:bg-primary-hover text-white transition-all cursor-pointer"
                    >
                      <i className="ri-sparkling-2-line" />
                    </button>
                  )}
                </div>

                <p className={`${eyebrowClass} mt-3 mb-2`}>Font</p>
                <select
                  value={activeOverlay.fontFamily}
                  onChange={(e) => updateOverlay(activeOverlay.id, { fontFamily: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg bg-white border border-dark-muted/40 focus:border-primary focus:outline-none text-sm text-dark cursor-pointer"
                  style={{ fontFamily: `'${activeOverlay.fontFamily}', sans-serif` }}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f} style={{ fontFamily: `'${f}', sans-serif` }}>
                      {f}
                    </option>
                  ))}
                  {!FONT_OPTIONS.includes(activeOverlay.fontFamily) && (
                    <option value={activeOverlay.fontFamily}>
                      {activeOverlay.fontFamily} (current)
                    </option>
                  )}
                </select>

                <p className={`${eyebrowClass} mt-3`}>Font size</p>
                <input
                  type="range"
                  min={20}
                  max={140}
                  step={2}
                  value={activeOverlay.fontSize}
                  onChange={(e) => updateOverlay(activeOverlay.id, { fontSize: Number(e.target.value) })}
                  className="w-full accent-primary mt-1.5"
                />

                <div className="flex gap-6 mt-3 flex-wrap">
                  <div>
                    <p className={`${eyebrowClass} mb-2`}>Placement</p>
                    <div className="inline-grid grid-cols-3 gap-1 p-1 rounded-lg bg-surface border border-surface-dark/40">
                      {PLACEMENT_CELLS.map((pos) => {
                        const active = activeOverlay.positionId === pos;
                        return (
                          <button
                            key={pos}
                            type="button"
                            title={pos.replace('-', ' ')}
                            aria-label={`Place text ${pos.replace('-', ' ')}`}
                            onClick={() => {
                              const snap = snapForPosition(pos);
                              updateOverlay(activeOverlay.id, {
                                positionId: pos,
                                x: snap.x,
                                y: snap.y,
                                anchor: snap.anchor,
                                textAlign: snap.textAlign,
                              });
                            }}
                            className={`w-7 h-7 rounded flex items-center justify-center transition-colors cursor-pointer ${active ? 'bg-primary text-white' : 'bg-white text-dark-muted/50 hover:text-primary'}`}
                          >
                            <span className={`block w-2 h-2 rounded-full ${active ? 'bg-white' : 'bg-current'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className={`${eyebrowClass} mb-2`}>Weight</p>
                      <div className="flex gap-1.5">
                        {WEIGHT_OPTIONS.map((w) => (
                          <button
                            key={w.value}
                            type="button"
                            title={w.title}
                            onClick={() => updateOverlay(activeOverlay.id, { fontWeight: w.value })}
                            className={`${chipClass(activeOverlay.fontWeight === w.value)} ${w.cls}`}
                          >
                            A
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className={`${eyebrowClass} mb-2`}>Case</p>
                      <div className="flex gap-1.5">
                        {CASE_OPTIONS.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            title={c.title}
                            onClick={() => updateOverlay(activeOverlay.id, { textTransform: c.value })}
                            className={`${chipClass(activeOverlay.textTransform === c.value)} font-bold`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <p className={`${eyebrowClass} mt-3 mb-2`}>Color</p>
                <div className="flex flex-col gap-2">
                  {[
                    ...(brandColors.length ? [{ key: 'brand', label: 'Brand', colors: brandColors }] : []),
                    { key: 'bw', label: 'B & W', colors: ['#FFFFFF', '#000000'] },
                    ...(bgColors.length ? [{ key: 'bg', label: 'From BG', colors: bgColors }] : []),
                  ].map((group) => (
                    <div key={group.key} className="flex items-center gap-2">
                      <span
                        className="w-14 flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-dark-muted/70 leading-tight"
                        title={group.key === 'bg' ? 'Complementary colors sampled from the background' : undefined}
                      >
                        {group.label}
                      </span>
                      <div className="flex gap-1.5 flex-wrap">
                        {group.colors.map((c) => (
                          <button
                            key={`${group.key}-${c}`}
                            type="button"
                            onClick={() => updateOverlay(activeOverlay.id, { color: c })}
                            title={c}
                            className={`w-7 h-7 rounded-lg border-2 transition-all cursor-pointer ${
                              activeOverlay.color.toUpperCase() === c.toUpperCase()
                                ? 'border-primary scale-110'
                                : 'border-surface-dark/40 hover:border-primary/40'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => removeLayer(activeOverlay.id)}
                  className="mt-auto pt-4 self-start text-xs font-bold text-red-500 hover:text-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1" />
                  Delete layer
                </button>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center px-4">
                <p className="text-sm text-dark-muted leading-relaxed">
                  Select a layer on the left — or add one — then drag it
                  anywhere on the image.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Edit with AI (point 2) — punched-out, accent-tinted card so the
            prompt-edit affordance is impossible to miss. Sparkle icon + label
            + a primary EDIT button. */}
        <div className="flex-shrink-0 rounded-2xl border-2 border-primary/30 bg-primary-light/40 p-3.5">
          <div className="flex items-center gap-2.5 mb-2.5">
            <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-primary text-white flex items-center justify-center shadow-sm shadow-primary/30">
              <i className="ri-sparkling-2-fill text-base" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-dark leading-none">Edit with AI</p>
              <p className="text-[11px] text-dark-muted mt-1 leading-tight">
                Describe a change — the scene transforms, your text stays on top.
              </p>
            </div>
          </div>
          <textarea
            value={editPrompt}
            onChange={(e) => onEditPromptChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onPromptEdit();
              }
            }}
            disabled={promptEditing}
            rows={2}
            placeholder={frames.length > 1
              ? `e.g. "make the background a sunset" — frame ${activeFrameIdx + 1}`
              : 'e.g. "make the background a sunset", "add soft studio lighting"…'}
            className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-surface-dark/40 focus:border-primary focus:outline-none text-sm text-dark placeholder:text-dark-muted/60 resize-none disabled:opacity-60"
          />
          {editError && (
            <p className="text-xs text-red-600 mt-1.5">{editError}</p>
          )}
          <button
            type="button"
            onClick={onPromptEdit}
            disabled={promptEditing || !editPrompt.trim()}
            title="AI-edit the image base — your text layers stay on top"
            className="mt-2 w-full h-11 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {promptEditing ? (
              <><i className="ri-loader-4-line animate-spin text-base" /> Editing…</>
            ) : (
              <><i className="ri-magic-line text-base" /> Apply edit · 1 cr</>
            )}
          </button>
        </div>

        {/* Action row (points 3 + 4). Schedule is the FILLED primary CTA;
            Download is icon-only; Re-render opens the quality popover. Save is
            automatic — its status lives on the image (top-right), not here. */}
        <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onBackToDesign} className={actionPillClass}>
            <i className="ri-arrow-left-line text-base" />
            Back to design
          </button>
          <FashionFooterPill
            label="Re-render"
            icon="ri-refresh-line"
            isOpen={retryOpen}
            onToggle={onToggleRetry}
            onClose={onCloseRetry}
            popoverTitle="Re-render at a different quality"
            align="left"
          >
            <div className="space-y-2 min-w-[300px]">
              <p className="text-xs text-dark-muted leading-relaxed">
                Re-renders your current design — including any edits made since
                this render — at the quality you pick. Each run charges credits.
              </p>
              {retryOptions.map(({ q, label }) => {
                const locked = isFree && q !== 'low';
                const cost = QUALITY_MULTIPLIER[q] * frames.length;
                return (
                  <button
                    key={q}
                    type="button"
                    disabled={locked}
                    onClick={() => onRegenerateWithQuality(q)}
                    title={locked ? 'Upgrade to unlock this quality' : undefined}
                    className={`
                      w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-sm font-bold transition-all
                      ${locked
                        ? 'border-surface-dark/20 bg-surface/50 text-dark-muted/60 cursor-not-allowed'
                        : 'border-surface-dark/40 bg-white hover:border-primary hover:text-primary text-dark cursor-pointer'}
                    `}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {locked && <i className="ri-lock-2-line" />}
                      {label}
                    </span>
                    <span className="text-xs font-bold text-dark-muted">
                      {cost} cr
                    </span>
                  </button>
                );
              })}
            </div>
          </FashionFooterPill>
          {/* Schedule — filled primary CTA (point 3). */}
          <button
            type="button"
            onClick={onSchedule}
            disabled={saveState === 'saving'}
            className="h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-calendar-schedule-line text-base" />
            Schedule
          </button>
          {/* Download — icon only (point 3). */}
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            title="Download"
            aria-label="Download"
            className="w-11 h-11 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading
              ? <i className="ri-loader-4-line animate-spin text-base" />
              : <i className="ri-download-2-line text-base" />}
          </button>
        </div>
      </div>

      {/* ============ Image stage (now on the LEFT via order) ============ */}
      <div className="xl:order-1 min-w-0 h-full min-h-[440px]">
        {/* Single container (white-halo chrome) holding BOTH the image stage
            and the thumbnail strip, so the thumbs read as part of the panel
            (point 1). */}
        <div className="h-full rounded-2xl overflow-hidden shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] bg-gradient-to-br from-surface to-surface-dark/40 flex flex-col">
          {/* Image stage — fills remaining height; the image letterboxes here
              (point 5). Measured (stageRef) so the image box fits inside it. */}
          <div ref={stageRef} className="flex-1 min-h-0 flex items-center justify-center p-3">
        <div
          ref={previewRef}
          className={`relative rounded-xl overflow-hidden bg-surface ${aspectClass(aspectRatio)}`}
          style={{ width: `${boxW}px` }}
        >
          {/* Save status — top-right overlay. Dirty-aware: clean = emerald
              "Saved"; unsaved overlay/prompt edits = amber "Unsaved". */}
          {saveState === 'saving' ? (
            <div className="absolute top-3 right-3 z-30">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-dark-muted pointer-events-none">
                <i className="ri-loader-4-line animate-spin" /> Saving…
              </span>
            </div>
          ) : saveState === 'error' ? (
            <div className="absolute top-3 right-3 z-30">
              <button
                type="button"
                onClick={onRetrySave}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-red-600 cursor-pointer"
              >
                <i className="ri-error-warning-line" /> Save failed — retry
              </button>
            </div>
          ) : saveState === 'saved' ? (
            <div className="absolute top-3 right-3 z-30">
              {creativeDirty ? (
                <span
                  title="You have edits that aren't saved yet — hit Save"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-amber-600 pointer-events-none"
                >
                  <i className="ri-edit-circle-line" /> Unsaved edits
                </span>
              ) : (
                <span
                  title="Saved to My Creatives"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-emerald-600 pointer-events-none"
                >
                  <i className="ri-check-line" /> Saved
                </span>
              )}
            </div>
          ) : null}

          {/* Manual Save — bottom-right of the image (point 2). Flushes the
              current composite to My Creatives on demand; emphasized while
              there are unsaved edits. */}
          <button
            type="button"
            onClick={onManualSave}
            disabled={saveState === 'saving'}
            title="Save your edits to My Creatives"
            className={`
              absolute bottom-3 right-3 z-30 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full
              text-xs font-bold uppercase tracking-[0.06em] shadow-lg transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed
              ${creativeDirty
                ? 'bg-primary hover:bg-primary-hover text-white shadow-primary/30'
                : 'bg-white/90 backdrop-blur-sm hover:bg-white text-dark border border-surface-dark/30'}
            `}
          >
            <i className="ri-save-3-line text-sm" />
            {creativeDirty ? 'Save edits' : 'Save'}
          </button>

          {activeImage ? (
            <img
              src={activeImage}
              alt="Generated ad"
              className="w-full h-full object-cover"
              draggable={false}
              onMouseDown={() => setActiveId(null)}
            />
          ) : null}

          {/* Draggable text overlays — render the RESOLVED layer so the
              visible position matches the canvas output exactly. */}
          {resolvedCurrent.map((overlay) => (
            <div
              key={overlay.id}
              onMouseDown={(e) => {
                e.preventDefault();
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                dragBoxRef.current = { w: r.width, h: r.height };
                if (previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (e.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (e.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - overlay.x,
                    dy: cursorY - overlay.y,
                  };
                }
                if (overlay.stackOnPrevious) {
                  onOverlaysChange(overlaysRef.current.map((o) =>
                    o.id === overlay.id
                      ? { ...o, y: overlay.y, stackOnPrevious: false }
                      : o,
                  ));
                }
                setActiveId(overlay.id);
                setDraggingId(overlay.id);
              }}
              onTouchStart={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                const t = e.touches[0];
                dragBoxRef.current = { w: r.width, h: r.height };
                if (t && previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (t.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (t.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - overlay.x,
                    dy: cursorY - overlay.y,
                  };
                }
                if (overlay.stackOnPrevious) {
                  onOverlaysChange(overlaysRef.current.map((o) =>
                    o.id === overlay.id
                      ? { ...o, y: overlay.y, stackOnPrevious: false }
                      : o,
                  ));
                }
                setActiveId(overlay.id);
                setDraggingId(overlay.id);
              }}
              className={`absolute select-none ${
                draggingId === overlay.id ? 'cursor-grabbing' : 'cursor-grab'
              } ${
                activeId === overlay.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''
              }`}
              style={getTextLayerStyle(overlay, { previewFontScale })}
            >
              {overlay.text}
            </div>
          ))}

          {/* Brand-logo overlays — drawn LAST (above text). */}
          {currentLogos.map((logo) => (
            <img
              key={logo.id}
              src={logo.src}
              alt="Brand logo"
              draggable={false}
              onMouseDown={(e) => {
                e.preventDefault();
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                dragBoxRef.current = { w: r.width, h: r.height };
                if (previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (e.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (e.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - logo.x,
                    dy: cursorY - logo.y,
                  };
                }
                draggingLogoRef.current = true;
                setActiveId(logo.id);
                setDraggingId(logo.id);
              }}
              onTouchStart={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                const t = e.touches[0];
                dragBoxRef.current = { w: r.width, h: r.height };
                if (t && previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (t.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (t.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - logo.x,
                    dy: cursorY - logo.y,
                  };
                }
                draggingLogoRef.current = true;
                setActiveId(logo.id);
                setDraggingId(logo.id);
              }}
              className={`absolute select-none ${
                draggingId === logo.id ? 'cursor-grabbing' : 'cursor-grab'
              } ${
                activeId === logo.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''
              }`}
              style={{
                left: `${logo.x * 100}%`,
                top: `${logo.y * 100}%`,
                width: `${logo.widthFrac * 100}%`,
                height: 'auto',
                opacity: logo.opacity,
                filter: logoFilterCss(logo.tint),
                pointerEvents: 'auto',
              }}
            />
          ))}
        </div>{/* /image box */}
          </div>{/* /image stage (stageRef) */}

          {/* Frame thumbs — INSIDE the container (point 1), flex-shrink-0 so
              they take from the stage height (never grow past the column).
              A hairline divider separates them from the image stage. */}
          {frames.length > 1 && (
            <div className="flex-shrink-0 flex items-center justify-center gap-2 px-3 pb-3 pt-2 border-t border-white/50">
              {frames.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { onActiveFrameChange(i); setActiveId(null); }}
                  className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                    activeFrameIdx === i ? 'border-primary shadow-md' : 'border-transparent hover:border-surface-dark/50'
                  }`}
                >
                  <img src={img} alt={`Frame ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                  <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
                    activeFrameIdx === i ? 'text-primary' : 'text-white drop-shadow-md'
                  }`}>
                    {i + 1}
                  </span>
                  {overlays.some((o) => o.imageIndex === i) && (
                    <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-accent" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>{/* /container */}
      </div>{/* /right column */}
    </div>
  );
}

// 4-col wrapping grid of all fashion models + "AI picks" tile. Hero of the
// LEFT panel.
function StudioBigGrid({ selectedId, onSelect }: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-2.5">
      {/* AI picks tile */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        title="AI picks"
        className="group flex flex-col items-stretch gap-1.5 cursor-pointer"
      >
        <div className={`
          relative aspect-square rounded-2xl overflow-hidden bg-primary-light
          border-2 transition-all
          ${selectedId === null
            ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
            : 'border-transparent group-hover:border-primary/30 group-hover:shadow-md'}
        `}>
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="ri-sparkling-2-fill text-3xl text-primary/40" />
          </div>
          {selectedId === null && (
            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
              <i className="ri-check-line text-xs" />
            </div>
          )}
        </div>
        <p className={`
          text-xs leading-tight text-center truncate px-1
          ${selectedId === null ? 'text-primary font-bold' : 'text-dark-muted font-medium'}
        `}>
          AI picks
        </p>
      </button>

      {fashionModels.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onSelect(m.id)}
          title={m.name}
          className="group flex flex-col items-stretch gap-1.5 cursor-pointer"
        >
          <div className={`
            relative aspect-square rounded-2xl overflow-hidden bg-surface
            border-2 transition-all
            ${m.id === selectedId
              ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
              : 'border-transparent group-hover:border-primary/30 group-hover:shadow-md'}
          `}>
            {/* On hover (Jun 14) — scale + shift focal point to the
                top of the image so the user sees a zoomed face preview.
                Fashion model base.pngs have the face roughly in the top
                ~25% (full-body or 3/4 framing). `object-top` shifts the
                focal point, `scale-[2.2]` pushes the face close.
                Selected state suppresses the zoom (tile is already
                clearly picked — no need to re-preview). */}
            <img
              src={fashionAssetUrl(m, m.base)}
              alt=""
              className={`
                absolute inset-0 w-full h-full object-cover object-center
                transition-transform duration-300 ease-out
                ${m.id === selectedId
                  ? ''
                  : 'group-hover:scale-[2.2] group-hover:object-top'}
              `}
              loading="lazy"
              draggable={false}
            />
            {m.id === selectedId && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
                <i className="ri-check-line text-xs" />
              </div>
            )}
          </div>
          <p className={`
            text-xs leading-tight text-center truncate px-1
            ${m.id === selectedId ? 'text-primary font-bold' : 'text-dark-muted font-medium'}
          `}>
            {m.name}
          </p>
        </button>
      ))}
    </div>
  );
}

// Held-route POSE picker — the hero of the LEFT panel (occupies the
// MODEL slot Fashion uses). 6 pose tiles in a 3-col grid using the
// existing /filter-previews/<slug>.webp assets (rendered Jun 10 — the
// pose-named previews are already on disk per CLAUDE.md). Click → writes
// to creativeDirection.pose.
function StudioPoseGrid({ poses, selected, onSelect }: {
  poses: readonly CreativePose[];
  /** null = this group is inactive (the other hero group holds the pick). */
  selected: CreativePose | null;
  onSelect: (pose: CreativePose) => void;
}) {
  // grid-cols-6 → smaller thumbnails, all 6 poses in one clean row
  // (Jul 19, user request to reduce Create-section thumbnail size).
  return (
    <div className="grid grid-cols-6 gap-2.5">
      {poses.map((p) => (
        <StudioFramingTile
          key={p}
          name={p}
          imageSrc={`/filter-previews/${studioFramingSlug(p)}.webp`}
          isActive={p === selected}
          onClick={() => onSelect(p)}
          size="primary"
        />
      ))}
    </div>
  );
}

// Product Shot route hero. Same grid + tile shape as Pose/Model so all
// three hero pickers feel uniform. Preview images live at
// /public/pattern-previews/<slug>.webp — these are arrangement-style
// renders (NOT scene-template thumbnails). When the preview PNG is
// missing the tile falls back to a centered text label.
function StudioPatternGrid({ patterns, selected, onSelect }: {
  patterns: readonly CreativePattern[];
  /** null = this group is inactive (the other hero group holds the pick). */
  selected: CreativePattern | null;
  onSelect: (p: CreativePattern | null) => void;
}) {
  // grid-cols-6 → smaller thumbnails, all 6 arrangements in one clean row
  // (Jul 19, user request to reduce Create-section thumbnail size).
  return (
    <div className="grid grid-cols-6 gap-2.5">
      {patterns.map((p) => (
        <StudioFramingTile
          key={p}
          name={p}
          imageSrc={`/pattern-previews/${studioFramingSlug(p)}.webp`}
          isActive={p === selected}
          onClick={() => onSelect(p)}
          size="primary"
        />
      ))}
    </div>
  );
}

// V3 Social Post (Jun 15 v3) — Shot Type hero grid. THREE large tiles:
//   • AI Chooses (default) — sparkle gradient, no image (LLM decides)
//   • Product Shot — neutral product image (no humans)
//   • In Use — model applying serum/cream (actual product usage,
//             NOT just two hands holding — that read as "presenting"
//             rather than "using" per user direction Jun 15)
//
// AI Chooses uses a bespoke gradient tile (cannot reuse StudioFramingTile
// since that requires an image src) — mirrors the AI Choose treatment
// in CarouselTypeCard. 3-col grid since three options.
function StudioShotTypeGrid({ selected, onSelect }: {
  selected: 'ai-chooses' | 'product-only' | 'in-use';
  onSelect: (v: 'ai-chooses' | 'product-only' | 'in-use') => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 max-w-lg">
      {/* AI Chooses — sparkle gradient (no image, no auto-discovery
          needed since this is the "let the model decide" path) */}
      <ShotTypeAiTile
        isActive={selected === 'ai-chooses'}
        onClick={() => onSelect('ai-chooses')}
      />
      {/* Product Shot — neutral product-on-surface */}
      <StudioFramingTile
        name="Product shot"
        imageSrc="/filter-previews/centered-product.webp"
        isActive={selected === 'product-only'}
        onClick={() => onSelect('product-only')}
        size="primary"
      />
      {/* In Use — model applying serum/cream (canonical "product in
          use" shot from Held route's framing set). Replaces the
          earlier two-hand-present image which read as "holding" not
          "using" per user direction Jun 15. */}
      <StudioFramingTile
        name="In use"
        imageSrc="/filter-previews/mid-application.webp"
        isActive={selected === 'in-use'}
        onClick={() => onSelect('in-use')}
        size="primary"
      />
    </div>
  );
}

// V3 Social Post (Jun 15 v3) — AI Chooses tile. Visual twin of
// CarouselTypeCard's "AI Choose" treatment: sparkle gradient panel +
// "AI · MAGIC" label below. Sized to match a primary StudioFramingTile
// (aspect-square) so it sits flush in the 3-col Shot Type grid.
function ShotTypeAiTile({ isActive, onClick }: {
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="AI Chooses"
      className="group flex flex-col items-stretch gap-1.5 cursor-pointer"
    >
      <div className={`
        relative aspect-square rounded-xl overflow-hidden
        bg-gradient-to-br from-pink-100 via-indigo-100 to-sky-100
        border-2 transition-all
        ${isActive
          ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
          : 'border-transparent group-hover:border-primary/30 group-hover:shadow-md'}
      `}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center shadow-sm">
            <i className="ri-sparkling-2-fill text-2xl text-primary" />
          </div>
          <div className="text-[10px] font-bold tracking-[0.18em] text-dark/70 uppercase">
            AI · Magic
          </div>
        </div>
        {isActive && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
            <i className="ri-check-line text-xs" />
          </div>
        )}
      </div>
      <div className={`text-xs text-center font-medium ${isActive ? 'text-primary' : 'text-dark-muted'}`}>
        AI Chooses
      </div>
    </button>
  );
}

// Single-tile current-selection display for one CD field. Click → popover
// opens ABOVE the tile (so it doesn't get clipped by the panel footer).
function FashionCompactPicker({
  label, currentValue, imageSrc,
  isOpen, onToggle, onClose,
  disabled, disabledHint,
  align = 'left',
  tileContent,
  children,
}: {
  label: string;
  currentValue: string;
  imageSrc?: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  disabled?: boolean;
  disabledHint?: string;
  align?: 'left' | 'right';
  /** Optional custom body for the tile (e.g. text in a script font for
   *  the Brand filter). When provided, replaces the imageSrc rendering. */
  tileContent?: React.ReactNode;
  children: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [imageSrc]);

  // Click-outside closes; container as boundary means re-clicking the
  // trigger correctly toggles (doesn't double-fire close + open).
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [isOpen, onClose]);

  return (
    // Fixed 100px width (matches the tile cap) so the filter bar's flex
    // row packs the pickers tight against the left edge instead of
    // letting grid cells spread them across the panel.
    <div ref={containerRef} className="relative w-[100px] flex-shrink-0">
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        title={disabled && disabledHint ? disabledHint : currentValue}
        className={`
          group w-full flex flex-col items-stretch gap-1.5 text-left
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <span className="block text-xs font-bold uppercase tracking-[0.2em] text-dark">
          {label}
        </span>
        {/* Tile capped at 100px (significantly smaller than MODEL tiles
            which fill the grid cell ~155px) so MODEL stays the clear
            visual hero and these read as fine-tune knobs, not peers. */}
        <div className={`
          relative aspect-square w-full max-w-[100px] rounded-2xl overflow-hidden bg-surface
          border-2 transition-all
          ${isOpen
            ? 'border-primary shadow-md shadow-primary/25'
            : 'border-transparent group-hover:border-primary/30 group-hover:shadow-md'}
        `}>
          {tileContent ? (
            <div className="absolute inset-0 flex items-center justify-center">
              {tileContent}
            </div>
          ) : !imgFailed && imageSrc ? (
            <img
              src={imageSrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setImgFailed(true)}
              loading="lazy"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-dark-muted text-center px-1">
              {currentValue}
            </div>
          )}
          <div className="absolute bottom-1.5 right-1.5 w-6 h-6 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-sm">
            <i className={`${isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-primary text-base`} />
          </div>
        </div>
        <p className="text-xs leading-tight truncate text-dark-muted font-medium">
          {currentValue}
        </p>
      </button>

      {isOpen && (
        <FashionPopoverPanel title={label} onClose={onClose} align={align}>
          {children}
        </FashionPopoverPanel>
      )}
    </div>
  );
}

// Reusable floating panel for popovers. Sits ABOVE the trigger (bottom-full
// + mb-3). Title + X close button at top. align='right' anchors to the
// right edge of the trigger (used by the last picker so the popover doesn't
// overflow the panel).
function FashionPopoverPanel({ title, onClose, align = 'left', children }: {
  title: string;
  onClose: () => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  return (
    <div
      className={`
        absolute bottom-full mb-3 z-30
        rounded-2xl bg-white border border-surface-dark/40 shadow-xl shadow-dark/10
        p-4 min-w-[280px]
        ${align === 'right' ? 'right-0' : 'left-0'}
      `}
      role="dialog"
      aria-label={title}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-dark">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-6 h-6 rounded-full bg-surface hover:bg-surface-dark/30 flex items-center justify-center text-dark-muted hover:text-dark transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div>{children}</div>
    </div>
  );
}

// Grid of image option tiles inside a popover. Reuses StudioFramingTile
// at 'secondary' size to stay visually consistent with the rest of the app.
function FashionImageOptionGrid({ values, selected, onSelect, pathPrefix = '/filter-previews' }: {
  values: readonly string[];
  selected: string;
  onSelect: (v: string) => void;
  /** Folder under public/ to source preview WebPs from. Defaults to the
   *  bottle-subject set; Fashion route's Light/BG/Vibe pickers pass
   *  `/filter-previews-fashion` to get model-subject previews. */
  pathPrefix?: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {values.map((v) => (
        <StudioFramingTile
          key={v}
          name={v}
          imageSrc={`${pathPrefix}/${studioFramingSlug(v)}.webp`}
          isActive={v === selected}
          onClick={() => onSelect(v)}
          size="secondary"
        />
      ))}
    </div>
  );
}

// Outlined clickable pill in the right-panel footer. Opens a popover ABOVE.
// Used for grouped settings (Output = format+aspect+quality; Brand =
// brand_presence + show_logo + text_density).
function FashionFooterPill({
  label, icon, isOpen, onToggle, onClose, popoverTitle, align = 'left', children,
}: {
  label: string;
  /** Optional leading icon class so the trigger matches the action-pill family. */
  icon?: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  popoverTitle: string;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [isOpen, onClose]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={`
          h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full border
          text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer whitespace-nowrap
          ${isOpen
            ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20'
            : 'bg-white text-dark border-surface-dark/40 hover:border-primary hover:text-primary'}
        `}
      >
        {icon && <i className={`${icon} text-base`} />}
        {label}
      </button>
      {isOpen && (
        <FashionPopoverPanel title={popoverTitle} onClose={onClose} align={align}>
          {children}
        </FashionPopoverPanel>
      )}
    </div>
  );
}

// Output popover body: Format (single/carousel) + Aspect + Quality.
// Compact value shown on the Text density chip. The chip's icon already
// reads as "text", so the value just needs to communicate the level —
// None reads as "Off" for at-a-glance scanning against the other chips.
function textDensityChipLabel(d: CreativeDirection['text_density']): string {
  const v = d ?? 'Text + CTA';
  if (v === 'None') return 'Text: OFF';
  if (v === 'Text only') return 'Text: ON';
  return 'Text: ON + CTA';
}

// Format glyph — visually distinguishes Single (one image) from Carousel
// (four images) per user spec. Inline SVG so it inherits the chip's
// currentColor (primary when active, muted when not). Single = one
// rounded square; Carousel = a 2×2 grid of four squares.
function FormatGlyph({ format }: { format: Format }) {
  if (format === 'carousel') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
        <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" />
        <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
        <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" fill="currentColor" />
    </svg>
  );
}

// Format toggle (Jun 12 v3) — a VISIBLE segmented control so Carousel is
// discoverable without opening a popover (user direction). Single + 4-frame
// Carousel each show their glyph (1 square vs 2×2 grid). h-11 to match the
// setting chips. Active segment = primary fill.
function StudioFormatToggle({ value, onChange, carouselDisabled = false }: {
  value: Format;
  onChange: (v: Format) => void;
  /** Carousel isn't wired for the AI-template Templates tab yet (the
   *  round-robin is next) — disable that segment with a hint rather than
   *  let it silently render a single. */
  carouselDisabled?: boolean;
}) {
  return (
    <div className="inline-flex items-center h-11 p-1 rounded-full bg-surface/70 border border-surface-dark/40">
      {(['single', 'carousel'] as const).map((opt) => {
        const active = opt === value;
        const disabled = opt === 'carousel' && carouselDisabled;
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => { if (!disabled) onChange(opt); }}
            title={
              disabled
                ? 'Carousel works in the Create tab — coming to templates'
                : opt === 'single' ? 'Single image' : 'Carousel — 4 frames'
            }
            className={`
              h-full px-3.5 inline-flex items-center gap-1.5 rounded-full text-sm font-bold transition-all
              ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}
              ${active
                ? 'bg-primary text-white shadow-sm shadow-primary/25'
                : 'bg-transparent text-dark-muted hover:text-dark'}
            `}
          >
            <FormatGlyph format={opt} />
            {opt === 'single' ? 'Single' : 'Carousel'}
          </button>
        );
      })}
    </div>
  );
}

// V3 Social Post (Jun 15 v3 — iOS-style slide) — master Filters ON/OFF
// toggle. Sits on the LEFT of the FILTERS title. When OFF (default),
// the visual filter pickers below dim + go non-interactive; Generate
// strips vibe/light/bg/framing/text_density from the CD payload so
// backend's allowlist drops their prompt fragments → AI gets full
// discretion.
//
// Geometry: fixed w-16 pill with an absolute-positioned dot that slides
// from left (OFF) to right (ON) via `left`/`right` CSS transitions.
// Label sits on the OPPOSITE side of the dot:
//   • OFF: [● ····· OFF]  ← dot left, label right
//   • ON:  [ON ····· ●]   ← label left, dot right
// Matches iOS toggle affordance — flipping ON visibly slides the dot.
function StudioMasterFiltersToggle({ value, onChange }: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      title={value ? 'Filters ON — AI honors visual direction below' : 'Filters OFF — AI freestyles based on intent only'}
      className={`
        relative inline-block w-16 h-7 rounded-full transition-colors cursor-pointer
        ${value
          ? 'bg-primary shadow-sm shadow-primary/25'
          : 'bg-dark-muted/70 hover:bg-dark-muted'}
      `}
    >
      {/* Sliding dot — left edge in OFF state, right edge in ON state.
          Both rest 4px from their respective edges (top-1 + left-1/right-1
          inside a 28px-tall pill with 20px dot = 4px gap all around). */}
      <span
        className={`
          absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-200 ease-out
          ${value ? 'left-[2.625rem]' : 'left-1'}
        `}
        aria-hidden="true"
      />
      {/* Label — opposite side of the dot. Positioned absolutely so
          the dot's slide doesn't push it around. */}
      <span
        className={`
          absolute top-0 h-7 inline-flex items-center text-[10px] font-bold uppercase tracking-[0.12em] text-white pointer-events-none
          ${value ? 'left-2.5' : 'right-2'}
        `}
      >
        {value ? 'On' : 'Off'}
      </span>
    </button>
  );
}

// (V3 Social Post Shot Type was a segmented pill inside the FILTERS row
// in Jun 15 v2. Replaced by StudioShotTypeGrid hero picker in Jun 15 v3
// — same selection, hero-picker treatment with large thumbnails.)

// Clean vertical option list for the setting-chip popovers (Jun 12 v3).
// Replaces the cramped horizontal StudioPillRow inside popovers — vertical
// rows never overlap and read as premium (matches the "Adj quality" retry
// popover). Each row: label (+ optional sublabel like a credit cost) +
// check when selected; locked rows render muted + non-clickable.
function StudioChipOptionList({ options, selected, onSelect }: {
  options: { value: string; label: string; sublabel?: string; locked?: boolean }[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5 min-w-[230px]">
      {options.map((o) => {
        const active = o.value === selected;
        return (
          <button
            key={o.value}
            type="button"
            disabled={o.locked}
            onClick={() => { if (!o.locked) onSelect(o.value); }}
            title={o.locked ? 'Upgrade to unlock this quality' : undefined}
            className={`
              w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border text-sm font-bold transition-all
              ${o.locked
                ? 'border-surface-dark/20 bg-surface/50 text-dark-muted/60 cursor-not-allowed'
                : active
                  ? 'border-primary bg-primary-light text-primary cursor-pointer'
                  : 'border-surface-dark/40 bg-white text-dark hover:border-primary/40 cursor-pointer'}
            `}
          >
            <span className="inline-flex items-center gap-2">
              {o.locked && <i className="ri-lock-2-line text-dark-muted/70" />}
              {o.label}
            </span>
            <span className="inline-flex items-center gap-2.5">
              {o.sublabel && (
                <span className={`text-xs font-semibold ${active ? 'text-primary/70' : 'text-dark-muted'}`}>
                  {o.sublabel}
                </span>
              )}
              {active && <i className="ri-check-line text-primary text-base" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// A single labeled setting chip in the right-panel footer (Jun 12 v3).
// Replaces the two combined "Output" / "Brand" pills — each setting is now
// its own chip showing [glyph] [value], opening a popover ABOVE with that
// setting's options. h-11 = the app's standard control height. `dimmed`
// renders the value muted for "Off" states so the eye can scan which
// settings are active at a glance.
function StudioSettingChip({
  glyph, value, label, isOpen, onToggle, onClose, align = 'left', dimmed = false, children, onDirectToggle,
}: {
  glyph: React.ReactNode;
  value: string;
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  align?: 'left' | 'right';
  dimmed?: boolean;
  children?: React.ReactNode;
  /** Jul 14 — when set, the chip is a plain two-state toggle: clicking flips
   *  the value directly (no popover). Used for binary Off/On settings where a
   *  dropdown would be overkill. Takes precedence over onToggle/children. */
  onDirectToggle?: () => void;
}) {
  // Portal the popover to <body> so the right panel's `overflow-hidden`
  // (needed for the rounded chrome) can't clip it. Positioned `fixed`
  // above the trigger via its measured rect; re-measured on scroll/resize.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  // Read onClose via a ref so it can't be an effect dependency. onClose
  // (closePop) is an inline arrow — a NEW reference every parent render.
  // With it in the deps below, the effect re-ran every render, and since
  // the body calls measure() → setRect(getBoundingClientRect()) (a fresh
  // DOMRect object each time), that scheduled another render → an infinite
  // re-render loop the moment any popover opened. The spin dropped the
  // option clicks, so every footer chip "opened but wouldn't apply". Keeping
  // the effect keyed on isOpen alone (measure once on open + on scroll/resize)
  // fixes it; onDown reads the latest onClose off the ref.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) { setRect(null); return; }
    // generateControls (this footer) renders TWICE — desktop (hidden xl:block)
    // + mobile (xl:hidden) — sharing ONE popover state, so BOTH copies see
    // isOpen=true. Only one is visible per breakpoint; the other is
    // display:none and measures 0×0. That hidden duplicate must NOT render a
    // portal OR register an outside-click handler — otherwise clicking an
    // option in the VISIBLE popover fires the hidden copy's handler (the click
    // isn't inside ITS off-screen portal) → onClose() on mousedown → the
    // popover closes before the option's onClick lands → "opens but the
    // selection never applies". (Long-standing footer-chip bug — broke when the
    // footer was extracted to render on both breakpoints, Jul 2.) Bail if hidden.
    const r0 = triggerRef.current?.getBoundingClientRect();
    if (!r0 || (r0.width === 0 && r0.height === 0)) { setRect(null); return; }
    const measure = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      setRect(r && (r.width || r.height) ? r : null);
    };
    measure();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      onCloseRef.current();
    };
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      window.removeEventListener('mousedown', onDown);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={onDirectToggle ?? onToggle}
        title={label}
        className={`
          h-11 px-3.5 inline-flex items-center gap-2 rounded-full border
          text-sm font-bold transition-all cursor-pointer whitespace-nowrap
          ${isOpen
            ? 'border-primary bg-primary-light text-primary'
            : 'border-surface-dark/40 bg-white text-dark hover:border-primary/40'}
        `}
      >
        <span className={`inline-flex items-center text-base ${dimmed && !isOpen ? 'text-dark-muted' : 'text-primary'}`}>
          {glyph}
        </span>
        <span className={dimmed && !isOpen ? 'text-dark-muted' : ''}>{value}</span>
      </button>
      {!onDirectToggle && isOpen && rect && createPortal(
        <div
          ref={popRef}
          role="dialog"
          aria-label={label}
          style={{
            position: 'fixed',
            bottom: window.innerHeight - rect.top + 10,
            left: align === 'left' ? rect.left : undefined,
            right: align === 'right' ? window.innerWidth - rect.right : undefined,
            zIndex: 70,
          }}
          className="rounded-2xl bg-white border border-surface-dark/40 shadow-xl shadow-dark/15 p-4"
        >
          <div className="flex items-center justify-between mb-3 gap-6">
            <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-dark whitespace-nowrap">
              {label}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-6 h-6 rounded-full bg-surface hover:bg-surface-dark/30 flex items-center justify-center text-dark-muted hover:text-dark transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          {children}
        </div>,
        document.body,
      )}
    </div>
  );
}

// The primary GENERATE CTA. Switches to amber "Upgrade" state when balance
// is insufficient. `fullWidth` (Jun 12 v3) makes it the single hero button
// on its own footer row at h-12 (the one size bump above the h-11 control
// chips — establishes "this is THE action"); without it, the legacy
// h-11 inline pill. `label` overrides the verb ("Generate Image"); count is
// pluralized for the matrix path ("Generate 6 Images").
function FashionGenerateButton({
  creditCost, insufficient, canGenerate, generating, balance,
  onGenerate, onUpgrade, fullWidth = false, label = 'Generate', imageCount = 1,
}: {
  creditCost: number;
  insufficient: boolean;
  canGenerate: boolean;
  generating: boolean;
  balance: number | null;
  onGenerate: () => void;
  onUpgrade: () => void;
  fullWidth?: boolean;
  label?: string;
  imageCount?: number;
}) {
  const sizing = fullWidth ? 'w-full h-12' : 'h-11 min-w-[200px]';
  // "Generate Image" / "Generate 6 Images" — pluralize + inject count when
  // a matrix of >1 image is queued.
  const verb = imageCount > 1
    ? `${label.replace(/ Image$/, '')} ${imageCount} Images`
    : label;
  if (insufficient) {
    return (
      <button
        type="button"
        onClick={onUpgrade}
        title={balance !== null ? `${balance} credits available · need ${creditCost}` : undefined}
        className={`${sizing} px-6 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold uppercase tracking-[0.08em] whitespace-nowrap shadow-md shadow-amber-500/25 transition-all cursor-pointer`}
      >
        <i className="ri-lock-2-line" />
        Upgrade — need {creditCost}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onGenerate}
      disabled={!canGenerate}
      title={balance !== null ? `${balance} credits available` : undefined}
      className={`${sizing} px-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold uppercase tracking-[0.08em] whitespace-nowrap shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {generating ? (
        <><i className="ri-loader-4-line animate-spin" /> Generating…</>
      ) : (
        <>
          {verb} <span className="opacity-80">· {creditCost} cr</span>
        </>
      )}
    </button>
  );
}

// Template + product confirmation (Jun 12) — shown in the right panel's
// preview body when a template AND a product are both picked but nothing
// has been generated yet. The template sample renders as the hero (it IS
// the scene authority for the render) with the picked product inset as a
// floating chip, so the user can verify the pairing before spending
// credits. Width-anchored square per gotcha #50 (h-full + aspect inside
// a content-sized parent collapses on Safari).
function TemplateConfirmPreview({
  templateName, templateImageUrl, productImageUrl, productName,
}: {
  templateName: string;
  /** Null for the AI Magic freestyle tile (no sample to show) — the
   *  hero renders the gradient + sparkle treatment instead. */
  templateImageUrl: string | null;
  productImageUrl: string;
  productName: string;
}) {
  const isFreestyle = templateImageUrl === null;
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-5">
      <div className="relative w-full max-w-[460px] aspect-square rounded-xl overflow-hidden shadow-lg shadow-dark/15 border border-surface-dark/20 bg-surface">
        {isFreestyle ? (
          // AI Magic freestyle hero — same gradient family as the tile.
          <>
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(135deg, rgba(244,114,182,0.22), rgba(99,102,241,0.18), rgba(56,189,248,0.22))',
              }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center">
              <span className="w-16 h-16 rounded-2xl bg-white/85 backdrop-blur-sm shadow-md flex items-center justify-center">
                <i className="ri-sparkling-2-fill text-4xl text-primary" />
              </span>
              <p className="text-base font-bold text-dark">AI designs the scene</p>
              <p className="text-xs text-dark-muted leading-relaxed max-w-[280px]">
                No template — the AI writes a fresh scene from your
                product&apos;s name, description and your brand context.
              </p>
            </div>
          </>
        ) : (
          <img
            src={templateImageUrl}
            alt={templateName}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        )}
        {/* Template name chip — top-left */}
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-sm">
          <p className="text-xs font-bold text-white flex items-center gap-1.5">
            <i className={isFreestyle ? 'ri-sparkling-2-fill' : 'ri-layout-grid-line'} />
            {templateName}
          </p>
        </div>
        {/* Bottom scrim so the product chip reads cleanly over any
            sample art. */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
        {/* Picked product inset — bottom-left */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-2xl bg-white/95 backdrop-blur-sm shadow-lg">
          <img
            src={productImageUrl}
            alt={productName || 'product'}
            className="w-12 h-12 rounded-xl object-cover bg-surface border border-surface-dark/20"
            draggable={false}
          />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary leading-none">
              Your product
            </p>
            <p className="text-xs font-bold text-dark truncate max-w-[180px] mt-0.5">
              {productName || 'Untitled product'}
            </p>
          </div>
        </div>
      </div>
      <p className="flex-shrink-0 text-xs text-dark-muted text-center flex items-center gap-1.5">
        <i className="ri-checkbox-circle-line text-primary text-base" />
        {isFreestyle
          ? 'AI will invent the scene around your product — hit'
          : 'Your product will be placed into this scene — hit'}
        <span className="font-bold text-dark">Generate</span> to confirm.
      </p>
    </div>
  );
}

// Template deck (Jun 12 v4) — shown when 2+ AI templates are picked. The
// samples stack like a deck of cards (iMessage image-stack): the NEWEST pick
// sits on top, fully visible with the product chip; earlier picks peek out
// behind it (offset + slight rotation). No grid, no scrolling — the depth +
// a count chip convey "N templates". Caption adapts to the output:
//   • single   → N independent images
//   • carousel → 1 post whose 4 frames cycle the templates
function TemplateDeckPreview({ templates, productImageUrl, productName, imageCount, isCarousel }: {
  templates: { id: string; name: string; imageUrl: string }[];
  productImageUrl: string;
  productName: string;
  imageCount: number;
  isCarousel: boolean;
}) {
  // Render the newest 5 (the front-most); deeper ones are implied by the
  // stack + the count chip. Back-to-front so z-order is natural.
  const VISIBLE = 5;
  const start = Math.max(0, templates.length - VISIBLE);
  const stack = templates.slice(start); // oldest-of-visible first … newest last
  const top = stack[stack.length - 1];

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-5">
      {/* Count chip */}
      <div className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-light text-primary text-xs font-bold">
        <i className="ri-stack-line text-sm" />
        {templates.length} templates
        <span className="text-primary/50">·</span>
        {isCarousel ? '1 carousel' : `${imageCount} images`}
      </div>

      {/* The deck — front card straight + product chip; behind cards offset. */}
      <div className="relative flex-1 min-h-0 w-full flex items-center justify-center">
        <div className="relative w-full max-w-[360px] aspect-square">
          {stack.map((t, i) => {
            // depth 0 = front (newest); higher = further back.
            const depth = stack.length - 1 - i;
            const isTop = depth === 0;
            const tx = depth * 16;   // shift right
            const ty = depth * 12;   // shift down
            const rot = depth * 3;   // fan clockwise
            const scale = 1 - depth * 0.045;
            return (
              <div
                key={t.id}
                className="absolute inset-0 rounded-2xl overflow-hidden border border-surface-dark/20 bg-surface shadow-xl shadow-dark/15"
                style={{
                  transform: `translate(${tx}px, ${ty}px) rotate(${rot}deg) scale(${scale})`,
                  transformOrigin: 'center',
                  zIndex: i + 1,
                  opacity: depth > 3 ? 0.7 : 1,
                }}
              >
                <img
                  src={t.imageUrl}
                  alt={t.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                {isTop && (
                  <>
                    {/* name chip top-left */}
                    <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-sm">
                      <p className="text-xs font-bold text-white flex items-center gap-1.5">
                        <i className="ri-layout-grid-line" />
                        {top?.name}
                      </p>
                    </div>
                    {/* bottom scrim + product chip */}
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-3 flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-2xl bg-white/95 backdrop-blur-sm shadow-lg">
                      <img
                        src={productImageUrl}
                        alt={productName || 'product'}
                        className="w-11 h-11 rounded-xl object-cover bg-surface border border-surface-dark/20"
                        draggable={false}
                      />
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-primary leading-none">
                          Your product
                        </p>
                        <p className="text-xs font-bold text-dark truncate max-w-[150px] mt-0.5">
                          {productName || 'Untitled product'}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p className="flex-shrink-0 text-xs text-dark-muted text-center flex items-center justify-center gap-1.5">
        <i className="ri-checkbox-circle-line text-primary text-base" />
        {isCarousel
          ? <>These templates cycle through 4 carousel frames — hit <span className="font-bold text-dark">Generate</span>.</>
          : <>Your product goes into each scene — hit <span className="font-bold text-dark">Generate</span> for {imageCount} images.</>}
      </p>
    </div>
  );
}

// Empty state — shown when no product picked AND no generation in flight.
// Whole container is wrapped in a button (click anywhere → opens modal).
function FashionPreviewEmpty() {
  return (
    <div className="flex flex-col items-center gap-3 max-w-[420px] px-6 text-center pointer-events-none">
      <div className="w-16 h-16 rounded-2xl bg-primary-light flex items-center justify-center">
        <i className="ri-image-add-line text-3xl text-primary" />
      </div>
      <h3 className="text-xl font-semibold text-dark">No product selected yet</h3>
      <p className="text-sm text-dark-muted leading-relaxed">
        Pick from your library, paste a product URL, or upload an image. The preview will appear here.
      </p>
    </div>
  );
}


// (b64ToBytes removed Jun 12 v2 — buildFinalBlob flattens via
// flattenComposedFrame on data-URL frames, so no bare-b64 decode needed.)
